/**
 * SOCCER PARTNER DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated soccer partner drills organized into skill levels:
 * 1. Beginner Partner Passing (30 minutes) - Basic partner passing and coordination
 * 2. Intermediate Partner Passing (45 minutes) - Speed, distance, and movement
 * 3. Advanced Partner Passing (45 minutes) - Precision and advanced techniques
 * 4. Beginner Partner Skills (30 minutes) - Basic partner coordination
 * 5. Intermediate Partner Skills (45 minutes) - Dribbling and movement coordination
 * 6. Advanced Partner Skills (45 minutes) - Advanced partner techniques and coordination
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 1501 (soccer partner drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'soccer'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Partner Passing', 'Partner Skills')
 * - points: Point value for completion (30-45 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-2700 seconds = 30-45 minutes)
 * - equipment: Required equipment (e.g., 'Soccer ball')
 * - requiresPartner: true (always true for partner drills)
 * - minPlayers: 2 (minimum players needed)
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * PARTNER DRILL REQUIREMENTS:
 * - All drills require 2+ players
 * - Focus on teamwork and coordination
 * - Include communication cues
 * - Encourage movement and positioning
 * - Build realistic soccer skills
 * - Progressive skill development within each drill
 *
 * CREATED: [Date] - Soccer partner drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const soccerPartnerDrills = [
  // BEGINNER PARTNER PASSING (30 minutes)
  {
    id: 2170,
    name: 'Beginner Partner Passing',
    categories: ['Passing', 'Ball Control', 'Partner Drill', 'Accuracy'],
    sports: ['soccer'],
    difficulty: 'Beginner',
    skill: 'Basic passing, receiving, and coordination with a partner.',
    instructions:
      'Beginner Partner Passing is a full session to develop your basic passing and receiving skills with a partner. You will need a partner and a soccer ball.',
    equipment: '1 soccer ball',
    timeTotal: 1480,
    timedInstructions: [
      {
        time: 0,
        text: "Hey superstars! Welcome to today's training! Let's get started with our first and most important skill: the 'Tick-Tock Pass'. You two, stand about five big steps apart. Ready? Go!",
      },
      {
        time: 10,
        text: "Let's start with Player A. Use the inside of your foot, that big flat part, to pass the ball nice and easy to your partner.",
      },
      {
        time: 18,
        text: "Great pass! Now, Player B, stop the ball with the bottom of your shoe. Just step on it gently, like you're squishing a bug.",
      },
      {
        time: 26,
        text: "Perfect! Now, Player B, pass it back with the inside of your other foot. So it's stop with one foot, pass with the other. Keep it going, you two!",
      },
      {
        time: 36,
        text: "Let's get a rhythm going. Tick-tock, back and forth. Stop, pass. Stop, pass.",
      },
      {
        time: 43,
        text: "Focus on that first touch when you stop the ball. Be a ball magnet! Don't let it bounce away from you.",
      },
      {
        time: 51,
        text: 'Good! When you pass, remember to point your toes up a little and lock your ankle. It makes the pass go super straight.',
      },
      {
        time: 59,
        text: "Remember to look at your partner before you pass! Give them a little nod so they know it's coming. That's teamwork!",
      },
      {
        time: 67,
        text: "That's it! Keep your body over the ball when you pass. Don't lean back.",
      },
      {
        time: 73,
        text: "Let's try a slightly firmer pass now. Make it zip across the grass to your partner.",
      },
      {
        time: 79,
        text: "Awesome! Now a super soft one. Like you're passing an egg you don't want to break.",
      },
      {
        time: 86,
        text: 'One minute in! You two are doing great. Keep that tick-tock rhythm going.',
      },
      {
        time: 92,
        text: "Let's see you two make five perfect passes in a row. Count them out loud! One...",
      },
      {
        time: 98,
        text: 'Two... remember to lock that ankle!',
      },
      {
        time: 102,
        text: 'Three... great eye contact!',
      },
      {
        time: 106,
        text: 'Four... what a soft touch to stop it!',
      },
      {
        time: 110,
        text: "Five! Yes! Let's do it again, team!",
      },
      {
        time: 115,
        text: 'Stay bouncy on your feet. Wiggle your toes! Be ready for the ball at all times.',
      },
      {
        time: 121,
        text: 'If the ball gets away, no problem! That happens. Just go get it and start the tick-tock again.',
      },
      {
        time: 128,
        text: "Let's check our feet. Is your other foot, the one not kicking, right next to the ball? That's your secret to staying balanced.",
      },
      {
        time: 137,
        text: 'Two minutes in! Your passes are looking sharper already. I can see the improvement.',
      },
      {
        time: 143,
        text: 'Okay, challenge time! For the next 30 seconds, only use your right foot. Pass and stop with just your right.',
      },
      {
        time: 151,
        text: "It feels a little weird, right? That's okay! We're waking up that foot and training our brains.",
      },
      {
        time: 158,
        text: 'Keep it up, just a few more seconds on the right foot.',
      },
      {
        time: 163,
        text: 'Awesome. Now switch! For the next 30 seconds, only use your left foot. Stop and pass with just the left.',
      },
      {
        time: 171,
        text: 'Come on, left foot, you can do it! This is how you become a player who can use both feet.',
      },
      {
        time: 178,
        text: 'Keep that focus. Stop, pass. Stop, pass.',
      },
      {
        time: 183,
        text: 'Awesome job, you two. Back to normal, use any foot you want. Tick-tock, tick-tock.',
      },
      {
        time: 190,
        text: 'Halfway through this drill! Keep that focus. Every single pass matters.',
      },
      {
        time: 196,
        text: 'Imagine a defender is between you two. Your pass has to be quick and accurate to get past them.',
      },
      {
        time: 204,
        text: "Let's see a super straight pass. No wobbles! A straight pass is a happy pass.",
      },
      {
        time: 210,
        text: "Think about your arms. Keep them out a little for balance, like you're an airplane.",
      },
      {
        time: 217,
        text: "Let's see another five perfect passes in a row. Go for it!",
      },
      {
        time: 222,
        text: "Keep that quality high. You're both working so hard.",
      },
      {
        time: 227,
        text: 'The best players in the world are the best passers. This is the most important skill.',
      },
      {
        time: 234,
        text: 'Keep that ball on the ground. A pass on the ground is much easier for your partner to control.',
      },
      {
        time: 240,
        text: "Last minute of Tick-Tock Passing! Let's make it the best minute yet. Maximum focus!",
      },
      {
        time: 247,
        text: "Let's see how many good passes you can get in 30 seconds. Count them up! Go!",
      },
      {
        time: 253,
        text: 'Faster, faster! But keep it clean. A good pass is better than a fast, sloppy pass.',
      },
      {
        time: 260,
        text: "Keep that energy high! You're almost there.",
      },
      {
        time: 265,
        text: 'Come on, push yourselves! Final few seconds of the count.',
      },
      {
        time: 270,
        text: 'Thirty seconds left! Can you speed it up even more? Faster, faster! Tick-tock-tick-tock!',
      },
      {
        time: 278,
        text: "Don't get tired now. This is where champions are made.",
      },
      {
        time: 283,
        text: 'Final push! Give me your best passes of the day.',
      },
      {
        time: 288,
        text: 'Get ready for the countdown!',
      },
      {
        time: 292,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Great job, team!',
      },

      {
        time: 300,
        text: "That was a fantastic first exercise! Let's keep that energy going! Next up, we're going to learn how to dribble like the GOAT with the 'Messi Dribble'! You'll do this one at a time.",
      },
      {
        time: 310,
        text: "Alright, Player A, you're up first! Player B, you can cheer them on. Player A, start by just tapping the ball with the inside and outside of your feet. Tiny little touches.",
      },
      {
        time: 320,
        text: "Imagine the ball is glued to your feet, just like Messi! Keep it super, super close. That's the secret.",
      },
      {
        time: 328,
        text: 'Tap, tap, tap. Little touches. Try to keep your head up and take a quick peek around the room.',
      },
      {
        time: 336,
        text: "Now, let's try to dribble forward a few steps, keeping it close. Then turn around and come back. Lots of little taps, not big kicks.",
      },
      {
        time: 345,
        text: "Messi is the king of keeping the ball close. That's our only goal right now! Tiny touches.",
      },
      {
        time: 353,
        text: "Don't kick it and run! It's tap, tap, tap. You are the boss of that ball!",
      },
      {
        time: 360,
        text: "One minute in! Your feet are getting faster! Let's try to dribble in a little circle now.",
      },
      {
        time: 368,
        text: 'Keep the ball on the outside of your circle, using the outside of your foot to guide it.',
      },
      {
        time: 375,
        text: 'Good! Now go the other way! Make a circle in the other direction.',
      },
      {
        time: 382,
        text: "Let's use the bottom of our foot. Roll the ball forward with your sole. Then roll it back.",
      },
      {
        time: 390,
        text: "Now roll it side to side, right foot then left foot. You're dancing with the ball!",
      },
      {
        time: 398,
        text: 'Okay, back to dribbling. Take five little Messi touches forward, then stop the ball dead with the bottom of your shoe.',
      },
      {
        time: 407,
        text: "Let's do it again. Five touches, then STOP. Be the boss of that ball!",
      },
      {
        time: 414,
        text: "Try to look up for a second while you're dribbling. Just a quick peek! See your amazing partner cheering for you!",
      },
      {
        time: 422,
        text: "You're doing great. Keep that ball close. That's the Messi secret.",
      },
      {
        time: 428,
        text: "Let's see another circle. Make it a perfect, clean circle.",
      },
      {
        time: 434,
        text: 'Awesome. Your control is looking fantastic.',
      },
      {
        time: 440,
        text: 'Get ready to switch! Final ten seconds for Player A!',
      },
      {
        time: 445,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 450,
        text: "Great job, Player A! Now it's Player B's turn to be Messi! Player A, you're the cheerleader now. Player B, start with those tiny taps.",
      },
      {
        time: 460,
        text: 'Remember, the ball is glued to your feet! Keep it super close. Use the inside and outside of your feet.',
      },
      {
        time: 469,
        text: 'Tap, tap, tap. Little touches. Try to keep your head up and take a quick peek.',
      },
      {
        time: 476,
        text: "Now, let's dribble forward a few steps, keeping it close. Then turn and come back.",
      },
      {
        time: 484,
        text: "Lots of little taps, not big kicks. You're in control!",
      },
      {
        time: 490,
        text: "Let's see you dribble in a little circle. Keep the ball right on the edge of your circle.",
      },
      {
        time: 498,
        text: 'Good! Now go the other way! Make the circle the other direction.',
      },
      {
        time: 505,
        text: "Let's use the bottom of our foot. Roll the ball forward with your sole. Then roll it back.",
      },
      {
        time: 513,
        text: "Now roll it side to side, right foot then left foot. You're a dancer!",
      },
      {
        time: 520,
        text: 'Okay, back to dribbling. Five little Messi touches forward, then stop the ball dead with the bottom of your shoe.',
      },
      {
        time: 529,
        text: "Let's do it again. Five touches, then STOP. Show that ball who's boss!",
      },
      {
        time: 536,
        text: "Try to look up for a second while you're dribbling. Quick peek!",
      },
      {
        time: 542,
        text: "You're doing great. Your feet are getting faster and faster.",
      },
      {
        time: 548,
        text: "Let's see another perfect circle. Make it smooth.",
      },
      {
        time: 554,
        text: 'Awesome. Your control is looking amazing.',
      },
      {
        time: 560,
        text: "Last minute of Messi dribbles! Let's see your best stuff.",
      },
      {
        time: 566,
        text: "Dribble with a little more speed now! Go a little faster, but don't lose control! Still tiny touches!",
      },
      {
        time: 574,
        text: 'Thirty seconds left! Just dribble wherever you want. Free dance! But keep it close!',
      },
      {
        time: 582,
        text: 'Show me your best moves! A little spin, a quick turn!',
      },
      {
        time: 588,
        text: 'Final push! Keep that ball glued to your feet!',
      },
      {
        time: 593,
        text: 'Get ready for the countdown!',
      },
      {
        time: 597,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and FREEZE! Awesome control, both of you!',
      },
      {
        time: 600,
        text: "Fantastic job with the Messi Dribble! Now for a super cool move from a legend: 'The Zidane Turn'! This one is all about a sneaky spin.",
      },
      {
        time: 610,
        text: "Let's learn it without the ball first. Stand up straight. Put your right foot on top of an imaginary ball.",
      },
      {
        time: 618,
        text: 'Now, pull your right foot back and as you do, spin your whole body to the right. Try to do a full circle!',
      },
      {
        time: 626,
        text: "Awesome! Let's try that again. Right foot on the ball, pull it back, and spin. It's a dance move!",
      },
      {
        time: 634,
        text: "Okay, now with the real ball! One player at a time. Player A, you're up! Dribble forward slowly.",
      },
      {
        time: 642,
        text: 'Now, put the bottom of your right foot on the ball and stop it.',
      },
      {
        time: 648,
        text: 'Just like we practiced, pull the ball back behind your standing leg and spin your body to the right at the same time.',
      },
      {
        time: 657,
        text: "As you finish the spin, take the ball away with the outside of that same right foot. It's a drag, spin, and take!",
      },
      {
        time: 666,
        text: "This move is amazing for tricking defenders! They think you're going one way... PSYCH! You spin the other way.",
      },
      {
        time: 675,
        text: "Let's try it again. Dribble... stop, drag, spin, and take! Good.",
      },
      {
        time: 682,
        text: 'Use your arms to help you spin and to protect the ball. Keep that defender away!',
      },
      {
        time: 690,
        text: "Let's try it with the other foot. Dribble forward, stop with your left foot.",
      },
      {
        time: 697,
        text: 'Now, drag it back with your left foot and spin your body to the left. Take it away with the outside of your left foot.',
      },
      {
        time: 706,
        text: "Awesome! You're a two-footed magician. Keep practicing that.",
      },
      {
        time: 712,
        text: "Don't worry if it's not perfect. Zidane practiced this a million times!",
      },
      {
        time: 719,
        text: "Let's see another one with your right foot. Make it smooth.",
      },
      {
        time: 725,
        text: 'Now one with your left. Excellent!',
      },
      {
        time: 730,
        text: "Two minutes in! Your spins are looking great. Let's get ready to switch players.",
      },
      {
        time: 737,
        text: 'Player A, show me one more perfect Zidane turn. Make it your best one yet.',
      },
      {
        time: 744,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 750,
        text: "Great job, Player A! Now it's Player B's turn to be Zidane! Player A, you're the cheerleader. Player B, let's start by dribbling forward slowly.",
      },
      {
        time: 760,
        text: 'Now, put the bottom of your right foot on the ball and stop it.',
      },
      {
        time: 766,
        text: 'Pull the ball back behind your standing leg and spin your body to the right. Then take it away with the outside of your right foot.',
      },
      {
        time: 776,
        text: "Drag, spin, and take! You've got it! Now let's try it with your left foot.",
      },
      {
        time: 783,
        text: 'Dribble, stop with the left, drag it back, and spin to the left. Perfect!',
      },
      {
        time: 791,
        text: 'Remember to use your arms to help you balance and protect the ball.',
      },
      {
        time: 798,
        text: "Let's see five good turns with your right foot. Go for it.",
      },
      {
        time: 804,
        text: 'Now five with your left. Keep that quality high.',
      },
      {
        time: 810,
        text: "The key is the drag. You have to pull the ball right behind your standing leg. That's the magic part.",
      },
      {
        time: 818,
        text: "You're doing great. This is a super fun move to use in a game.",
      },
      {
        time: 824,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 830,
        text: "Excellent. You're looking like a real pro.",
      },
      {
        time: 835,
        text: 'Keep that focus. Every repetition makes you better.',
      },
      {
        time: 840,
        text: "Last minute of Zidane turns! Let's see your best stuff.",
      },
      {
        time: 846,
        text: 'Try to do it a little bit faster now. A quicker dribble, a quicker spin.',
      },
      {
        time: 853,
        text: 'Thirty seconds left! Show me your best moves! A little spin, a quick turn!',
      },
      {
        time: 860,
        text: "Let's see a perfect right-footed turn.",
      },
      {
        time: 865,
        text: 'Now a perfect left-footed turn.',
      },
      {
        time: 870,
        text: 'Final push! Keep that ball under control.',
      },
      {
        time: 875,
        text: 'Get ready for the countdown!',
      },
      {
        time: 880,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... and TIME! You're both magicians!",
      },
      {
        time: 890,
        text: "That was some amazing skill! Get ready for our next exercise, which is all about teamwork: the 'Give-and-Go'!",
      },
      {
        time: 900,
        text: "This is one of the most important moves in soccer! Player A, you'll start with the ball. Player B, you'll be the 'wall'.",
      },
      {
        time: 910,
        text: "Player A, pass the ball to Player B. As SOON as you pass it, don't just stand there! You have to sprint forward into an open space.",
      },
      {
        time: 922,
        text: "Player B, you are the wall! When the ball comes to you, don't stop it. Just tap it back with one touch into the space where your partner is running.",
      },
      {
        time: 936,
        text: "Let's try it! Pass, run, and get the ball back! That's a 'Give-and-Go'!",
      },
      {
        time: 944,
        text: 'The runner has to be fast! Explode after you pass the ball! Vroom!',
      },
      {
        time: 952,
        text: "The 'wall' player, your pass has to be soft and perfect. Aim right for their path.",
      },
      {
        time: 961,
        text: "This is how you get around defenders super fast. It's a 1-2 punch!",
      },
      {
        time: 969,
        text: "Okay, now switch roles! Player B passes and runs, and Player A is the wall. Let's see it.",
      },
      {
        time: 978,
        text: "Communicate! Yell 'Here!' or 'Ball!' so your partner knows where you're going.",
      },
      {
        time: 986,
        text: "Let's see five good ones each. Keep switching after every turn.",
      },
      {
        time: 993,
        text: 'One minute in! The timing is looking good. Runners, make sure you run into the open grass, not into your partner.',
      },
      {
        time: 1004,
        text: 'Wall player, that one-touch pass is tricky. Just focus on a clean, simple tap.',
      },
      {
        time: 1013,
        text: "Let's see it again. Pass and MOVE! Don't watch your pass, start running immediately.",
      },
      {
        time: 1022,
        text: 'You two are starting to look like a real team. This is awesome.',
      },
      {
        time: 1029,
        text: "Let's focus on the pass from the runner. It has to be firm and accurate so the wall can play it back easily.",
      },
      {
        time: 1040,
        text: "Now let's focus on the wall's pass. It has to be aimed ahead of the runner, not behind them.",
      },
      {
        time: 1050,
        text: 'Two minutes in! Your teamwork is getting better and better.',
      },
      {
        time: 1057,
        text: "Let's try to make the run a little curved. A little arc into space.",
      },
      {
        time: 1065,
        text: 'That makes it even harder for the defender to follow you. Great job.',
      },
      {
        time: 1072,
        text: 'Keep switching roles. Everyone gets to be the runner and the wall.',
      },
      {
        time: 1080,
        text: 'Halfway! Keep it sharp, team. This move is all about timing and trust.',
      },
      {
        time: 1088,
        text: 'Trust that your partner will run, and trust that your partner will make a good pass.',
      },
      {
        time: 1097,
        text: "Let's see another perfect one. Pass, sprint, one-touch return. Beautiful.",
      },
      {
        time: 1106,
        text: 'Keep that energy high. This is a running drill too!',
      },
      {
        time: 1112,
        text: "Let's see a little more speed on that run. Can you go even faster?",
      },
      {
        time: 1120,
        text: 'Awesome! Now, wall player, can you make your pass even softer?',
      },
      {
        time: 1128,
        text: 'The softer the pass, the easier it is for the runner to control it at full speed.',
      },
      {
        time: 1137,
        text: "You're both doing great. Time on ball is key.",
      },
      {
        time: 1143,
        text: "Last minute! Let's see your best give-and-goes. Game speed now!",
      },
      {
        time: 1151,
        text: "This is a fast-break move! Imagine you're about to score a goal!",
      },
      {
        time: 1159,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1166,
        text: 'Faster runs, sharper passes! Come on!',
      },
      {
        time: 1172,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1178,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1183,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1186,
        text: 'Five, four, three, two, one... and TIME! High-five each other, that was awesome teamwork.',
      },
      {
        time: 1195,
        text: "That was some incredible teamwork! For our last exercise, we're going to train our brains with a super important drill called 'Look Up, Pass Sharp'!",
      },
      {
        time: 1205,
        text: "Here's how it works. Player A, you're going to be the dribbler first. Player B, you're the 'open player'. Dribbler, keep the ball close using your Messi dribble.",
      },
      {
        time: 1215,
        text: "Open player, your job is to jog around and find an open space. When you're ready for a pass, hold up one hand and shout 'NOW!'",
      },
      {
        time: 1225,
        text: 'Dribbler, you MUST keep your head up to see your partner. When you see their hand and hear them shout, you have to make a perfect, sharp pass right to their feet. Ready? Go!',
      },
      {
        time: 1238,
        text: 'Keep your head on a swivel, dribbler! Look around! You have to see the game to be a great player.',
      },
      {
        time: 1246,
        text: 'Good! After the pass, you two switch roles. The open player becomes the dribbler, and the dribbler becomes the open player. Keep it flowing!',
      },
      {
        time: 1257,
        text: 'Open player, make your runs smart. Find a new space every time.',
      },
      {
        time: 1264,
        text: 'Dribbler, that pass has to be quick and accurate. No lazy passes!',
      },
      {
        time: 1271,
        text: 'One minute in! This is fun, right? It teaches you to think and play at the same time, just like the pros.',
      },
      {
        time: 1280,
        text: "Let's see a good pass! Head up, see your teammate, and a sharp pass to their feet.",
      },
      {
        time: 1288,
        text: "Remember to keep the ball close when you're dribbling. It makes it easier to pass quickly when you hear the call.",
      },
      {
        time: 1297,
        text: "Open player, make your call loud and clear! 'NOW!'",
      },
      {
        time: 1303,
        text: 'Excellent teamwork. Keep that communication going.',
      },
      {
        time: 1309,
        text: 'Two minutes in! Your awareness is getting so much better.',
      },
      {
        time: 1315,
        text: "Dribbler, don't panic when they yell 'NOW!'. Take a calm touch and make a good, clean pass.",
      },
      {
        time: 1324,
        text: "Let's focus on the quality of the pass. It should be firm and on the ground.",
      },
      {
        time: 1331,
        text: 'Open player, after you get the pass, start your dribble right away. Quick transition!',
      },
      {
        time: 1339,
        text: 'This is looking like real soccer now. Great job, you two.',
      },
      {
        time: 1345,
        text: "Halfway! Let's make the passes even sharper. A fast, accurate pass right to your partner's feet.",
      },
      {
        time: 1354,
        text: 'Dribbler, challenge yourself. Try to use your weak foot for the pass sometimes.',
      },
      {
        time: 1361,
        text: 'Open player, try to be a little more tricky with your movements. A little fake, then run into space.',
      },
      {
        time: 1370,
        text: "That's it! You're thinking like a team, trying to help each other out.",
      },
      {
        time: 1377,
        text: "Let's see another perfect pass. Head up, see the target, sharp pass.",
      },
      {
        time: 1385,
        text: 'Keep that energy high. This is a thinking game and a running game.',
      },
      {
        time: 1392,
        text: "Let's see another one. Dribble with your head up... good call... perfect pass!",
      },
      {
        time: 1400,
        text: "You're both doing an amazing job. This is a very advanced skill.",
      },
      {
        time: 1406,
        text: "Last minute! Let's be as quick and sharp as possible. Head up, see your teammate, perfect pass.",
      },
      {
        time: 1415,
        text: 'Imagine this is the last minute of a big game. Every pass is important!',
      },
      {
        time: 1422,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1428,
        text: 'Faster dribbling, quicker calls, sharper passes! Come on!',
      },
      {
        time: 1434,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1440,
        text: 'One more perfect pass from each of you!',
      },
      {
        time: 1445,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1448,
        text: 'Five, four, three, two, one... and TIME! Incredible awareness!',
      },
      {
        time: 1455,
        text: "Fantastic work today, superstars! You both completed the whole drill! Give your partner a huge high-five and tell them 'Great job!'",
      },
      {
        time: 1466,
        text: "Let's cool down now. Just walk around and shake out your legs. Take a big, deep breath in... and out.",
      },
      {
        time: 1476,
        text: "You both worked on passing, dribbling, turning, teamwork, and your soccer brains today. You're becoming true all-around players. Amazing job. Drill complete!",
      },
    ],
  },
  {
    id: 2171,
    name: 'Intermediate Partner Passing',
    categories: [
      'Passing',
      'Ball Control',
      'Partner Drill',
      'First Touch',
      'Movement',
    ],
    sports: ['soccer'],
    difficulty: 'Intermediate',
    skill:
      'Advanced passing, first touch under pressure, and dynamic movement.',
    instructions:
      'This 30-minute intermediate session is for players ready to take their teamwork to the next level. It focuses on speed of play, receiving in tight spaces, and more complex passing combinations to enhance your game awareness and technical skill.',
    equipment: '1 soccer ball',
    timeTotal: 1746,
    timedInstructions: [
      {
        time: 0,
        text: "Alright team, let's get to work! Welcome to our intermediate drill. First up, a super important one called 'Receive and Escape'. Stand about seven big steps apart. Let's go!",
      },
      {
        time: 10,
        text: "One of you starts as the passer, the other is the receiver. Passer, send a firm, crisp ball right to your partner's feet.",
      },
      {
        time: 18,
        text: "Receiver, imagine a defender is right on your back! As the ball arrives, your first touch must be an 'escape touch'. Push the ball into the open space to your left or right.",
      },
      {
        time: 29,
        text: 'Your first touch is everything here! It has to be sharp and get the ball out of your feet. Then, pass it straight back to your partner. Go!',
      },
      {
        time: 38,
        text: 'Think about opening up your body. Before the ball even gets to you, take a quick look over your shoulder to see where the space is.',
      },
      {
        time: 47,
        text: 'Passer, your job is to make the passes challenging. Keep them firm and on the ground, like a real game.',
      },
      {
        time: 54,
        text: "Receiver, after you escape and pass back, reset quickly! Get back to your spot, ready for the next one. It's all about speed!",
      },
      {
        time: 63,
        text: "Let's try to use the outside of your foot for the escape touch. It's a super quick way to change direction.",
      },
      {
        time: 71,
        text: 'After five passes, you two switch roles. The passer becomes the receiver. Keep track!',
      },
      {
        time: 78,
        text: 'This is how you play in tight spaces in a real game. Receive, escape, pass. Quick, quick, quick.',
      },
      {
        time: 86,
        text: "Let's see it again. A good firm pass, a quick peek over the shoulder, and a sharp escape touch.",
      },
      {
        time: 94,
        text: 'Excellent! The ball should be moving into space before the imaginary defender can even react.',
      },
      {
        time: 101,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 106,
        text: "Let's see another one. Escape to your left this time.",
      },
      {
        time: 111,
        text: 'Good! Now escape to your right. Be unpredictable.',
      },
      {
        time: 116,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 120,
        text: 'Two minutes in! Your escape touches should be faster now. Explode into that space.',
      },
      {
        time: 127,
        text: "Don't just push the ball, accelerate with it! Your first step after the touch should be powerful.",
      },
      {
        time: 135,
        text: "Let's see that explosion. Receive, escape, and GO!",
      },
      {
        time: 141,
        text: "Awesome! That's how you create space for yourself.",
      },
      {
        time: 146,
        text: 'Keep switching roles every five passes. Make sure you both get to work.',
      },
      {
        time: 152,
        text: 'Halfway! Now, passer, you can follow your pass and apply a little bit of pressure. Just a light jog towards the receiver.',
      },
      {
        time: 161,
        text: 'Receiver, you have to be even quicker now! That first touch has to beat your partner.',
      },
      {
        time: 168,
        text: 'This makes it game-realistic. You have to be composed under pressure.',
      },
      {
        time: 174,
        text: "Let's see it. A good pass, a little pressure, and a clean escape touch. You can do it.",
      },
      {
        time: 182,
        text: "Remember to scan before the ball arrives. It's even more important now that there's pressure.",
      },
      {
        time: 190,
        text: "See the space, feel the pressure, and escape. That's soccer!",
      },
      {
        time: 196,
        text: "Keep that quality high. Don't panic. Just a calm, sharp touch.",
      },
      {
        time: 202,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 207,
        text: "Excellent work. You're handling the pressure like a pro.",
      },
      {
        time: 213,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 218,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 224,
        text: 'Good effort! Keep working on it.',
      },
      {
        time: 229,
        text: 'Now back to your strong foot. Make it perfect.',
      },
      {
        time: 234,
        text: 'Prepare for the final minute.',
      },
      {
        time: 238,
        text: "Last minute of this drill! Let's see game speed now. Sharp passes, explosive touches.",
      },
      {
        time: 246,
        text: 'Imagine this is the final minute of a big game. Every touch is important!',
      },
      {
        time: 253,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 259,
        text: 'Faster passes, quicker escapes! Come on!',
      },
      {
        time: 264,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 270,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 275,
        text: 'Get ready for the countdown!',
      },
      {
        time: 279,
        text: 'Five, four, three, two, one... and time! Excellent work.',
      },
      {
        time: 286,
        text: "That was a fantastic first exercise! Now, let's work on some new passes and controls. This one is called 'Driven Pass & Aerial Control'.",
      },
      {
        time: 296,
        text: "Okay, you two, stand about 15 big steps apart. We're working on a different type of pass now: the driven pass. It's low and fast, like a rocket!",
      },
      {
        time: 306,
        text: "To hit a driven pass, you strike the middle of the ball with your shoelaces. Keep your ankle locked and follow through low to the ground. Let's try it. Passer, drive the ball to your partner.",
      },
      {
        time: 318,
        text: 'Receiver, that ball is coming in hot! Your job is to control it with one soft touch. Use the inside of your foot to cushion the pass and absorb all its power.',
      },
      {
        time: 329,
        text: "Imagine your foot is a pillow. Let's see that soft touch. Drive, control. Drive, control.",
      },
      {
        time: 337,
        text: 'Now for the aerial control! Passer, I want you to chip the ball into the air to your partner. A nice, soft, loopy pass.',
      },
      {
        time: 346,
        text: "Receiver, your job is to bring it down out of the air. First, let's use our thigh. Get your thigh up like a little table and let the ball bounce off it, down to your feet.",
      },
      {
        time: 358,
        text: "The key is to relax. Don't be stiff. Your leg should be soft for the ball. After you control it, pass it back.",
      },
      {
        time: 366,
        text: "Let's alternate. One driven pass on the ground, then one chipped pass in the air. Go!",
      },
      {
        time: 374,
        text: "One minute in! Let's see that technique. Good driven passes, soft aerial control.",
      },
      {
        time: 381,
        text: "Let's see a driven pass with your other foot now. Challenge yourself!",
      },
      {
        time: 387,
        text: "Now an aerial control with your other thigh. You're a two-footed player!",
      },
      {
        time: 394,
        text: 'Keep that focus. Watch the ball all the way onto your body.',
      },
      {
        time: 400,
        text: "Let's see another powerful driven pass. Really strike it.",
      },
      {
        time: 406,
        text: 'Now a super soft thigh control. Excellent.',
      },
      {
        time: 411,
        text: "You're doing great. This is how you learn to handle all types of passes.",
      },
      {
        time: 417,
        text: "Two minutes down. Now, let's try to control the aerial ball with our chest.",
      },
      {
        time: 424,
        text: 'Receiver, as the ball comes, make a big chest and lean back a little. Let it hit your chest and drop right in front of you.',
      },
      {
        time: 433,
        text: "It's like a big cushion. Let's see it. Chip, chest control, and pass back.",
      },
      {
        time: 440,
        text: "Awesome! Now let's go back to the driven pass on the ground.",
      },
      {
        time: 446,
        text: 'Now another chip for a chest control. Keep alternating.',
      },
      {
        time: 452,
        text: "You're looking like pros out there. This is fantastic control.",
      },
      {
        time: 458,
        text: "Halfway! Let's pick up the pace. Quick control, quick pass back.",
      },
      {
        time: 465,
        text: "Let's see a driven pass and a quick return pass.",
      },
      {
        time: 470,
        text: 'Now a chip, a quick thigh control, and a quick pass back.',
      },
      {
        time: 476,
        text: 'Now a chip, a quick chest control, and a quick pass back.',
      },
      {
        time: 482,
        text: "The faster you can control the ball, the more time you'll have in a game.",
      },
      {
        time: 489,
        text: "Let's see that speed. Come on, push yourselves.",
      },
      {
        time: 494,
        text: 'Excellent work. Keep that quality high.',
      },
      {
        time: 499,
        text: "Let's see another driven pass. Make it a rocket!",
      },
      {
        time: 505,
        text: 'Great control! Now a high chip. Get under it.',
      },
      {
        time: 511,
        text: "Awesome chest control. You're doing great.",
      },
      {
        time: 516,
        text: "Keep that focus. We're in the final stretch of this drill.",
      },
      {
        time: 521,
        text: "Let's see one more perfect rep of each. A driven pass...",
      },
      {
        time: 527,
        text: 'A thigh control...',
      },
      {
        time: 531,
        text: 'And a chest control. Excellent.',
      },
      {
        time: 536,
        text: "Last minute! Let's see perfect control every time. Thigh, chest, foot... be ready for anything.",
      },
      {
        time: 544,
        text: 'Passer, you can mix it up now. Surprise your partner. A driven pass or a chip.',
      },
      {
        time: 551,
        text: 'Receiver, you have to react to what kind of pass is coming. Stay on your toes!',
      },
      {
        time: 558,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 564,
        text: 'Fast passes, soft control! Come on!',
      },
      {
        time: 569,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 575,
        text: 'One more perfect control from each of you!',
      },
      {
        time: 580,
        text: 'Get ready for the countdown!',
      },
      {
        time: 583,
        text: 'Five, four, three, two, one... and TIME! Awesome touch, you two.',
      },
      {
        time: 591,
        text: "That was some amazing control! Now for a super smart, brainy move called 'The Up-Back-Through' combo!",
      },
      {
        time: 600,
        text: "This is a three-pass trick to beat defenders. Player A starts with the ball. Player B, you're going to check towards your partner, like you're asking for the ball.",
      },
      {
        time: 610,
        text: "Player A, you'll pass the ball to Player B's feet. That's the 'UP' pass. Let's see it.",
      },
      {
        time: 619,
        text: "Now, Player B, with one soft touch, you're going to lay the ball right back to Player A. That's the 'BACK' pass.",
      },
      {
        time: 627,
        text: "As soon as you make that back pass, Player B, you're going to spin and sprint into the space behind you!",
      },
      {
        time: 635,
        text: "Player A, once you get the back pass, you're going to play a final pass into that space for Player B to run onto. That's the 'THROUGH' pass. Let's try the whole thing!",
      },
      {
        time: 646,
        text: "Up, back, through! It's a quick combination to beat a defender. Let's see it again.",
      },
      {
        time: 652,
        text: "The 'back' pass has to be perfect. A soft, simple layoff for your partner.",
      },
      {
        time: 658,
        text: "And the 'through' pass needs perfect timing. You have to pass it right into your partner's path.",
      },
      {
        time: 666,
        text: "Keep practicing that. Let's see five good combos.",
      },
      {
        time: 671,
        text: 'One minute in! Your timing is looking good. Keep that quality high.',
      },
      {
        time: 677,
        text: "Let's switch roles now. Player B, you start with the ball. Player A, you make the run. Go!",
      },
      {
        time: 685,
        text: "Remember the pattern: Up, back, and through! It's a triangle of passes.",
      },
      {
        time: 692,
        text: 'The player making the run has to explode after they lay the ball back. A real sprint!',
      },
      {
        time: 700,
        text: 'And the player making the final pass, you have to be calm and precise. A perfect pass.',
      },
      {
        time: 708,
        text: 'You two are starting to think like a team. This is awesome.',
      },
      {
        time: 714,
        text: 'Keep switching roles every five combos. Everyone gets to be the passer and the runner.',
      },
      {
        time: 720,
        text: "Two minutes in! Let's focus on your communication. The runner can shout 'Through!' to let the passer know they're ready.",
      },
      {
        time: 729,
        text: "Let's hear it! Good communication makes a good team great.",
      },
      {
        time: 735,
        text: 'The quality of that layoff pass is so important. Make it easy for your partner.',
      },
      {
        time: 742,
        text: "And the timing of the through ball is everything. Don't pass it too early or too late.",
      },
      {
        time: 750,
        text: "Halfway! The movement is key. The player making the 'back' pass has to spin and accelerate instantly.",
      },
      {
        time: 759,
        text: "Don't be lazy on the turn! A sharp spin will get you away from the defender.",
      },
      {
        time: 765,
        text: "Let's see five more good combos, focusing on that sharp turn. Go!",
      },
      {
        time: 772,
        text: "Excellent! You're looking like real pros out there.",
      },
      {
        time: 778,
        text: 'Keep that quality high. Every pass in the combo matters.',
      },
      {
        time: 784,
        text: "Let's switch roles again. Keep it fresh.",
      },
      {
        time: 789,
        text: 'This is a brainy move. You have to think one step ahead of the game.',
      },
      {
        time: 795,
        text: "Let's see another perfect one. Up, back, through. Beautiful.",
      },
      {
        time: 801,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 807,
        text: "Let's see one more with your weak foot on the final pass. Challenge yourself.",
      },
      {
        time: 813,
        text: 'Good effort! Keep working on it.',
      },
      {
        time: 818,
        text: 'Now back to your strong foot. Make it perfect.',
      },
      {
        time: 823,
        text: 'Prepare for the final minute.',
      },
      {
        time: 827,
        text: "Last minute! Let's see it at game speed. This should be a fluid, fast combination. No hesitation!",
      },
      {
        time: 836,
        text: "Imagine you're breaking through the defense to score a goal. That's the speed we need!",
      },
      {
        time: 843,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 849,
        text: 'Faster runs, sharper passes! Come on!',
      },
      {
        time: 854,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 860,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 865,
        text: 'Get ready for the countdown!',
      },
      {
        time: 868,
        text: 'Five, four, three, two, one... and TIME! Brilliant combination play.',
      },
      {
        time: 876,
        text: "That was some smart soccer! Now for some flair. We're doing 'Chip and Volley'!",
      },
      {
        time: 883,
        text: "Okay, stand about ten big steps apart. One of you is the 'chipper', and one is the 'volleyer'.",
      },
      {
        time: 890,
        text: "Chipper, your job is to scoop the ball up to your partner, about waist high. A soft, delicate chip, like you're lifting it over a puddle.",
      },
      {
        time: 899,
        text: "Volleyer, your job is to hit the ball back out of the air BEFORE it bounces. That's a volley!",
      },
      {
        time: 907,
        text: "Use the inside of your foot, just like a pass. Lock your ankle and just connect with the ball. Don't swing your leg wildly.",
      },
      {
        time: 916,
        text: "The goal is to volley it right back to your partner's hands. It's all about control, not power.",
      },
      {
        time: 924,
        text: "Let's try it. Chip, volley. Chip, volley. Find a good rhythm.",
      },
      {
        time: 930,
        text: 'Keep your eye on the ball! Watch it all the way onto your foot.',
      },
      {
        time: 936,
        text: "Let's see five good volleys each, then you'll switch roles. Go for it.",
      },
      {
        time: 942,
        text: 'One minute in! Your volleys are looking clean. Remember to keep your body balanced.',
      },
      {
        time: 949,
        text: 'Chipper, make your chips easy to hit. A nice, soft arc.',
      },
      {
        time: 955,
        text: "Volleyer, try to meet the ball out in front of you. Don't let it get too close to your body.",
      },
      {
        time: 962,
        text: "Let's see another good one. Chip... and a perfect volley back. Awesome.",
      },
      {
        time: 968,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 973,
        text: "Let's switch roles now if you haven't already. New chipper, new volleyer.",
      },
      {
        time: 979,
        text: 'This is a great drill for your touch and timing. Keep it up.',
      },
      {
        time: 984,
        text: "Let's see another five good volleys from the new volleyer.",
      },
      {
        time: 989,
        text: "You're both doing great. This is a fantastic skill to work on.",
      },
      {
        time: 994,
        text: "Two minutes in! Let's make it a little harder. Volleyer, try to use your other foot now.",
      },
      {
        time: 1001,
        text: "A weak-foot volley is a superstar skill! It's tricky, so be patient.",
      },
      {
        time: 1008,
        text: 'Focus on locking your ankle and making a clean connection with the ball.',
      },
      {
        time: 1014,
        text: "Let's see three good ones with your weak foot. You can do it.",
      },
      {
        time: 1019,
        text: 'Excellent! Now back to your strong foot. Feels easier, right?',
      },
      {
        time: 1025,
        text: "Let's switch roles again. Everyone gets to practice their weak foot.",
      },
      {
        time: 1031,
        text: "New volleyer, let's see that weak foot volley. Give it a try.",
      },
      {
        time: 1037,
        text: 'Awesome effort! Keep working on it.',
      },
      {
        time: 1042,
        text: "Halfway! Let's try a lace volley. Use your shoelaces to hit it. This will have more power.",
      },
      {
        time: 1050,
        text: 'Point your toe down, lock your ankle, and punch through the ball. Still aim for your partner!',
      },
      {
        time: 1058,
        text: "This is more of a power shot, but we're still trying to be accurate. Let's see it.",
      },
      {
        time: 1064,
        text: "Good power! Now let's go back to the inside-of-the-foot volley for control.",
      },
      {
        time: 1071,
        text: "Let's switch roles again. New volleyer, let's see that lace volley.",
      },
      {
        time: 1077,
        text: "Remember, point that toe down. That's the secret.",
      },
      {
        time: 1082,
        text: "Excellent strike! You're both looking like goal scorers.",
      },
      {
        time: 1088,
        text: 'Keep that focus. Every volley is a chance to get better.',
      },
      {
        time: 1093,
        text: "Let's see another good one. Make it your best one yet.",
      },
      {
        time: 1098,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1102,
        text: "Last minute! Let's see some amazing volleys. Can you do five in a row back and forth without dropping it?",
      },
      {
        time: 1110,
        text: 'This is a big challenge! Chip, volley, chip, volley. Keep it alive!',
      },
      {
        time: 1117,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1123,
        text: "Come on, keep that rally going! Don't let it drop.",
      },
      {
        time: 1128,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1133,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1138,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1141,
        text: 'Five, four, three, two, one... and TIME! Great technique.',
      },
      {
        time: 1148,
        text: "That was some incredible flair! Now it's time to test your skills under pressure with a drill called 'Pressure Passing'!",
      },
      {
        time: 1156,
        text: "This is a big challenge. One of you is the 'worker' in the middle, and the other is the 'feeder' with the ball.",
      },
      {
        time: 1164,
        text: "Feeder, you're going to pass the ball to the worker. Worker, you must control it and pass it back with one or two touches, MAX.",
      },
      {
        time: 1173,
        text: 'As soon as you pass it back, Feeder, you have to jog to a new spot! Worker, you have to adjust and be ready for the next pass from a different angle.',
      },
      {
        time: 1184,
        text: "Worker, you have to be on your toes, constantly checking your shoulders and moving your feet. You'll work for one minute straight! Ready? Go!",
      },
      {
        time: 1194,
        text: 'Feeder, make your partner work! Pass it to their left, to their right, make them move. Keep them guessing!',
      },
      {
        time: 1202,
        text: 'Worker, great first touch! Now a sharp pass back. Good.',
      },
      {
        time: 1208,
        text: 'Feeder, find a new space! Good. Now pass it.',
      },
      {
        time: 1213,
        text: 'Worker, excellent control. This is how you play in a real midfield.',
      },
      {
        time: 1220,
        text: 'Keep that quality high. A good touch and a good pass, every time.',
      },
      {
        time: 1226,
        text: 'Thirty seconds down, thirty to go for the first worker. Keep that energy up!',
      },
      {
        time: 1233,
        text: "Feeder, keep moving! Don't make it easy for them.",
      },
      {
        time: 1239,
        text: 'Worker, check your shoulder! See where the next pass might come from.',
      },
      {
        time: 1245,
        text: 'Final ten seconds for this worker. Push through it!',
      },
      {
        time: 1250,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1255,
        text: "Great job! Now the feeder becomes the worker. Let's see that same high intensity. Ready? Go!",
      },
      {
        time: 1263,
        text: 'New feeder, make your partner work! Move around, find new angles.',
      },
      {
        time: 1270,
        text: 'New worker, stay on your toes! Be ready for anything.',
      },
      {
        time: 1276,
        text: 'Good control and a sharp pass back. Excellent.',
      },
      {
        time: 1282,
        text: 'This is great work and it makes you both smarter and faster players.',
      },
      {
        time: 1289,
        text: 'Keep that communication going if you need to. A quick shout can help.',
      },
      {
        time: 1295,
        text: 'Thirty seconds down for our new worker. Keep that focus!',
      },
      {
        time: 1301,
        text: "Remember to check your shoulder. It's a super important habit.",
      },
      {
        time: 1307,
        text: "Final ten seconds for this worker. Don't stop now!",
      },
      {
        time: 1312,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1317,
        text: "Awesome work! Back to our first worker. Halfway through the drill! Let's up the challenge.",
      },
      {
        time: 1325,
        text: "Now, it's ONE TOUCH ONLY! Worker, you have to pass the ball back with your very first touch.",
      },
      {
        time: 1333,
        text: 'This requires great anticipation. You have to know where your partner is before the ball even arrives. Ready? Go!',
      },
      {
        time: 1342,
        text: 'Feeder, your passes need to be perfect. Make it easy for your partner to play one-touch.',
      },
      {
        time: 1350,
        text: 'Worker, a soft, clean one-touch pass right back. Excellent.',
      },
      {
        time: 1356,
        text: "This is how you play lightning-fast soccer. It's all about thinking ahead.",
      },
      {
        time: 1363,
        text: 'Thirty seconds down for this worker. Keep that mind sharp!',
      },
      {
        time: 1369,
        text: 'Feeder, keep moving! The pressure is on.',
      },
      {
        time: 1374,
        text: 'Worker, adjust your body shape early to make the one-touch pass.',
      },
      {
        time: 1380,
        text: 'Final ten seconds for this worker. High quality!',
      },
      {
        time: 1385,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1390,
        text: "Fantastic! Last worker of the drill. One-touch only. Let's see your best stuff. Ready? Go!",
      },
      {
        time: 1398,
        text: 'Come on, this is the final push. Sharp mind, quick feet.',
      },
      {
        time: 1404,
        text: 'Feeder, keep those passes crisp and clean.',
      },
      {
        time: 1409,
        text: 'Worker, show me that amazing one-touch pass. Perfect.',
      },
      {
        time: 1415,
        text: "You're both doing an incredible job. This is a pro-level drill.",
      },
      {
        time: 1421,
        text: 'Thirty seconds left in the whole exercise! Empty the tank!',
      },
      {
        time: 1427,
        text: 'Faster movement, quicker passes! Go, go, go!',
      },
      {
        time: 1432,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1437,
        text: "Don't stop now, you're almost there!",
      },
      {
        time: 1442,
        text: 'Five, four, three, two, one... and TIME! Incredible work rate.',
      },
      {
        time: 1448,
        text: "That was amazing pressure passing! For our final drill, we're going to put everything together in the 'Continuous Combination'! Take a quick breather.",
      },
      {
        time: 1458,
        text: 'This is all about non-stop soccer. You two will be constantly moving and passing in a small area. The ball should never stop.',
      },
      {
        time: 1467,
        text: "Let's start with a simple one-two pass. Player A, pass to Player B, move to a new spot, and get the one-touch pass back. Go!",
      },
      {
        time: 1477,
        text: 'Good. Now, Player B, after you get the ball back, you take an escape touch, just like our first drill. A sharp touch into space.',
      },
      {
        time: 1487,
        text: 'After your escape touch, look for your partner, who has moved to a new spot, and play another pass. Keep it flowing.',
      },
      {
        time: 1496,
        text: 'The goal is to keep the ball moving between you two with no more than two touches each. Use one-twos, escape touches, and smart passes.',
      },
      {
        time: 1507,
        text: "Communicate! Tell your partner where you want the ball! 'Here!', 'Turn!', 'One-two!'",
      },
      {
        time: 1515,
        text: 'One minute in. The ball should be fizzing between you. This is what top-level soccer looks like. Constant movement, constant thinking.',
      },
      {
        time: 1525,
        text: "Your movement off the ball is just as important as your pass. After you pass, don't be a statue! Move to a new space to help your partner.",
      },
      {
        time: 1535,
        text: "Let's see a good sequence. Pass, move, get it back, escape touch, find your partner. Beautiful.",
      },
      {
        time: 1544,
        text: 'Keep that quality high. Every pass should be on the ground and easy for your partner to control.',
      },
      {
        time: 1552,
        text: "Let's see another one. Don't let the ball stop moving. Keep that rhythm.",
      },
      {
        time: 1559,
        text: "You two are starting to read each other's movements. That's amazing teamwork.",
      },
      {
        time: 1566,
        text: "Two minutes in! Can you do a sequence of five passes without the ball stopping? Let's try it.",
      },
      {
        time: 1574,
        text: 'Count them out! One... two... three... four... five! Awesome!',
      },
      {
        time: 1582,
        text: "Let's try that again. A five-pass combo. This is how you break down a defense.",
      },
      {
        time: 1590,
        text: 'Keep your head up. You have to see where your partner is moving to make the right pass.',
      },
      {
        time: 1598,
        text: 'Excellent work. Keep that intensity high.',
      },
      {
        time: 1603,
        text: "Halfway! Let's add a new rule. Your escape touch must be with your weak foot.",
      },
      {
        time: 1610,
        text: 'This is a big challenge! Get the pass, and take that first touch into space with your other foot.',
      },
      {
        time: 1618,
        text: "It's tricky, but this is how you become a player who can go in any direction.",
      },
      {
        time: 1625,
        text: "Let's see it. A good pass, and a clean weak-foot escape touch. You can do it.",
      },
      {
        time: 1633,
        text: "Don't worry if it's not perfect. Just trying it is making you better.",
      },
      {
        time: 1640,
        text: "Let's go back to using any foot you want. Feel the difference? That's practice paying off.",
      },
      {
        time: 1648,
        text: "Let's see another five-pass combo. Go for it.",
      },
      {
        time: 1654,
        text: "You're both thinking and playing so fast. This is incredible to watch.",
      },
      {
        time: 1661,
        text: "Keep that focus. We're in the final stretch.",
      },
      {
        time: 1666,
        text: "Let's see one more perfect sequence. Pass, move, receive, escape, pass. Beautiful.",
      },
      {
        time: 1674,
        text: 'Prepare for the final minute.',
      },
      {
        time: 1678,
        text: 'Last minute! This is where champions are made. Push through the fatigue. Keep the quality high.',
      },
      {
        time: 1687,
        text: "Let's see your best combination yet! Quick, clean, and clever!",
      },
      {
        time: 1694,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1700,
        text: 'Faster movement, sharper passes! Come on!',
      },
      {
        time: 1706,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1712,
        text: 'One more perfect combo from you two!',
      },
      {
        time: 1717,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1720,
        text: "Five, four, three, two, one... AND THAT'S A WRAP! INCREDIBLE SESSION!",
      },
      {
        time: 1728,
        text: 'Fantastic work today, both of you! That was a high-level session. Give your partner a huge high-five.',
      },
      {
        time: 1736,
        text: "Let's cool down. Jog slowly, shake out your arms and legs. Take some deep breaths.",
      },
      {
        time: 1744,
        text: "You pushed your skills, your brains, and your fitness today. That's how you improve. Amazing job. Drill complete!",
      },
    ],
  },
  {
    id: 2172,
    name: 'Advanced Partner Passing',
    categories: [
      'Passing',
      'Ball Control',
      'Partner Drill',
      'First Touch',
      'Movement',
      'Tactical',
    ],
    sports: ['soccer'],
    difficulty: 'Advanced',
    skill:
      'Elite first touch, speed of play, tactical movement, and creative combination play.',
    instructions:
      'This 30-minute advanced session is for high-level players looking to master the speed, precision, and creativity of top-tier soccer. The drill focuses on one-touch play, deception, and complex tactical movements to build a seamless connection with your partner.',
    equipment: '1 soccer ball',
    timeTotal: 1920,
    timedInstructions: [
      {
        time: 0,
        text: "Alright team, let's raise the level! Welcome to the advanced drill. First up: 'First-Time Pass & Dynamic Movement'. Ten steps apart. Let's work!",
      },
      {
        time: 11,
        text: 'The ball cannot stop moving in this drill. Every single pass is one-touch. As soon as you pass, you have to move. Explode into a new space!',
      },
      {
        time: 22,
        text: "Your movement tells your partner where to pass. Don't just run anywhere. Make a sharp, quick cut to your left or right. Your partner has to read it and play the ball to your new spot.",
      },
      {
        time: 34,
        text: 'The pass has to be perfect: firm, on the ground, and right in front of your partner so they can play it back with one touch. Go!',
      },
      {
        time: 44,
        text: "Think like a superstar. As you're moving to the ball, you should already know where your next pass is going.",
      },
      {
        time: 52,
        text: "Let's vary the movement. A short zig, a little zag, a curved run. Be unpredictable like Messi!",
      },
      {
        time: 61,
        text: 'This is all about having a fast brain and fast feet. Read the game, play the pass, and move!',
      },
      {
        time: 69,
        text: "Let's demand quality. If the pass isn't perfect, or the movement is lazy, we start our count over. Let's try to get ten perfect one-touch passes in a row.",
      },
      {
        time: 82,
        text: 'One... good pass. Two... great movement. Three... keep it going!',
      },
      {
        time: 89,
        text: "Four... sharp pass. Five... you're halfway there! Six... don't get sloppy now.",
      },
      {
        time: 97,
        text: 'Seven... excellent. Eight... almost there. Nine... one more!',
      },
      {
        time: 104,
        text: "Ten! You did it! Awesome job. Let's start a new count.",
      },
      {
        time: 110,
        text: "Keep that ball on the ground. A one-touch pass is much easier when it's not bouncing.",
      },
      {
        time: 117,
        text: "Let's see that sharp movement again. After you pass, explode into a new space.",
      },
      {
        time: 124,
        text: 'Two minutes in! The tempo should be high now. The ball should be a blur between you two. No lazy passes.',
      },
      {
        time: 133,
        text: "Keep that quality high. You're both working so hard.",
      },
      {
        time: 138,
        text: 'The best players in the world are the best passers. This is the most important skill.',
      },
      {
        time: 145,
        text: "Let's see another ten in a row. Go for it.",
      },
      {
        time: 150,
        text: 'Halfway! Now for a big challenge. Weak foot only for the next minute! All one-touch passes must be with your other foot.',
      },
      {
        time: 161,
        text: "This is where you become an amazing player. It's going to be tricky, but you can do it. Let's go!",
      },
      {
        time: 169,
        text: "Focus on locking your ankle and making a clean connection. Don't worry about power, just accuracy.",
      },
      {
        time: 177,
        text: 'Good try! Keep working on it. Every touch with that foot makes it stronger.',
      },
      {
        time: 183,
        text: "Let's see five good one-touch passes in a row with your weak foot. You've got this.",
      },
      {
        time: 190,
        text: "Excellent! You're building a new weapon. Keep it up.",
      },
      {
        time: 195,
        text: "Keep that movement sharp, even with your weak foot. Don't get lazy.",
      },
      {
        time: 201,
        text: "Let's see another good one. Make it clean.",
      },
      {
        time: 206,
        text: "You're both doing great. This is a fantastic drill to work on.",
      },
      {
        time: 211,
        text: 'Keep that focus. Watch the ball all the way onto your foot.',
      },
      {
        time: 217,
        text: 'Awesome. Prepare to switch back to your strong foot.',
      },
      {
        time: 222,
        text: "Last minute! Back to any foot. Let's see the highest tempo yet. Crisp, sharp, perfect.",
      },
      {
        time: 230,
        text: "This is game speed now. Imagine you're playing in a big final. Every pass matters.",
      },
      {
        time: 238,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 244,
        text: 'Faster movement, sharper passes! Come on!',
      },
      {
        time: 249,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 255,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 260,
        text: 'Get ready for the countdown!',
      },
      {
        time: 263,
        text: 'Five, four, three, two, one... and hold. Top level.',
      },
      {
        time: 270,
        text: "That was a fantastic first exercise! Now, let's add some magic to your game. It's time for 'No-Look & Disguised Passes'!",
      },
      {
        time: 279,
        text: "This is all about tricking the defender. For now, let's use two touches. Your partner will pass you the ball.",
      },
      {
        time: 286,
        text: 'Receive the ball, but your first touch must be a setup for deception. As you receive it, I want you to look all the way to your left, but get ready to pass to your right. Sell the fake!',
      },
      {
        time: 298,
        text: "Let's try a no-look pass. Stare right at your partner, but with your second touch, slide the ball to their left or right foot without looking. It's all about feeling where they are.",
      },
      {
        time: 309,
        text: "Now, a disguised pass. Shape your body like you're going to hit a super powerful long pass, but at the last second, just cut the ball back to your partner with the inside of your foot.",
      },
      {
        time: 320,
        text: "This is how you trick the best defenders. Let's practice. One player is the 'magician', the other is the 'assistant'. Magician, do three deceptive passes, then you'll switch.",
      },
      {
        time: 331,
        text: "Let's see a no-look pass first. Look your partner in the eye, and pass to the side. Good!",
      },
      {
        time: 338,
        text: 'Now a disguised pass. Big wind-up... and a soft pass. Excellent fake!',
      },
      {
        time: 345,
        text: 'One more no-look. The bigger the fake, the better the trick!',
      },
      {
        time: 351,
        text: "Awesome! Switch roles. New magician, you're up!",
      },
      {
        time: 356,
        text: "Remember, it's all in your body language. A quick head fake or a dip of the shoulder makes the move unstoppable.",
      },
      {
        time: 365,
        text: "Let's see that disguised pass. Make me believe you're going to kick it a mile!",
      },
      {
        time: 372,
        text: "Perfect! Now a no-look. Where's it going? Great deception!",
      },
      {
        time: 379,
        text: 'Keep that quality high. Every pass is a chance to practice your magic.',
      },
      {
        time: 385,
        text: "Two minutes in! Your fakes are looking great. Let's try to make it even harder.",
      },
      {
        time: 392,
        text: "Let's combine them. A one-touch no-look pass. This is a superstar move.",
      },
      {
        time: 399,
        text: "As the pass comes to you, look away and guide it back to your partner with one single touch. It's all one motion.",
      },
      {
        time: 408,
        text: "This is a challenge, so be patient. You'll get better with practice. Let's see five attempts each. Go for it.",
      },
      {
        time: 414,
        text: 'Good try! The key is to know where your partner is without looking.',
      },
      {
        time: 420,
        text: "Let's switch roles. New magician, let's see that one-touch no-look.",
      },
      {
        time: 426,
        text: "You're both doing great. This is a very advanced skill.",
      },
      {
        time: 432,
        text: "Halfway! Let's go back to the two-touch disguised pass. The big power fake.",
      },
      {
        time: 439,
        text: "Let's see a big, dramatic wind-up. Really sell it!",
      },
      {
        time: 445,
        text: "And then a soft, clever pass. You're a true magician.",
      },
      {
        time: 451,
        text: 'Now the other way. Fake with your other foot. Challenge yourself.',
      },
      {
        time: 457,
        text: 'Excellent! Now back to the no-look pass. Stare me down and pass to the side.',
      },
      {
        time: 464,
        text: "The more you practice these fakes, the more you'll use them in a game without even thinking.",
      },
      {
        time: 472,
        text: "Let's see another one. Make it your best fake yet.",
      },
      {
        time: 478,
        text: "Awesome. You're looking like a pro.",
      },
      {
        time: 483,
        text: "Keep that quality high. We're building great habits.",
      },
      {
        time: 489,
        text: "Let's see one more with your weak foot. A disguised pass.",
      },
      {
        time: 495,
        text: 'Good. Now get ready for the final minute.',
      },
      {
        time: 500,
        text: 'Last minute! Freestyle! Mix in all your deceptions. No-looks, disguised passes, fakes. Be creative, have fun with it.',
      },
      {
        time: 509,
        text: "Surprise your partner! They won't know what's coming.",
      },
      {
        time: 515,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 521,
        text: 'Faster, sharper, more deceptive!',
      },
      {
        time: 526,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 531,
        text: 'Final fifteen seconds. Give me your best trick yet.',
      },
      {
        time: 536,
        text: "Let's see that magic!",
      },
      {
        time: 541,
        text: 'Almost there, keep working!',
      },
      {
        time: 546,
        text: 'Five, four, three, two, one... and time! Pure magic.',
      },
      {
        time: 552,
        text: "That was some next-level deception! Now, let's take our game to the air with 'Aerial Combinations & Flick-Ons'!",
      },
      {
        time: 560,
        text: "This is all about controlling the ball before it hits the ground. Player A, you'll be the server first. Player B, you're the target. Server, toss a nice, high, loopy ball to your partner.",
      },
      {
        time: 571,
        text: "Player B, your first job is a 'cushioned header'. As the ball comes, meet it with your forehead, but be soft! Nod it gently right back to your partner's hands.",
      },
      {
        time: 582,
        text: "Let's try it. Toss, and a soft header back. Perfect! Now let's try a 'flick-on' header, like the pros do to set up a goal.",
      },
      {
        time: 592,
        text: 'This time, Player B, as the ball comes, get under it and use your head to gently flick it backwards over your own head. Player A, you have to run past them to catch it!',
      },
      {
        time: 604,
        text: "Let's see that combo. Toss, flick-on, and run! It's all about timing.",
      },
      {
        time: 611,
        text: "Now, let's try the ultimate aerial control: a chest-down to a volley. Player A, chip the ball to Player B.",
      },
      {
        time: 619,
        text: 'Player B, cushion the ball on your chest, let it drop, and then volley it back to your partner with one touch, before it hits the ground!',
      },
      {
        time: 630,
        text: 'This requires superstar touch and timing. The chest control has to be perfect to set up the volley.',
      },
      {
        time: 638,
        text: "Let's put it in a sequence. Player A chips to B. B does a chest-down volley back to A. A catches it, and then you switch roles. Go!",
      },
      {
        time: 650,
        text: 'One minute in! Your aerial control is looking great. Remember to be soft with every touch.',
      },
      {
        time: 657,
        text: "Let's see another cushioned header. Just a gentle nod.",
      },
      {
        time: 663,
        text: 'Now a flick-on! Get that timing right. Flick it right over your head.',
      },
      {
        time: 670,
        text: 'Now the chest-volley combo. This is the big one. Chest, volley! Awesome!',
      },
      {
        time: 678,
        text: 'Keep switching roles so you both get to practice everything.',
      },
      {
        time: 684,
        text: 'Two minutes in! The quality of the chip from the server is just as important as the control. Make it easy for your partner.',
      },
      {
        time: 693,
        text: "Let's focus on the flick-on again. The key is to get your head behind the ball and just guide it backwards.",
      },
      {
        time: 701,
        text: "Let's see three good flick-ons in a row. You can do it.",
      },
      {
        time: 707,
        text: "Now let's see three perfect chest-volleys. A soft chest, a clean volley.",
      },
      {
        time: 714,
        text: "You're both looking like target forwards in the box! Great work.",
      },
      {
        time: 720,
        text: "Halfway! Let's try a new, tricky control. The shoulder pass! It's a bit unusual, but very effective.",
      },
      {
        time: 729,
        text: "Server, toss the ball near your partner's shoulder. Receiver, as the ball drops, dip your shoulder and just pop it up to your partner.",
      },
      {
        time: 739,
        text: "It's a surprising way to pass the ball. Let's see five attempts each.",
      },
      {
        time: 745,
        text: "Good try! It's all about timing. Just a little shrug of the shoulder.",
      },
      {
        time: 751,
        text: "Now back to the chest-volley. Let's make it perfect.",
      },
      {
        time: 757,
        text: 'And a cushioned header. Remember to be soft.',
      },
      {
        time: 762,
        text: 'And a flick-on! Get that timing down.',
      },
      {
        time: 767,
        text: "You're building a complete set of aerial skills. Keep it up.",
      },
      {
        time: 773,
        text: "Let's see another shoulder pass. Can you get it right back to your partner?",
      },
      {
        time: 779,
        text: "Awesome! You're a creative player.",
      },
      {
        time: 784,
        text: 'Prepare for the final minute.',
      },
      {
        time: 788,
        text: "Last minute! Let's see continuous aerial control. Try to keep the ball from touching the ground between you using headers, volleys, and chest passes.",
      },
      {
        time: 799,
        text: 'This is the ultimate challenge! How many touches can you get in a row?',
      },
      {
        time: 805,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 811,
        text: "Keep it up, keep it up! Don't let it drop!",
      },
      {
        time: 816,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 821,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 826,
        text: 'Get ready for the countdown!',
      },
      {
        time: 829,
        text: 'Five, four, three, two, one... and TIME! World-class touch.',
      },
      {
        time: 837,
        text: "That was some incredible aerial skill! Now it's time to get tactical with a super smart move called 'The Overlap & Third-Man Run'!",
      },
      {
        time: 846,
        text: "This is a high-level move that will really trick the other team. Player A, you'll start with the ball. Player B, you'll be in the middle.",
      },
      {
        time: 855,
        text: "Player A, pass the ball to Player B. As soon as you pass, make a big, sprinting run AROUND the outside of Player B. That's the 'overlap'.",
      },
      {
        time: 866,
        text: "Now, Player B, here's the tricky part. You are NOT going to pass to your running partner. Instead, with one touch, you'll play a soft pass back to where Player A just came from.",
      },
      {
        time: 879,
        text: "Player A, after you make your overlap run, you have to quickly loop back around to get that pass from Player B. You're the secret 'third man'!",
      },
      {
        time: 890,
        text: "Let's try it slowly. Pass, overlap, pass back, loop around. It's a little triangle of movement.",
      },
      {
        time: 898,
        text: "This move is amazing because the defender will follow your overlap run, leaving space for you to get the ball back. It's pure soccer magic.",
      },
      {
        time: 908,
        text: "Let's see it again. Player A, pass and make that big run! Player B, a soft one-touch pass back.",
      },
      {
        time: 917,
        text: "Player A, great loop! Now you're in tons of space. Awesome.",
      },
      {
        time: 923,
        text: "Let's switch roles now. Player B, you start with the ball. Player A, you're in the middle. Let's see the combo.",
      },
      {
        time: 932,
        text: 'Remember, the pass back from the middle player has to be soft and easy to control.',
      },
      {
        time: 939,
        text: 'And the overlap run has to be a real sprint to make the defender follow you!',
      },
      {
        time: 946,
        text: 'You two are starting to think like professional players. This is a very smart move.',
      },
      {
        time: 953,
        text: "Two minutes in! Let's pick up the pace a little bit. The whole sequence should be faster.",
      },
      {
        time: 960,
        text: 'The pass back from the middle player must be one-touch. No stopping the ball.',
      },
      {
        time: 967,
        text: "This makes the move much quicker and harder to defend. Let's see that one-touch pass.",
      },
      {
        time: 974,
        text: 'Excellent! Now the runner has to be even faster on their loop to get back to the ball.',
      },
      {
        time: 981,
        text: 'Keep switching roles every few turns. You both need to master both parts.',
      },
      {
        time: 987,
        text: 'The movement has to be sharp and the passes have to be precise.',
      },
      {
        time: 993,
        text: "Let's see another one. Pass, overlap, one-touch back, loop. Perfect.",
      },
      {
        time: 1000,
        text: 'Keep that quality high. Every pass, every run matters.',
      },
      {
        time: 1006,
        text: 'Halfway! The weight of the passes is critical. The pass back must be soft, but the first pass can be firm.',
      },
      {
        time: 1015,
        text: "Let's focus on the runner's movement. After you pass, it's a full-speed sprint on the overlap.",
      },
      {
        time: 1023,
        text: "Then, a quick change of direction to loop back. It's like a little zig-zag run.",
      },
      {
        time: 1030,
        text: "Let's see that sharp change of direction on the loop. Go!",
      },
      {
        time: 1036,
        text: "Awesome! That's how you create space for yourself.",
      },
      {
        time: 1042,
        text: "Keep that communication going. A little shout can help your partner know you're looping back.",
      },
      {
        time: 1050,
        text: "You're both doing great. This is a very advanced tactical idea.",
      },
      {
        time: 1056,
        text: "Let's see another perfect combo. Make it your best one yet.",
      },
      {
        time: 1062,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1067,
        text: "Last minute! Let's see it at full speed. This should look like a professional team attacking. Fluid, fast, and intelligent.",
      },
      {
        time: 1077,
        text: "Imagine you're in a big game. This is the move that wins the championship!",
      },
      {
        time: 1084,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1090,
        text: 'Faster runs, sharper passes! Come on!',
      },
      {
        time: 1095,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1100,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1105,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1108,
        text: "Five, four, three, two, one... and TIME! That's beautiful soccer.",
      },

      {
        time: 1115,
        text: "That was some incredibly smart teamwork! Now for some pure technique with 'Rapid Fire Volley & Half-Volley'!",
      },
      {
        time: 1123,
        text: "This is a big challenge. One of you is the 'server' with the ball in your hands, and one is the 'striker'. Stand about ten big steps apart.",
      },
      {
        time: 1132,
        text: 'Server, your job is to toss the ball at different heights and speeds to the striker. Your goal is to challenge them!',
      },
      {
        time: 1140,
        text: "Striker, you must hit every ball back to the server's hands with a clean volley or a half-volley. A half-volley is when you hit it right after it bounces. Ready? Go!",
      },
      {
        time: 1152,
        text: 'Focus on clean contact. Lock your ankle and keep your eye on the ball. Your body has to adjust instantly to where the ball is going.',
      },
      {
        time: 1161,
        text: "Let's work for 45 seconds, then you'll switch roles. Server, be relentless! Low tosses, high tosses, fast tosses!",
      },
      {
        time: 1170,
        text: 'Striker, good volley! Now get ready for the next one.',
      },
      {
        time: 1175,
        text: 'Nice half-volley! That was a fantastic bounce.',
      },
      {
        time: 1180,
        text: 'Keep your feet active. You have to be ready to move.',
      },
      {
        time: 1185,
        text: 'Fifteen seconds left for this striker. Keep that quality high.',
      },
      {
        time: 1190,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1195,
        text: "Great job! New server, new striker. Let's see that same high intensity. 45 seconds on the clock. Go!",
      },
      {
        time: 1204,
        text: 'New server, challenge your partner! Make them work for it.',
      },
      {
        time: 1210,
        text: 'New striker, show me that clean technique. A good volley and a good half-volley.',
      },
      {
        time: 1217,
        text: "This is a massive test of your technique and reaction speed. You're doing great.",
      },
      {
        time: 1224,
        text: 'Keep that focus. Watch the ball all the way onto your foot.',
      },
      {
        time: 1230,
        text: 'Fifteen seconds left for this striker. Finish strong!',
      },
      {
        time: 1235,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1240,
        text: "Awesome work! Back to our first striker. Halfway through the drill! Let's up the challenge.",
      },
      {
        time: 1248,
        text: "Now, it's WEAK FOOT ONLY! Striker, you must use your other foot for every volley and half-volley.",
      },
      {
        time: 1257,
        text: 'This will require some practice, but this is how you become a player who can score with both feet. Server, be kind, but not too kind! 45 seconds. Go!',
      },
      {
        time: 1267,
        text: "Striker, focus on locking that ankle and making a clean connection. Don't worry about power.",
      },
      {
        time: 1274,
        text: 'Good try! Every touch with that foot is making it stronger.',
      },
      {
        time: 1280,
        text: "Let's see three good ones in a row with that weak foot. You can do it.",
      },
      {
        time: 1286,
        text: "Excellent! You're building a new weapon.",
      },
      {
        time: 1291,
        text: 'Fifteen seconds left for this striker. Keep working that weak foot.',
      },
      {
        time: 1296,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1301,
        text: "Great effort! New striker, your turn with the weak foot. Let's see that focus. 45 seconds. Go!",
      },
      {
        time: 1310,
        text: 'Come on, this is the final push for the weak foot. Make every rep count.',
      },
      {
        time: 1316,
        text: 'Remember to adjust your body to get in the right position. Good footwork is key.',
      },
      {
        time: 1323,
        text: "Let's see another good one. Make it clean.",
      },
      {
        time: 1328,
        text: "You're both doing an amazing job. This is a very advanced skill.",
      },
      {
        time: 1334,
        text: 'Fifteen seconds left. Finish strong with that weak foot.',
      },
      {
        time: 1339,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1344,
        text: "Awesome work! Final minute of this drill. Back to your strong foot. Let's see a flawless minute. Perfect contact on every single ball.",
      },
      {
        time: 1355,
        text: 'Server, this is the final challenge. Rapid fire! Quick tosses, one after another.',
      },
      {
        time: 1362,
        text: "Striker, you have to be ready! Quick feet, quick reactions. Let's go!",
      },
      {
        time: 1368,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1374,
        text: 'Faster, cleaner, sharper! Come on!',
      },
      {
        time: 1379,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1384,
        text: 'One more perfect strike from each of you!',
      },
      {
        time: 1389,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1392,
        text: "Five, four, three, two, one... and TIME! Striker's paradise.",
      },
      {
        time: 1400,
        text: "That was some incredible technique! Get ready for our final exercise, the ultimate test: 'Game-Speed Improvisation'!",
      },
      {
        time: 1409,
        text: 'This is it. Put it all together. You two will play in a small area, with continuous movement and continuous passing.',
      },
      {
        time: 1418,
        text: 'I will be your coach. I will shout instructions and you must adapt instantly. Start with simple two-touch passing. Find a rhythm. Get moving. Ready? Go!',
      },
      {
        time: 1429,
        text: 'Good. Keep that ball moving. Soft touches, sharp passes.',
      },
      {
        time: 1435,
        text: 'ONE-TOUCH ONLY! SPEED IT UP!',
      },
      {
        time: 1441,
        text: "Good, that's the tempo! Quick feet, quick passes. Don't let it stop.",
      },
      {
        time: 1448,
        text: 'Keep that quality high. Every pass on the ground.',
      },
      {
        time: 1454,
        text: 'AERIAL ONLY! KEEP IT OFF THE GROUND!',
      },
      {
        time: 1460,
        text: 'Use your feet, thighs, chest! Anything to keep it up. A little volley.',
      },
      {
        time: 1467,
        text: 'Good. Now a thigh control. Keep it alive!',
      },
      {
        time: 1473,
        text: 'Back to TWO-TOUCH on the ground. Find that rhythm again.',
      },
      {
        time: 1479,
        text: 'NO-LOOK PASS! DECEIVE YOUR PARTNER!',
      },
      {
        time: 1484,
        text: 'Look one way, pass the other! Good fake! Keep them guessing.',
      },
      {
        time: 1491,
        text: "Let's see another one! A big look away, then a perfect pass.",
      },
      {
        time: 1498,
        text: 'Back to simple ONE-TOUCH! Speed it up!',
      },
      {
        time: 1504,
        text: 'WEAK FOOT ONLY! QUALITY!',
      },
      {
        time: 1509,
        text: 'This is the big challenge. Soft touches with your other foot. You can do it.',
      },
      {
        time: 1517,
        text: 'Keep that quality high. A good pass is a good pass, no matter which foot.',
      },
      {
        time: 1525,
        text: 'UP-BACK-THROUGH! EXECUTE THE COMBO!',
      },
      {
        time: 1530,
        text: 'Remember, pass up, one-touch back, spin and run, then the through ball! Go!',
      },
      {
        time: 1538,
        text: "Excellent! Let's see it again. Switch roles. Go!",
      },
      {
        time: 1544,
        text: "Two minutes in. The quality can't drop when your mind is racing. Stay sharp.",
      },
      {
        time: 1552,
        text: "Back to FREESTYLE! Combine everything you've learned. Show me some creative, intelligent soccer.",
      },
      {
        time: 1560,
        text: 'A one-two, into an escape touch, into a no-look pass! Get creative!',
      },
      {
        time: 1567,
        text: 'Keep that ball moving. You two are a real team.',
      },
      {
        time: 1573,
        text: "Let's see a sequence of five passes without a mistake. Go!",
      },
      {
        time: 1580,
        text: 'Your movement off the ball is just as important as your pass. Always be moving into a new space.',
      },
      {
        time: 1589,
        text: "Halfway! Keep that energy high. Let's see another creative combo.",
      },
      {
        time: 1596,
        text: 'Maybe a chip and volley? Or a driven pass? Your choice!',
      },
      {
        time: 1603,
        text: 'Keep that communication up. Talk to each other.',
      },
      {
        time: 1609,
        text: 'This is what top-level soccer looks like. Thinking and playing fast.',
      },
      {
        time: 1616,
        text: "Let's see another five-pass combo. Push yourselves.",
      },
      {
        time: 1622,
        text: "Excellent work. You're reading the game.",
      },
      {
        time: 1628,
        text: 'Prepare for the final minute. This is the last push.',
      },
      {
        time: 1634,
        text: "Last minute! Empty the tank. This is the final push. Speed, skill, and smarts. Everything you've got.",
      },
      {
        time: 1643,
        text: "Let's see your best combination yet! Quick, clean, and clever!",
      },
      {
        time: 1650,
        text: 'Thirty seconds! Make it a masterpiece!',
      },
      {
        time: 1656,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 1662,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 1669,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 1675,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... AND DONE! PHENOMENAL WORK!',
      },
      {
        time: 1684,
        text: "That was an elite session. You should be proud. High-five your partner, you've earned it.",
      },
      {
        time: 1692,
        text: "Let's cool down. Jog slowly, shake out your arms and legs. Take some deep breaths.",
      },
      {
        time: 1700,
        text: "You've trained like professionals today. That focus, that quality, is what separates good from great. Drill complete!",
      },
    ],
  },
  {
    id: 2173,
    name: 'Beginner Partner Ball Control',
    categories: ['Ball Control', 'Partner Drill', 'First Touch', 'Trapping'],
    sports: ['soccer'],
    difficulty: 'Beginner',
    skill:
      'Fundamental ball control, trapping, and first touch with a partner.',
    instructions:
      'This 30-minute session is all about becoming the boss of the ball! You and your partner will work together to practice controlling the ball with your feet, thighs, and chest, turning practice into a fun, game-like challenge.',
    equipment: '1 soccer ball',
    timeTotal: 1704,
    timedInstructions: [
      {
        time: 0,
        text: "Hey team! Ready to become ball control masters? Let's start with the 'Ground Control Station'. Stand about five big steps apart. Go!",
      },
      {
        time: 8,
        text: 'One partner, pass the ball on the ground to the other. Receiver, your only job is to stop the ball dead with one touch. Make it stick to your foot!',
      },
      {
        time: 17,
        text: "Use the inside of your foot. Open it up like a gate and just block the ball. Don't kick at it, just meet it.",
      },
      {
        time: 24,
        text: "Perfect! Now, try stopping it with the bottom of your foot, the sole. Like you're gently squishing a bug. Stop it, then pass it back.",
      },
      {
        time: 33,
        text: "Let's get a rhythm. Pass, stop dead. Pass, stop dead. The ball should not roll away after your touch.",
      },
      {
        time: 41,
        text: "Be a ball magnet! Imagine your foot has super glue on it, like Messi's glue foot! That's how close the ball should be.",
      },
      {
        time: 50,
        text: "Let's try to alternate. Stop one with your inside, the next with your sole. Keep your brain working!",
      },
      {
        time: 58,
        text: "Stay on your toes, be bouncy! Don't be flat-footed. Be ready for the pass.",
      },
      {
        time: 64,
        text: "Let's see five perfect stops in a row between you two. Count them out loud!",
      },
      {
        time: 70,
        text: 'One... good stop. Two... excellent control.',
      },
      {
        time: 75,
        text: "Three... you've got this. Four... almost there.",
      },
      {
        time: 80,
        text: 'Five! Awesome job. Keep that rhythm going.',
      },
      {
        time: 85,
        text: "Remember to pass it right to your partner's feet. A good pass makes their job easy.",
      },
      {
        time: 92,
        text: "Two minutes in! Your control is looking better. Let's keep that focus.",
      },
      {
        time: 98,
        text: "Let's try stopping it only with your right foot for a few turns.",
      },
      {
        time: 104,
        text: 'Good. Now only with your left foot. Wake up that other foot!',
      },
      {
        time: 110,
        text: 'Back to any foot you want. Keep that ball under your spell.',
      },
      {
        time: 116,
        text: "Let's see another perfect stop. Make it your best one yet.",
      },
      {
        time: 122,
        text: "Excellent! You're looking like a pro.",
      },
      {
        time: 127,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 132,
        text: "Let's see another five in a row. Go for it.",
      },
      {
        time: 137,
        text: "You're both working so hard. This is great to see.",
      },
      {
        time: 142,
        text: 'The best players have the best first touch. This is the most important skill.',
      },
      {
        time: 149,
        text: "Halfway! Now, passer, make it a little trickier. Pass it a bit to your partner's left or right. Receiver, you have to move your feet to get behind the ball before you stop it.",
      },
      {
        time: 161,
        text: "That's it! Good footwork, receiver. Get there and make that perfect stop.",
      },
      {
        time: 168,
        text: "This is like a real game. The pass isn't always perfect. You have to adjust.",
      },
      {
        time: 175,
        text: "Let's see another one to the side. Move your feet, get your body behind it.",
      },
      {
        time: 182,
        text: 'Excellent! Now back to passing straight to each other. Feels easier, right?',
      },
      {
        time: 189,
        text: "Let's see another ten perfect stops in a row, straight passes. Go!",
      },
      {
        time: 195,
        text: "Keep that focus. You're in the zone now.",
      },
      {
        time: 200,
        text: 'Remember to keep your body over the ball. It helps you control it.',
      },
      {
        time: 206,
        text: "You're doing great. Keep that energy high.",
      },
      {
        time: 211,
        text: "Let's see another one. Make it your best stop of the day.",
      },
      {
        time: 217,
        text: "Awesome. You're a ball-stopping machine.",
      },
      {
        time: 222,
        text: "Keep that quality high. We're building great habits.",
      },
      {
        time: 227,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 232,
        text: 'Good. Now get ready for the final minute.',
      },
      {
        time: 237,
        text: "Last minute of this drill! Let's see your best stops yet. Make that ball your best friend.",
      },
      {
        time: 245,
        text: "Let's see how many perfect stops you can get in 30 seconds. Count them up! Go!",
      },
      {
        time: 252,
        text: "Faster passes now! Test your partner's control.",
      },
      {
        time: 258,
        text: 'Quick feet, soft touch! Come on!',
      },
      {
        time: 263,
        text: "Keep that energy high! You're almost there.",
      },
      {
        time: 268,
        text: 'Come on, push yourselves! Final few seconds of the count.',
      },
      {
        time: 273,
        text: 'Thirty seconds left! Can you speed it up even more? Faster, faster!',
      },
      {
        time: 280,
        text: "Don't get tired now. This is where champions are made.",
      },
      {
        time: 285,
        text: 'Final push! Give me your best passes and stops of the day.',
      },
      {
        time: 291,
        text: 'Get ready for the countdown!',
      },
      {
        time: 294,
        text: 'Five, four, three, two, one... and time! Great first touches.',
      },
      {
        time: 300,
        text: "That was a fantastic first exercise! No break, straight into the next drill! We're going up a level with 'Toss and Trap' using your feet.",
      },
      {
        time: 309,
        text: "Okay, one of you is the 'tosser', one is the 'trapper'. Tosser, stand about four big steps away and toss the ball underhand so it bounces once before it gets to your partner.",
      },
      {
        time: 320,
        text: 'Trapper, your job is to control that bouncing ball with one touch of your foot. Get it on the ground and under control instantly.',
      },
      {
        time: 329,
        text: 'Use the inside of your foot to cushion it. As the ball comes up from the bounce, meet it with a soft foot and guide it down.',
      },
      {
        time: 338,
        text: 'Your foot should be like a pillow. Take all the speed and sting out of the ball.',
      },
      {
        time: 344,
        text: "After you trap it, just pass it back to the tosser. Let's do five traps each, then you'll switch roles. Go!",
      },
      {
        time: 353,
        text: 'This is tricky! If you miss it, no big deal. Just get the ball and try again. This is how we learn.',
      },
      {
        time: 361,
        text: "Let's see that soft touch. Toss, bounce, trap. Perfect.",
      },
      {
        time: 367,
        text: 'Remember to move your feet to get your body right behind the ball before it arrives.',
      },
      {
        time: 374,
        text: "One minute in! Your traps are looking good. Let's switch roles if you haven't already.",
      },
      {
        time: 381,
        text: "New trapper, let's see that focus. Watch the ball all the way to your foot.",
      },
      {
        time: 388,
        text: 'Tosser, make your tosses nice and easy to control. A good toss helps your partner.',
      },
      {
        time: 395,
        text: "Let's see another perfect trap. Make it stick to your foot.",
      },
      {
        time: 401,
        text: "Awesome! You're making it look easy.",
      },
      {
        time: 406,
        text: "Two minutes down. Let's try trapping it with the top of your foot, where your shoelaces are. Lift your foot up to meet the ball.",
      },
      {
        time: 415,
        text: "This is a different feel. It's great for controlling balls that are dropping straight down.",
      },
      {
        time: 422,
        text: "Let's see five good traps with your laces. Go for it.",
      },
      {
        time: 428,
        text: 'Remember to be soft! A hard foot will send the ball flying.',
      },
      {
        time: 434,
        text: "Let's switch roles again. Everyone gets to practice with their laces.",
      },
      {
        time: 440,
        text: "New trapper, let's see that laces control. You can do it.",
      },
      {
        time: 446,
        text: "Halfway! You two are getting the hang of this. Let's see how soft you can make your touch. The ball should drop right in front of you.",
      },
      {
        time: 456,
        text: "Let's try a challenge. Alternate your traps. One with the inside of your foot, one with your laces.",
      },
      {
        time: 464,
        text: 'This is great for your soccer brain! You have to think and react.',
      },
      {
        time: 470,
        text: 'Inside... good. Now laces... awesome!',
      },
      {
        time: 475,
        text: "Keep that pattern going. You're building great skills.",
      },
      {
        time: 481,
        text: "Let's switch roles again. New trapper, let's see you alternate.",
      },
      {
        time: 487,
        text: "I'm impressed, keep up the good work.",
      },
      {
        time: 493,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 499,
        text: "Let's see one more perfect inside trap.",
      },
      {
        time: 504,
        text: 'And one more perfect laces trap. Excellent.',
      },
      {
        time: 509,
        text: 'Last minute! Tosser, you can make the bounces a little trickier now. A little higher or a little lower.',
      },
      {
        time: 518,
        text: 'Trapper, you have to adjust! Read the bounce and get your body in position.',
      },
      {
        time: 525,
        text: "That's it! Good reaction and a great touch.",
      },
      {
        time: 531,
        text: "Let's switch roles for the final time. Last 30 seconds!",
      },
      {
        time: 537,
        text: 'New trapper, show me your best control. Tosser, give them a challenge!',
      },
      {
        time: 544,
        text: 'Come on, finish strong! Every touch matters.',
      },
      {
        time: 550,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 556,
        text: 'One more perfect trap from each of you!',
      },
      {
        time: 561,
        text: 'Get ready for the countdown!',
      },
      {
        time: 564,
        text: 'Five, four, three, two, one... and TIME! Awesome control.',
      },
      {
        time: 570,
        text: "That was some fantastic trapping! No break, we're moving up the body. Time for 'Thigh Traps'!",
      },
      {
        time: 578,
        text: "Same setup as before. Tosser and trapper. But this time, tosser, you're aiming for your partner's thigh. A nice, soft toss, about waist-high.",
      },
      {
        time: 588,
        text: 'Trapper, your job is to get your thigh in the way of the ball. Lift your knee up so your thigh is flat, like a little table.',
      },
      {
        time: 597,
        text: "Let the ball hit your thigh and die. Don't swing at it! Just let the ball bounce off you and drop to the ground.",
      },
      {
        time: 606,
        text: "The secret is to pull your leg back just a tiny bit as the ball hits. It's like catching an egg. You have to be soft.",
      },
      {
        time: 615,
        text: 'After it drops to the ground, pass it back. Five tosses each, then switch. Go!',
      },
      {
        time: 622,
        text: "Let's see that soft thigh touch. Toss... and cushion. Perfect.",
      },
      {
        time: 628,
        text: 'Remember to stay on your toes so you can adjust to the toss.',
      },
      {
        time: 634,
        text: "Let's see another one. Make that thigh a big target.",
      },
      {
        time: 640,
        text: "One minute in! Your thigh control is looking good. Let's switch roles if you haven't already.",
      },
      {
        time: 648,
        text: "New trapper, let's see that focus. Watch the ball all the way to your thigh.",
      },
      {
        time: 655,
        text: 'Tosser, a nice, gentle toss is perfect. Help your partner succeed.',
      },
      {
        time: 661,
        text: "Let's see another perfect trap. The ball should drop right at your feet.",
      },
      {
        time: 668,
        text: "Awesome! You're making it look easy.",
      },
      {
        time: 673,
        text: "Two minutes in. Let's try to control it and take a second touch before it hits the ground. So, thigh, then foot. Tap-tap!",
      },
      {
        time: 683,
        text: "This is a great way to get the ball ready for a quick pass or a dribble. Let's see it. Thigh, foot, pass back.",
      },
      {
        time: 692,
        text: "Now try it with your other leg. Left thigh, left foot. It's a great challenge.",
      },
      {
        time: 700,
        text: "Let's switch roles again. Everyone gets to practice the thigh-foot combo.",
      },
      {
        time: 707,
        text: "New trapper, let's see that quick two-touch control.",
      },
      {
        time: 713,
        text: "Halfway! Your control is improving. Make sure you're on your toes, ready to move to the toss.",
      },
      {
        time: 721,
        text: "Let's try a new challenge. Tosser, make the toss a little to the side.",
      },
      {
        time: 727,
        text: 'Trapper, you have to move your feet, get in position, and then control it with your thigh.',
      },
      {
        time: 735,
        text: "That's it! Good footwork and a great touch.",
      },
      {
        time: 741,
        text: 'This is more like a real game. You have to move to the ball.',
      },
      {
        time: 747,
        text: "Let's switch roles again. New trapper, get ready to move.",
      },
      {
        time: 753,
        text: 'Fantastic, keep up the good work.',
      },
      {
        time: 759,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 765,
        text: "Let's go back to a straight toss. Make the thigh trap perfect.",
      },
      {
        time: 771,
        text: 'And now the thigh-foot combo. Make it sharp.',
      },
      {
        time: 777,
        text: "Excellent. You're looking like a real midfielder.",
      },
      {
        time: 782,
        text: "Last minute! Let's see how clean you can be. A perfect thigh trap should drop the ball right at your feet, ready for a pass.",
      },
      {
        time: 791,
        text: "Let's see five perfect thigh traps in a row from each of you. Switch after five. Go!",
      },
      {
        time: 798,
        text: 'Thirty seconds left! Push yourselves. Make every touch your best touch.',
      },
      {
        time: 804,
        text: "Come on, finish strong! Don't get sloppy now.",
      },
      {
        time: 810,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 816,
        text: 'One more perfect trap from each of you!',
      },
      {
        time: 821,
        text: 'Get ready for the countdown!',
      },
      {
        time: 824,
        text: 'Five, four, three, two, one... and TIME! Excellent job, team.',
      },
      {
        time: 830,
        text: "That was some fantastic thigh control! No break, we're moving up to the final level: 'Chest Control'!",
      },
      {
        time: 838,
        text: "You know the drill. Tosser and trapper. Tosser, this time you're aiming for your partner's chest. A nice, soft, high toss.",
      },
      {
        time: 847,
        text: "Trapper, you're going to make a big chest. Stand tall, arms out for balance, and lean back just a little.",
      },
      {
        time: 855,
        text: 'Let the ball hit you right in the middle of your chest. As it hits, push your chest out a little to meet it. It will drop straight down.',
      },
      {
        time: 864,
        text: "Don't let the ball hit your face or your shoulders. It has to be on that big chest bone. It doesn't hurt if you do it right!",
      },
      {
        time: 873,
        text: 'Once it drops, control it with your feet and pass it back. Five each, then switch. Go!',
      },
      {
        time: 881,
        text: "Let's see that big chest. Toss... and cushion. Perfect drop.",
      },
      {
        time: 887,
        text: 'Remember to keep your eyes open and watch the ball all the way in.',
      },
      {
        time: 893,
        text: "Let's see another one. Make that chest a big target for your partner.",
      },
      {
        time: 899,
        text: "One minute in! Your chest control is looking great. Let's switch roles if you haven't already.",
      },
      {
        time: 907,
        text: "New trapper, let's see that focus. Big chest, ready for the ball.",
      },
      {
        time: 914,
        text: 'Tosser, a nice, gentle toss is perfect. Help your partner succeed.',
      },
      {
        time: 920,
        text: "Let's see another perfect trap. The ball should drop right at your feet.",
      },
      {
        time: 927,
        text: "Awesome! You're making it look easy.",
      },
      {
        time: 932,
        text: "Two minutes in. Let's try to get a touch with our foot before it bounces. Chest, then volley pass back! This is advanced stuff!",
      },
      {
        time: 942,
        text: "It's a quick tap with your foot right after the ball leaves your chest. Let's see it. Chest, volley!",
      },
      {
        time: 950,
        text: "Don't worry if you don't get it every time. This is a big challenge.",
      },
      {
        time: 956,
        text: "Let's switch roles again. Everyone gets to practice the chest-volley.",
      },
      {
        time: 963,
        text: "New trapper, let's see that quick reaction. Chest, then a quick tap back.",
      },
      {
        time: 970,
        text: "Halfway! Your control is improving. Let's go back to the simple chest trap. Make it perfect.",
      },
      {
        time: 978,
        text: 'Toss, and a super soft chest control. The ball should land like a feather.',
      },
      {
        time: 985,
        text: "Let's see five perfect chest traps in a row from each of you. Switch after five. Go!",
      },
      {
        time: 993,
        text: 'This is how you build a world-class first touch. Keep it up.',
      },
      {
        time: 999,
        text: "Remember to lean back and cushion the ball. Don't let it hit you hard.",
      },
      {
        time: 1006,
        text: "You're both doing great. This is a very important skill for controlling high balls.",
      },
      {
        time: 1013,
        text: "Let's see another one. Make it your softest touch yet.",
      },
      {
        time: 1019,
        text: "Excellent! You're looking like a real pro.",
      },
      {
        time: 1024,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 1030,
        text: "Let's go back to the chest-volley combo. Give it another try.",
      },
      {
        time: 1036,
        text: "Awesome! See? You're already getting better at it.",
      },
      {
        time: 1041,
        text: "Last minute! Let's see some pro-level control. Make that ball do exactly what you want it to do.",
      },
      {
        time: 1049,
        text: 'Tosser, you can make the tosses a little trickier. A bit higher, a bit wider.',
      },
      {
        time: 1056,
        text: 'Trapper, you have to move your feet and get your chest behind the ball.',
      },
      {
        time: 1062,
        text: 'Thirty seconds left! Push yourselves. Make every touch your best touch.',
      },
      {
        time: 1068,
        text: "Come on, finish strong! Don't get sloppy now.",
      },
      {
        time: 1074,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1080,
        text: 'One more perfect trap from each of you!',
      },
      {
        time: 1085,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1088,
        text: 'Five, four, three, two, one... and TIME! You look like real players!',
      },
      {
        time: 1095,
        text: "That was some amazing chest control! No break, let's get back to passing on the ground. It's time for 'Control and Turn'!",
      },
      {
        time: 1105,
        text: "Stand about seven steps apart. One player passes to the other. Receiver, this time, you're not stopping the ball. Your first touch is going to move the ball!",
      },
      {
        time: 1117,
        text: "Imagine a defender is right on your back. You have to take an 'escape touch' to get away from them. Take a touch to your left or your right.",
      },
      {
        time: 1129,
        text: 'Use the inside of your foot to push the ball into space. Then, dribble for a few steps and pass it back to your partner. Switch roles after each turn. Go!',
      },
      {
        time: 1142,
        text: 'Before the ball comes, take a quick peek over your shoulder to see where the space is. This is a huge skill!',
      },
      {
        time: 1151,
        text: "Let's see that again. Pass, peek, and a sharp escape touch. Perfect!",
      },
      {
        time: 1159,
        text: 'Remember to keep that first touch close enough that you can take a second touch right away.',
      },
      {
        time: 1166,
        text: "Let's see five good turns each. Keep switching roles.",
      },
      {
        time: 1172,
        text: "One minute in! Your turns are looking good. Let's try using the outside of our foot for the turn.",
      },
      {
        time: 1181,
        text: 'This is a quicker way to get away from a defender. A little tap with the outside of your foot.',
      },
      {
        time: 1189,
        text: "Let's see it. Pass, and an outside-of-the-foot escape touch. Awesome!",
      },
      {
        time: 1196,
        text: "Now try with your other foot. It's a great challenge.",
      },
      {
        time: 1202,
        text: 'Keep that focus. The first touch is everything.',
      },
      {
        time: 1208,
        text: "Let's go back to the inside-of-the-foot turn. Make it smooth.",
      },
      {
        time: 1214,
        text: 'Now an outside-of-the-foot turn. Make it sharp.',
      },
      {
        time: 1220,
        text: "You're building your toolbox of moves! Keep it up.",
      },
      {
        time: 1226,
        text: "Two minutes down. Let's really focus on that peek over the shoulder.",
      },
      {
        time: 1233,
        text: "You can't know where to go if you don't look! See the space, then attack it.",
      },
      {
        time: 1241,
        text: "Let's see a big, obvious peek. Turn your head and look!",
      },
      {
        time: 1247,
        text: 'Good. Now a sharp touch into that space you saw.',
      },
      {
        time: 1253,
        text: "This is how you become a smart soccer player. You're not just kicking the ball; you're thinking.",
      },
      {
        time: 1261,
        text: "Let's see three more good peeks and turns. Go!",
      },
      {
        time: 1267,
        text: 'Keep that quality high. Every repetition matters.',
      },
      {
        time: 1273,
        text: "Halfway! Let's focus on what happens AFTER the turn. Make your turn explosive!",
      },
      {
        time: 1281,
        text: 'Your first touch should take you past the defender, and your first step should be a powerful sprint.',
      },
      {
        time: 1290,
        text: 'First touch, then accelerate into the space. Vroom!',
      },
      {
        time: 1296,
        text: "Let's see that explosion. A sharp touch and a quick burst of speed.",
      },
      {
        time: 1303,
        text: "That's it! You're creating separation. Great job.",
      },
      {
        time: 1309,
        text: "Let's see another one. Turn and GO! Don't hesitate.",
      },
      {
        time: 1315,
        text: 'The more explosive you are, the harder you are to defend.',
      },
      {
        time: 1321,
        text: "Keep that focus. We're in the final stretch of this drill.",
      },
      {
        time: 1327,
        text: "Let's see one more perfect turn and explosion. Make it your best one yet.",
      },
      {
        time: 1334,
        text: 'Awesome! Prepare for the final minute.',
      },
      {
        time: 1338,
        text: "Last minute! Let's see your best moves. Control, turn, and go! Be sharp!",
      },
      {
        time: 1345,
        text: 'Your choice of turn. Inside or outside of the foot. Surprise me!',
      },
      {
        time: 1351,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1357,
        text: 'Faster turns, bigger explosions! Come on!',
      },
      {
        time: 1363,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1369,
        text: 'One more perfect turn from each of you!',
      },
      {
        time: 1374,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1377,
        text: 'Five, four, three, two, one... and TIME! Great movement.',
      },
      {
        time: 1384,
        text: "That was some smart soccer! For our final drill, get ready for the super fun 'Control Gauntlet'!",
      },
      {
        time: 1392,
        text: "This puts it all together. One of you is the 'Feeder', and the other is the 'Controller'. Controller, your job is to be ready for anything!",
      },
      {
        time: 1401,
        text: 'Feeder, you can send the ball any way you want. A ground pass, a bounce pass, a thigh-high toss, or a chest-high toss. Mix it up!',
      },
      {
        time: 1410,
        text: "Controller, you have to read the pass, move your feet, and use the right body part to control the ball in one touch. After you control it, pass it back. Let's work for one minute, then switch. Go!",
      },
      {
        time: 1424,
        text: 'Feeder, start with a simple ground pass. Controller, stop it dead!',
      },
      {
        time: 1430,
        text: 'Good. Feeder, now a bounce pass. Controller, a soft trap with your foot.',
      },
      {
        time: 1437,
        text: 'Excellent! Feeder, now a thigh-high toss. Controller, use that thigh pillow!',
      },
      {
        time: 1444,
        text: 'Awesome! Feeder, now a chest-high toss. Controller, big chest!',
      },
      {
        time: 1451,
        text: "You're using all your skills! Keep it up. Feeder, surprise them!",
      },
      {
        time: 1457,
        text: 'Thirty seconds down for this controller. Keep that focus!',
      },
      {
        time: 1462,
        text: 'Another bounce pass! Good trap.',
      },
      {
        time: 1467,
        text: 'Now a ground pass. Perfect stop.',
      },
      {
        time: 1472,
        text: 'Final ten seconds for this controller. Push through it!',
      },
      {
        time: 1477,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1482,
        text: "Great job! Now the feeder becomes the controller. Let's see that same focus. One minute on the clock. Go!",
      },
      {
        time: 1491,
        text: 'New feeder, challenge your partner! Mix up your passes.',
      },
      {
        time: 1497,
        text: 'New controller, stay on your toes! Be ready for anything.',
      },
      {
        time: 1503,
        text: 'Good chest control! Now pass it back.',
      },
      {
        time: 1508,
        text: 'Now a thigh trap! Excellent.',
      },
      {
        time: 1513,
        text: "This is a real test of everything we've learned. You're doing great.",
      },
      {
        time: 1519,
        text: 'Thirty seconds down for our new controller. Keep that quality high.',
      },
      {
        time: 1525,
        text: 'Remember to move your feet to get in the best position to control the ball.',
      },
      {
        time: 1531,
        text: "Final ten seconds for this controller. Don't stop now!",
      },
      {
        time: 1536,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1541,
        text: "Awesome work! Back to our first controller. Halfway through the drill! Let's make it harder.",
      },
      {
        time: 1549,
        text: "Controller, now you have to do a 'Control and Turn' on every ball you receive on the ground.",
      },
      {
        time: 1557,
        text: 'Feeder, send a ground pass. Controller, take an escape touch and turn away. Go!',
      },
      {
        time: 1564,
        text: 'Good. Now for the high balls, just control them like before. Feeder, send a thigh-high toss.',
      },
      {
        time: 1572,
        text: "Excellent thigh trap. Now another ground pass, feeder. Let's see that turn!",
      },
      {
        time: 1579,
        text: "You're thinking and playing like a real soccer player. Keep it up.",
      },
      {
        time: 1585,
        text: "Let's see one more turn. Make it sharp.",
      },
      {
        time: 1590,
        text: 'Thirty seconds down for this controller. Keep that brain working.',
      },
      {
        time: 1596,
        text: 'Final ten seconds for this controller. Push through it!',
      },
      {
        time: 1601,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1606,
        text: "Fantastic! Last worker of the drill. Remember, control and turn on the ground passes. Let's see your best stuff. Go!",
      },
      {
        time: 1616,
        text: 'Come on, this is the final push. Sharp mind, quick feet.',
      },
      {
        time: 1622,
        text: 'Feeder, keep those passes crisp and clean.',
      },
      {
        time: 1627,
        text: 'Controller, show me that amazing turn. Perfect.',
      },
      {
        time: 1633,
        text: 'Now a high ball. Good chest control.',
      },
      {
        time: 1638,
        text: "You're both doing an incredible job. This is a pro-level drill.",
      },
      {
        time: 1644,
        text: 'Thirty seconds left in the whole exercise! Empty the tank!',
      },
      {
        time: 1650,
        text: 'Faster movement, quicker control! Go, go, go!',
      },
      {
        time: 1655,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1661,
        text: 'One more perfect control from each of you!',
      },
      {
        time: 1666,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1669,
        text: "Five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1676,
        text: 'Fantastic work today, superstars! Your ball control is on another level now. Give your partner a big high-five!',
      },
      {
        time: 1685,
        text: "Let's cool down. Walk it out, shake your legs. You've earned your rest. Amazing job today!",
      },
    ],
  },
  {
    id: 2174,
    name: 'Intermediate Partner Ball Control',
    categories: [
      'Ball Control',
      'Partner Drill',
      'First Touch',
      'Trapping',
      'Movement',
    ],
    sports: ['soccer'],
    difficulty: 'Intermediate',
    skill:
      'Controlling driven passes, aerial balls, and executing a positive first touch under pressure.',
    instructions:
      'This 30-minute intermediate session challenges players to perfect their first touch in game-like scenarios. You and your partner will work on controlling firm passes, aerial balls, and turning that first touch into an immediate advantage.',
    equipment: '1 soccer ball',
    timeTotal: 1755,
    timedInstructions: [
      {
        time: 0,
        text: "Alright team, time to sharpen that touch! First up: 'Receive and Shield'. Stand about eight big steps apart. Let's go!",
      },
      {
        time: 8,
        text: "Passer, send a firm, crisp pass to your partner's feet. Receiver, as the ball arrives, your first touch should bring it across your body, shielding it from an imaginary defender.",
      },
      {
        time: 19,
        text: 'Think about your body position. Get side-on before the ball arrives. Your first touch should be with the foot farthest from the passer, bringing the ball into a safe space.',
      },
      {
        time: 30,
        text: 'After you shield it with that first touch, use your second touch to pass it back firmly. Touch-pass. Touch-pass.',
      },
      {
        time: 39,
        text: "The quality of the pass matters! It has to be firm enough to challenge your partner's control.",
      },
      {
        time: 46,
        text: 'Receiver, after you pass it back, reset your position. Be active, not stationary. Ten passes each, then switch roles.',
      },
      {
        time: 55,
        text: "Let's see that shielding motion again. Get your body between the ball and the imaginary defender.",
      },
      {
        time: 62,
        text: 'Use your arms for balance and to keep the defender away. Be strong!',
      },
      {
        time: 68,
        text: 'One minute in! Your first touch is looking great. Keep it up.',
      },
      {
        time: 74,
        text: "Let's see another one. A sharp pass, and an even sharper first touch.",
      },
      {
        time: 80,
        text: 'Remember to peek over your shoulder before the ball arrives. See the space!',
      },
      {
        time: 87,
        text: 'This is how you play in tight spaces in a real game. Receive, shield, pass. Quick, quick, quick.',
      },
      {
        time: 95,
        text: "Let's switch roles if you haven't already. New passer, new receiver.",
      },
      {
        time: 101,
        text: "New receiver, let's see that side-on body shape. Get ready for the pass.",
      },
      {
        time: 108,
        text: 'Excellent. That first touch is everything. Make it perfect.',
      },
      {
        time: 114,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 120,
        text: 'Two minutes in! Your first touch should be cleaner now. Take the sting out of the pass and guide it into your space.',
      },
      {
        time: 129,
        text: "Let's try using the outside of your foot for the first touch. It's a different feel, but a great skill.",
      },
      {
        time: 137,
        text: 'Good! Now back to the inside of your foot. Which one feels more comfortable?',
      },
      {
        time: 144,
        text: 'Keep working on both. Great players can use any part of their foot.',
      },
      {
        time: 150,
        text: 'Halfway! Now, passer, you can follow your pass and apply light pressure. This forces the receiver to have an even better first touch to create separation.',
      },
      {
        time: 161,
        text: 'Receiver, you have to be even quicker now! That first touch has to beat your partner.',
      },
      {
        time: 168,
        text: 'This makes it game-realistic. You have to be composed under pressure.',
      },
      {
        time: 174,
        text: "Let's see it. A good pass, a little pressure, and a clean escape touch. You can do it.",
      },
      {
        time: 182,
        text: "Remember to scan before the ball arrives. It's even more important now that there's pressure.",
      },
      {
        time: 190,
        text: "See the space, feel the pressure, and escape. That's soccer!",
      },
      {
        time: 196,
        text: "Keep that quality high. Don't panic. Just a calm, sharp touch.",
      },
      {
        time: 202,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 207,
        text: "Excellent work. You're handling the pressure like a pro.",
      },
      {
        time: 213,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 218,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 224,
        text: 'Good effort! Keep working on it.',
      },
      {
        time: 229,
        text: 'Now back to your strong foot. Make it perfect.',
      },
      {
        time: 234,
        text: 'Prepare for the final minute.',
      },
      {
        time: 238,
        text: "Last minute! Let's see game speed. A sharp pass, a clean shielding touch, and a quick pass back. Make it fluid.",
      },
      {
        time: 247,
        text: 'Imagine this is the final minute of a big game. Every touch is important!',
      },
      {
        time: 254,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 260,
        text: 'Faster passes, quicker escapes! Come on!',
      },
      {
        time: 265,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 271,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 276,
        text: 'Get ready for the countdown!',
      },
      {
        time: 279,
        text: 'Five, four, three, two, one... and time! Excellent shielding.',
      },
      {
        time: 286,
        text: "That was a fantastic first exercise! No break, let's keep that energy up! Next, we're dealing with some real pace in a drill called 'Driven Pass & Cushion Control'!",
      },
      {
        time: 296,
        text: "Okay, you two, take a few big steps back. About 12 steps apart. We're working on a new kind of pass now: the driven pass. It's low and fast, like a rocket!",
      },
      {
        time: 307,
        text: "To hit a driven pass, you're going to strike the middle of the ball with your shoelaces. Keep your ankle locked and follow through low to the ground. Let's try it. Passer, drive the ball to your partner.",
      },
      {
        time: 319,
        text: "Receiver, that ball is coming in hot! Your job is to 'cushion' it with one soft touch. You have to absorb its energy. As the ball makes contact with your foot, pull your foot back slightly. It's like catching an egg.",
      },
      {
        time: 333,
        text: 'Your first touch should stop the ball dead, right in front of you. No bouncing away. After you control it, send a driven pass back. Go!',
      },
      {
        time: 342,
        text: "Let's try to control it with different surfaces. First, the inside of your foot. Then, try the outside of your foot. It's trickier but a great skill.",
      },
      {
        time: 352,
        text: "One minute in! Your driven passes are looking powerful. Let's see five good ones in a row from each of you.",
      },
      {
        time: 359,
        text: 'Remember, the key to the cushion control is to be relaxed. If your leg is stiff and hard like a rock, the ball will just bounce off. Stay loose!',
      },
      {
        time: 369,
        text: "Let's see that soft touch again. A hard pass comes in... and you turn it into a soft little marshmallow at your feet. Perfect.",
      },
      {
        time: 379,
        text: 'Keep switching roles every five passes. Both of you need to be masters of the driven pass and the cushion control.',
      },
      {
        time: 388,
        text: "Let's see a driven pass with your weak foot now. It's a challenge, but you can do it!",
      },
      {
        time: 395,
        text: 'And now a cushion control with your weak foot. This is how you become unstoppable.',
      },
      {
        time: 402,
        text: "You're both doing great. This is a high-level skill.",
      },
      {
        time: 407,
        text: "Two minutes down. Let's keep that quality high. Every pass, every touch.",
      },
      {
        time: 413,
        text: "Let's see another powerful driven pass. Really connect with the middle of the ball.",
      },
      {
        time: 420,
        text: 'And another perfect cushion control. Make it look easy.',
      },
      {
        time: 426,
        text: "You're looking like real pros out there. Keep that focus.",
      },
      {
        time: 431,
        text: "Halfway! Let's increase the difficulty. Passer, I want you to aim your driven pass a little to the left or right of your partner.",
      },
      {
        time: 440,
        text: 'Receiver, you have to move your feet quickly to get in front of the ball, and then still make that perfect, soft cushion control. This is game-realistic!',
      },
      {
        time: 451,
        text: "Let's see it. A pass to the side... good movement... and a great touch! Awesome!",
      },
      {
        time: 459,
        text: 'This is how you learn to control tough passes in a real game. Keep working.',
      },
      {
        time: 465,
        text: "Let's see another one. A little wider this time. Challenge your partner!",
      },
      {
        time: 471,
        text: "Excellent hustle and a beautiful touch. You're a superstar.",
      },
      {
        time: 477,
        text: 'Keep switching roles. Everyone gets to practice moving to the ball.',
      },
      {
        time: 483,
        text: 'Remember to stay on your toes. It helps you move faster to those wide passes.',
      },
      {
        time: 490,
        text: "Let's go back to passing straight for a moment. Make that cushion control perfect.",
      },
      {
        time: 497,
        text: 'Now another one to the side. Adjust your feet!',
      },
      {
        time: 502,
        text: "You're doing great. This is a very tiring drill, but it's making you so much better.",
      },
      {
        time: 509,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 514,
        text: "Let's see one more perfect rep. A hard pass to the side, quick movement, and a perfect cushion touch.",
      },
      {
        time: 523,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 527,
        text: "Last minute! Let's see perfect cushion control. That ball should stick to your foot like velcro, no matter how hard the pass is.",
      },
      {
        time: 536,
        text: 'Passer, send in your best driven passes. Really test your partner.',
      },
      {
        time: 542,
        text: 'Receiver, show me that superstar touch. Make every control perfect.',
      },
      {
        time: 548,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 554,
        text: 'Faster passes, softer control! Come on!',
      },
      {
        time: 559,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 565,
        text: 'One more perfect control from each of you!',
      },
      {
        time: 570,
        text: 'Get ready for the countdown!',
      },
      {
        time: 573,
        text: 'Five, four, three, two, one... and TIME! Great control under fire.',
      },
      {
        time: 580,
        text: "That was some amazing control! No break, let's take our game to the air! It's time for 'Aerial Control & Layoff'!",
      },
      {
        time: 588,
        text: "One partner is the server, one is the receiver. Server, you're going to toss a high, loopy ball to your partner.",
      },
      {
        time: 595,
        text: "Receiver, your job is to control the ball out of the air with your thigh or chest, but your second touch MUST be a pass, or a 'layoff', to the side.",
      },
      {
        time: 605,
        text: "So it's two quick touches: Control, then Layoff. Imagine a teammate is running next to you, and you're setting them up for a shot!",
      },
      {
        time: 614,
        text: "Let's try with the thigh first. Receiver, control the ball with your right thigh, and as it drops, use the inside of your foot to pass it to an imaginary teammate on your left.",
      },
      {
        time: 625,
        text: 'Now try laying it off to your right. Control with your thigh, pass to the right. Be quick and efficient. Five each, then switch roles.',
      },
      {
        time: 635,
        text: 'One minute in! Your control is looking great. Remember, the thigh touch has to be super soft, like a pillow.',
      },
      {
        time: 643,
        text: "Let's see it again. A good toss, a soft thigh, and a sharp layoff pass. Perfect.",
      },
      {
        time: 650,
        text: "Now let's try the other leg. Left thigh control, then a layoff. It's a great challenge!",
      },
      {
        time: 657,
        text: 'Keep switching roles so you both get plenty of practice.',
      },
      {
        time: 663,
        text: "This is how you control those tricky high passes in a game. You're doing great.",
      },
      {
        time: 670,
        text: "Let's see another one. Make it your best one yet. Thigh, layoff. Awesome.",
      },
      {
        time: 677,
        text: "Two minutes in! Now let's use the chest. Server, a nice high toss. Receiver, control it with your chest, and as it falls, volley a soft pass to the side.",
      },
      {
        time: 689,
        text: 'Remember to make a big chest and lean back a little. Let the ball bounce off you like a trampoline.',
      },
      {
        time: 697,
        text: "Let's see it. Chest control, then a quick volley layoff. Excellent!",
      },
      {
        time: 704,
        text: "This is a very advanced skill. You're both doing an amazing job.",
      },
      {
        time: 710,
        text: 'Keep switching roles. Everyone gets to practice their chest control.',
      },
      {
        time: 716,
        text: "Let's see another good one. Big chest, soft touch, clean layoff.",
      },
      {
        time: 722,
        text: 'Halfway! Your movement before the ball arrives is key. Adjust your feet to get your body in the right position to control and lay it off cleanly.',
      },
      {
        time: 732,
        text: "Let's try alternating now. Server, your choice! Toss for the thigh or the chest. Receiver, you have to react!",
      },
      {
        time: 741,
        text: 'Good! A thigh control. Now, server, mix it up!',
      },
      {
        time: 746,
        text: 'A chest control! Excellent reaction and a great layoff.',
      },
      {
        time: 752,
        text: 'This is how you train your soccer brain to think and react quickly.',
      },
      {
        time: 758,
        text: 'Keep that quality high. Every touch is important.',
      },
      {
        time: 763,
        text: "Let's switch roles again. New server, new receiver. Let's see that variety.",
      },
      {
        time: 770,
        text: 'Remember, the layoff pass should be soft and easy for your imaginary teammate to run onto.',
      },
      {
        time: 778,
        text: "You're not just controlling the ball, you're creating an attack. Great work.",
      },
      {
        time: 784,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 789,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 793,
        text: 'Last minute! Server, you can make the tosses more difficult now. Higher, faster, wider. Receiver, adapt and execute.',
      },
      {
        time: 803,
        text: 'This is a real game situation. You have to move to the ball and make a perfect touch.',
      },
      {
        time: 810,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 816,
        text: 'Faster movement, cleaner control, sharper layoffs! Come on!',
      },
      {
        time: 822,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 828,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 833,
        text: 'Get ready for the countdown!',
      },
      {
        time: 836,
        text: 'Five, four, three, two, one... and TIME! Excellent combination play.',
      },
      {
        time: 843,
        text: "That was some amazing aerial control! No break, let's get creative with a drill called 'First Touch Forward'!",
      },
      {
        time: 851,
        text: 'Back to passing on the ground. Stand about ten big steps apart. Passer, send a firm ball to your partner.',
      },
      {
        time: 858,
        text: 'Receiver, your first touch must always move you forward, into attacking space. No stopping the ball, no going backwards!',
      },
      {
        time: 867,
        text: 'Take your touch at an angle, past an imaginary defender. Use the inside or outside of your foot to push the ball forward and to the side.',
      },
      {
        time: 877,
        text: 'After that first touch, accelerate for two or three steps, then turn and pass back to your partner. Switch roles after each turn. Go!',
      },
      {
        time: 887,
        text: "This is a mindset. Every time you receive the ball, you should be thinking, 'How can I go forward?'",
      },
      {
        time: 895,
        text: "Let's see it again. A good pass, and a sharp, positive first touch. Excellent.",
      },
      {
        time: 902,
        text: 'Remember to peek over your shoulder before the ball arrives. See the space you want to attack.',
      },
      {
        time: 910,
        text: "Let's see five good forward touches each. Keep switching roles.",
      },
      {
        time: 916,
        text: "One minute in! Your attacking touch is looking great. Let's try to use the outside of your foot for the touch.",
      },
      {
        time: 925,
        text: 'This is a great way to quickly change direction and surprise the defender.',
      },
      {
        time: 932,
        text: 'Now back to the inside of your foot. Which one feels better?',
      },
      {
        time: 938,
        text: "It's great to be able to do both. Keep practicing.",
      },
      {
        time: 943,
        text: "Let's see another one. A sharp touch and a quick acceleration.",
      },
      {
        time: 949,
        text: "You're doing great. Keep that quality high.",
      },
      {
        time: 954,
        text: "Two minutes in! Let's add a move. After your first touch forward, do a step-over or a body feint before you pass it back. Be creative!",
      },
      {
        time: 965,
        text: "So it's: First touch forward, a quick trick, then a pass. Let's see it.",
      },
      {
        time: 972,
        text: 'Awesome! That little fake makes you even more dangerous.',
      },
      {
        time: 978,
        text: 'Keep switching roles. Everyone gets to be the creative player.',
      },
      {
        time: 984,
        text: "Let's see another one. A good touch, a cool move, and a sharp pass.",
      },
      {
        time: 991,
        text: "You're looking like a real attacker. Keep it up.",
      },
      {
        time: 996,
        text: "Halfway! The quality of your first touch determines the success of the move. It can't be too heavy, or you'll lose the ball.",
      },
      {
        time: 1006,
        text: "Let's focus on that touch. It should be about two steps in front of you, perfectly setting you up.",
      },
      {
        time: 1014,
        text: "Let's see five perfect first touches in a row. Go for it.",
      },
      {
        time: 1020,
        text: 'Excellent. Now add the skill move back in.',
      },
      {
        time: 1025,
        text: "Let's see a step-over this time. A big, convincing fake.",
      },
      {
        time: 1031,
        text: "Good. Now let's see a body feint. Dip your shoulder one way, go the other.",
      },
      {
        time: 1038,
        text: "You're building your toolbox of tricks. This is awesome.",
      },
      {
        time: 1044,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1049,
        text: "Let's see one more with your weak foot. A good first touch forward.",
      },
      {
        time: 1055,
        text: 'Excellent effort. Keep working on it.',
      },
      {
        time: 1100,
        text: "Last minute! Let's see some explosive moves. A sharp first touch and a burst of speed. Beat that imaginary defender every time.",
      },
      {
        time: 1109,
        text: "Imagine you're breaking through on goal. That's the speed we need!",
      },
      {
        time: 1115,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1121,
        text: 'Faster turns, bigger explosions! Come on!',
      },
      {
        time: 1127,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1133,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1138,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1141,
        text: 'Five, four, three, two, one... and TIME! Great attacking mindset.',
      },
      {
        time: 1148,
        text: "That was some fantastic creative play! Now for a super fun challenge called the 'Two-Touch Gauntlet'!",
      },
      {
        time: 1156,
        text: "This is a big test of your reactions. One player is the feeder, one is the worker. Feeder, you're going to serve balls from your hands.",
      },
      {
        time: 1164,
        text: "Worker, you have a maximum of two touches to control the ball and play it back to the feeder's hands. Control, pass. That's it.",
      },
      {
        time: 1173,
        text: 'Feeder, you are in charge. Mix it up! A hard throw that bounces, a soft looping toss for a thigh, a fast roller on the ground. Be unpredictable.',
      },
      {
        time: 1184,
        text: "Worker, you have to read the serve and decide instantly how to control it. Foot, thigh, chest... whatever it takes. Let's go! 90 seconds of intense work, then we switch. Go!",
      },
      {
        time: 1197,
        text: "Feeder, let's start with a bounce pass. Worker, get ready.",
      },
      {
        time: 1202,
        text: 'Good control! Sharp pass back.',
      },
      {
        time: 1206,
        text: 'Now a roller on the ground, feeder. Worker, quick feet!',
      },
      {
        time: 1211,
        text: 'Excellent. Now a high toss, feeder. Worker, get under it!',
      },
      {
        time: 1216,
        text: "Great thigh control! You're using your whole body.",
      },
      {
        time: 1221,
        text: "Keep mixing it up, feeder. Don't let the worker get comfortable.",
      },
      {
        time: 1227,
        text: "Worker, stay on your toes. You never know what's coming next.",
      },
      {
        time: 1232,
        text: 'Thirty seconds down for this worker. Keep that focus!',
      },
      {
        time: 1237,
        text: "Let's see another bounce pass. Good. Another roller. Sharp.",
      },
      {
        time: 1242,
        text: 'Now a high one! Get there! Awesome control.',
      },
      {
        time: 1247,
        text: "You're doing great. Incredible work.",
      },
      {
        time: 1251,
        text: 'Final fifteen seconds for this worker. Push through it!',
      },
      {
        time: 1256,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1301,
        text: "Great job! New worker, you're up! Feeder, get ready to challenge them. 90 seconds on the clock. Go!",
      },
      {
        time: 1310,
        text: 'New feeder, make your partner work! Mix up those serves.',
      },
      {
        time: 1315,
        text: 'New worker, show me that sharp control. Two touches max!',
      },
      {
        time: 1321,
        text: 'Good chest control! And a perfect pass back.',
      },
      {
        time: 1326,
        text: 'Now a tricky bounce pass. Excellent trap.',
      },
      {
        time: 1331,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 1336,
        text: 'Thirty seconds down for our new worker. Keep that focus!',
      },
      {
        time: 1341,
        text: "Remember to move your feet to get in the best position. Don't be lazy.",
      },
      {
        time: 1347,
        text: "Final fifteen seconds for this worker. Don't stop now!",
      },
      {
        time: 1352,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1357,
        text: "Awesome work! Back to our first worker. Halfway through the drill! Let's up the challenge.",
      },
      {
        time: 1365,
        text: "Now, it's ONE TOUCH ONLY! Worker, you have to play the ball back with your very first touch. Volley, header, or one-touch pass.",
      },
      {
        time: 1376,
        text: 'This is the ultimate test of your technique and concentration. 45 seconds. Go!',
      },
      {
        time: 1382,
        text: 'Feeder, your serves need to be perfect. Help your partner out.',
      },
      {
        time: 1387,
        text: 'Worker, a clean one-touch volley. Good.',
      },
      {
        time: 1391,
        text: 'Now a one-touch pass on the ground. Sharp.',
      },
      {
        time: 1395,
        text: 'This is how you play lightning-fast soccer.',
      },
      {
        time: 1400,
        text: 'Fifteen seconds left for this worker. Keep it clean!',
      },
      {
        time: 1405,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 1410,
        text: "Fantastic! Last worker of the drill. One-touch only. Let's see your best stuff. 45 seconds. Go!",
      },
      {
        time: 1418,
        text: 'Come on, this is the final push. Sharp mind, quick feet.',
      },
      {
        time: 1423,
        text: 'Feeder, keep those serves crisp and clean.',
      },
      {
        time: 1428,
        text: 'Worker, show me that amazing one-touch pass. Perfect.',
      },
      {
        time: 1433,
        text: "You're both doing an incredible job. This is a pro-level drill.",
      },
      {
        time: 1439,
        text: 'Fifteen seconds left in the whole exercise! Empty the tank!',
      },
      {
        time: 1444,
        text: 'Faster serves, quicker reactions! Go, go, go!',
      },
      {
        time: 1449,
        text: "Final push! Give me everything you've got.",
      },
      {
        time: 1453,
        text: 'Five, four, three, two, one... and TIME! Incredible reactions.',
      },
      {
        time: 1459,
        text: "That was amazing pressure work! For our final drill, we're going to put everything together in the 'Control & Combine'!",
      },
      {
        time: 1467,
        text: 'This is all about non-stop soccer. You two will be constantly moving and passing in a small area. The ball should never stop.',
      },
      {
        time: 1476,
        text: "Let's start with a simple one-two pass. Player A, pass to Player B, move to a new spot, and get the one-touch pass back. Go!",
      },
      {
        time: 1486,
        text: 'Good. Now, Player B, after you get the ball back, you take an escape touch, just like our first drill. A sharp touch into space.',
      },
      {
        time: 1496,
        text: 'After your escape touch, look for your partner, who has moved to a new spot, and play another pass. Keep it flowing.',
      },
      {
        time: 1505,
        text: 'The goal is to keep the ball moving between you two with no more than two touches each. Use one-twos, escape touches, and smart passes.',
      },
      {
        time: 1516,
        text: "Communicate! Tell your partner where you want the ball! 'Here!', 'Turn!', 'One-two!'",
      },
      {
        time: 1524,
        text: 'One minute in. The ball should be fizzing between you. This is what top-level soccer looks like. Constant movement, constant thinking.',
      },
      {
        time: 1534,
        text: "Your movement off the ball is just as important as your pass. After you pass, don't be a statue! Move to a new space to help your partner.",
      },
      {
        time: 1544,
        text: "Let's see a good sequence. Pass, move, get it back, escape touch, find your partner. Beautiful.",
      },
      {
        time: 1553,
        text: 'Keep that quality high. Every pass should be on the ground and easy for your partner to control.',
      },
      {
        time: 1561,
        text: "Let's see another one. Don't let the ball stop moving. Keep that rhythm.",
      },
      {
        time: 1568,
        text: "You two are starting to read each other's movements. That's amazing teamwork.",
      },
      {
        time: 1575,
        text: "Two minutes in! Can you do a sequence of five passes without the ball stopping? Let's try it.",
      },
      {
        time: 1583,
        text: 'Count them out! One... two... three... four... five! Awesome!',
      },
      {
        time: 1591,
        text: "Let's try that again. A five-pass combo. This is how you break down a defense.",
      },
      {
        time: 1599,
        text: 'Keep your head up. You have to see where your partner is moving to make the right pass.',
      },
      {
        time: 1607,
        text: 'Excellent work. Keep that intensity high.',
      },
      {
        time: 1612,
        text: "Halfway! Let's add a new rule. Your escape touch must be with your weak foot.",
      },
      {
        time: 1619,
        text: 'This is a big challenge! Get the pass, and take that first touch into space with your other foot.',
      },
      {
        time: 1627,
        text: "It's tricky, but this is how you become a player who can go in any direction.",
      },
      {
        time: 1634,
        text: "Let's see it. A good pass, and a clean weak-foot escape touch. You can do it.",
      },
      {
        time: 1642,
        text: "Don't worry if it's not perfect. Just trying it is making you better.",
      },
      {
        time: 1649,
        text: "Let's go back to using any foot you want. Feel the difference? That's practice paying off.",
      },
      {
        time: 1657,
        text: "Let's see another five-pass combo. Go for it.",
      },
      {
        time: 1663,
        text: "You're both thinking and playing so fast. This is incredible to watch.",
      },
      {
        time: 1670,
        text: "Keep that focus. We're in the final stretch.",
      },
      {
        time: 1675,
        text: "Let's see one more perfect sequence. Pass, move, receive, escape, pass. Beautiful.",
      },
      {
        time: 1683,
        text: 'Prepare for the final minute.',
      },
      {
        time: 1687,
        text: 'Last minute! This is where champions are made. Push through the fatigue. Keep the quality high.',
      },
      {
        time: 1696,
        text: "Let's see your best combination yet! Quick, clean, and clever!",
      },
      {
        time: 1703,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1709,
        text: 'Faster movement, sharper passes! Come on!',
      },
      {
        time: 1715,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1721,
        text: 'One more perfect combo from you two!',
      },
      {
        time: 1726,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1729,
        text: "Five, four, three, two, one... AND THAT'S A WRAP! INCREDIBLE SESSION!",
      },
      {
        time: 1737,
        text: 'Fantastic work today, both of you! That was a high-level session. Give your partner a huge high-five.',
      },
      {
        time: 1745,
        text: "Let's cool down. Jog slowly, shake out your arms and legs. Take some deep breaths.",
      },
      {
        time: 1753,
        text: "You pushed your skills, your brains, and your fitness today. That's how you improve. Amazing job. Drill complete!",
      },
    ],
  },
  {
    id: 2175,
    name: 'Advanced Partner Ball Control',
    categories: [
      'Ball Control',
      'Partner Drill',
      'First Touch',
      'Trapping',
      'Movement',
      'Tactical',
    ],
    sports: ['soccer'],
    difficulty: 'Advanced',
    skill:
      'Mastering first touch under pressure, deceptive control, and reactive decision-making.',
    instructions:
      'This 30-minute advanced session pushes players to treat their first touch as an offensive weapon. You and your partner will master controlling unpredictable balls, using deception, and making split-second decisions to dominate in any situation.',
    equipment: '1 soccer ball',
    timeTotal: 1885,
    timedInstructions: [
      {
        time: 0,
        text: "Alright superstars, ready for a real challenge? Let's perfect our touch with a drill I call 'The Escape Touch'! Stand about ten big steps apart. Let's get sharp!",
      },
      {
        time: 10,
        text: 'Passer, I want you to play a fast, driven ball to your partner. Receiver, your first touch MUST take you away from an imaginary defender who is right on your back. No stopping the ball!',
      },
      {
        time: 22,
        text: 'Your touch has to be explosive, taking the ball into open space. Think about a sharp angle. Use the outside or inside of your foot to push it past the defender.',
      },
      {
        time: 33,
        text: "After that escape touch, accelerate for one big step, then play a firm pass right back. The rhythm is: Touch, explode, pass! Let's go!",
      },
      {
        time: 43,
        text: 'The most important part: scan before you receive! You have to know where the space is. A quick peek over your shoulder is your secret weapon.',
      },
      {
        time: 53,
        text: 'Passer, your passes must be a challenge. Keep them fast and on the ground. After ten passes, you two will switch roles. Keep count!',
      },
      {
        time: 64,
        text: "Let's see it again. A quick scan, an explosive touch into space, and a sharp pass back. Perfect!",
      },
      {
        time: 72,
        text: "Receiver, try to use the outside of your foot for the escape touch. It's a great way to get away quickly.",
      },
      {
        time: 80,
        text: "Now try the inside of your foot. Which one feels better? It's great to have both options.",
      },
      {
        time: 88,
        text: 'Keep that quality high. Every touch is a chance to get better.',
      },
      {
        time: 94,
        text: "Let's switch roles if you haven't already. New passer, new receiver. Let's see that same energy.",
      },
      {
        time: 102,
        text: 'New receiver, remember to scan! Look over that shoulder before the ball arrives.',
      },
      {
        time: 109,
        text: 'Excellent! The goal is to create immediate separation from that defender.',
      },
      {
        time: 115,
        text: "Keep that focus. You're doing great.",
      },
      {
        time: 120,
        text: 'Two minutes in! Your escape touch needs to be sharper and faster now.',
      },
      {
        time: 127,
        text: "Don't just push the ball, accelerate with it! Your first step after the touch should be powerful, like a rocket.",
      },
      {
        time: 136,
        text: "Let's see that explosion. Receive, escape, and GO!",
      },
      {
        time: 142,
        text: "Awesome! That's how you create space for yourself to make the next play.",
      },
      {
        time: 149,
        text: 'Keep switching roles every ten passes. Make sure you both get to work.',
      },
      {
        time: 155,
        text: 'Halfway! Now, the passer is going to follow their pass to apply some real pressure. Just a light jog towards the receiver.',
      },
      {
        time: 165,
        text: 'Receiver, you have to be even quicker now! That first touch has to be perfect to beat your partner.',
      },
      {
        time: 173,
        text: 'This makes it just like a real game. You have to be calm and cool under pressure.',
      },
      {
        time: 180,
        text: "Let's see it. A good pass, a little pressure, and a clean escape touch. You can do it.",
      },
      {
        time: 188,
        text: "Remember to scan before the ball arrives. It's even more important now that there's pressure.",
      },
      {
        time: 196,
        text: "See the space, feel the pressure, and escape. That's what soccer is all about!",
      },
      {
        time: 203,
        text: "Keep that quality high. Don't panic. Just a calm, sharp touch into space.",
      },
      {
        time: 210,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 215,
        text: "Excellent work. You're handling the pressure like a pro.",
      },
      {
        time: 221,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 226,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 232,
        text: 'Good effort! Keep working on it.',
      },
      {
        time: 237,
        text: 'Now back to your strong foot. Make it perfect.',
      },
      {
        time: 242,
        text: 'Prepare for the final minute.',
      },
      {
        time: 245,
        text: 'Last minute! Game intensity. Can your first touch beat a real, closing-down player? Prove it.',
      },
      {
        time: 253,
        text: 'Imagine this is the final minute of a big game. Every touch is important!',
      },
      {
        time: 260,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 266,
        text: 'Faster passes, quicker escapes! Come on!',
      },
      {
        time: 271,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 277,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 282,
        text: 'Get ready for the countdown!',
      },
      {
        time: 285,
        text: 'Five, four, three, two, one... and hold. Elite first touch.',
      },
      {
        time: 292,
        text: "That was a fantastic first exercise! Now for a super fun test of your technique called 'The One-Touch Gauntlet'!",
      },
      {
        time: 300,
        text: "One of you is the server, one is the worker. Server, you'll serve from your hands. Worker, you have one touch to control the ball and play it to a target.",
      },
      {
        time: 310,
        text: 'Imagine a target two steps to the left of the server, and another two steps to the right. Your one-touch pass must go to one of those targets, not back to the server.',
      },
      {
        time: 322,
        text: 'Server, be unpredictable! A bouncing throw, a hard roller, a spinning ball, a high lob. The worker must adapt instantly. 60 seconds of work each. Go!',
      },
      {
        time: 335,
        text: 'Worker, this is all about problem-solving. Read the serve, choose your surface—foot, thigh, chest, head—and execute a clean, directional first touch.',
      },
      {
        time: 347,
        text: "Let's see it. A bouncing throw... good control to the target!",
      },
      {
        time: 353,
        text: 'Now a high lob! Get under it... thigh control to the target! Awesome.',
      },
      {
        time: 360,
        text: 'Keep working, worker! Server, keep those passes tricky.',
      },
      {
        time: 365,
        text: 'Thirty seconds down! Keep that quality high. Every touch matters.',
      },
      {
        time: 371,
        text: "Let's see a roller now. Quick feet to adjust.",
      },
      {
        time: 376,
        text: "Excellent. You're reading the serves perfectly.",
      },
      {
        time: 380,
        text: 'Final 15 seconds for this worker. Push through it!',
      },
      {
        time: 385,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 390,
        text: "Great job! New worker, you're up! Server, get ready to challenge them. 60 seconds on the clock. Go!",
      },
      {
        time: 399,
        text: 'New server, make your partner work! Mix up those serves.',
      },
      {
        time: 404,
        text: 'New worker, show me that sharp control. One touch to the target!',
      },
      {
        time: 410,
        text: 'Good chest control! And a perfect pass to the side.',
      },
      {
        time: 415,
        text: 'Now a tricky bounce pass. Excellent trap.',
      },
      {
        time: 420,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 424,
        text: 'Thirty seconds down for our new worker. Keep that focus!',
      },
      {
        time: 429,
        text: "Remember to move your feet to get in the best position. Don't be lazy.",
      },
      {
        time: 435,
        text: "Final fifteen seconds for this worker. Don't stop now!",
      },
      {
        time: 440,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 445,
        text: "Awesome work! Back to our first worker. Halfway through the drill! Let's up the challenge.",
      },
      {
        time: 453,
        text: "Now, the server calls 'left' or 'right' just as they serve. The worker must react and play their one-touch pass to the correct target. 60 seconds of work. Go!",
      },
      {
        time: 467,
        text: "Server, serve and shout! 'LEFT!'",
      },
      {
        time: 470,
        text: "Good reaction! Now another one. 'RIGHT!'",
      },
      {
        time: 474,
        text: 'Excellent. This is a massive test of your brain and your feet.',
      },
      {
        time: 480,
        text: 'Keep that quality high. Read, decide, execute.',
      },
      {
        time: 485,
        text: "Thirty seconds down. Keep that focus. Let's see another one. 'LEFT!'",
      },
      {
        time: 491,
        text: "Good. Now 'RIGHT!'. Be ready for anything.",
      },
      {
        time: 495,
        text: 'Final 15 seconds for this worker. Push it!',
      },
      {
        time: 499,
        text: 'Five, four, three, two, one... SWITCH ROLES!',
      },
      {
        time: 504,
        text: "New worker, your turn! Server, keep those calls coming. 60 seconds. 'RIGHT!'",
      },
      {
        time: 511,
        text: "Good. 'LEFT!'",
      },
      {
        time: 513,
        text: "Awesome. Keep that brain working. 'LEFT!'",
      },
      {
        time: 517,
        text: "Don't just react, anticipate! Be ready for the call.",
      },
      {
        time: 522,
        text: "Thirty seconds down. Keep that quality high. 'RIGHT!'",
      },
      {
        time: 527,
        text: "Good. Now 'LEFT!'. Excellent.",
      },
      {
        time: 530,
        text: 'Final 15 seconds for this worker. Finish strong!',
      },
      {
        time: 535,
        text: 'Five, four, three, two, one... TIME!',
      },
      {
        time: 539,
        text: "Last minute of this drill. Let's go rapid-fire. Switch every 15 seconds!",
      },
      {
        time: 546,
        text: "Worker 1, go! 'LEFT!', 'RIGHT!', 'RIGHT!'",
      },
      {
        time: 551,
        text: "Switch! Worker 2, go! 'RIGHT!', 'LEFT!', 'LEFT!'",
      },
      {
        time: 557,
        text: "Switch! Worker 1, go! 'LEFT!', 'LEFT!', 'RIGHT!'",
      },
      {
        time: 563,
        text: "Switch! Worker 2, final push! 'RIGHT!', 'RIGHT!', 'LEFT!'",
      },
      {
        time: 569,
        text: 'Come on, keep that quality! Final ten seconds!',
      },
      {
        time: 574,
        text: 'Five, four, three, two, one... and TIME! Incredible adaptability.',
      },
      {
        time: 581,
        text: "That was some amazing reaction work! Now, let's get deceptive with a super tricky move called 'The Dummy & Take'!",
      },
      {
        time: 590,
        text: "Passer, play a firm ball to your partner. Receiver, you are going to let the ball run right across your body, faking like you're going to touch it. This is a 'dummy'!",
      },
      {
        time: 600,
        text: "You have to sell the fake! Open your body like you're going to receive it, then at the last second, pull your foot away and let it roll past you.",
      },
      {
        time: 610,
        text: 'As it rolls past, you have to pivot your body quickly and accelerate after it. This move completely freezes defenders.',
      },
      {
        time: 619,
        text: "So, it's: check to the ball, fake the touch, let it run, pivot, and explode into the space behind you. Then pass back. Let's try it!",
      },
      {
        time: 630,
        text: 'Your body language is everything. You have to convince the imaginary defender you are going to touch the ball.',
      },
      {
        time: 638,
        text: "Let's see five good dummies each. Switch roles after each one. Go!",
      },
      {
        time: 645,
        text: 'One minute in! Your fakes are looking great. Remember to accelerate after the pivot.',
      },
      {
        time: 652,
        text: 'The explosion after the dummy is what makes the move so dangerous.',
      },
      {
        time: 658,
        text: "Let's see it again. A good pass, a big fake, and a quick acceleration.",
      },
      {
        time: 665,
        text: "You're looking like a true magician out there. Keep it up.",
      },
      {
        time: 671,
        text: "Let's try letting it run across your body the other way now. Use your other foot to fake.",
      },
      {
        time: 679,
        text: "This makes you unpredictable. The defender will never know which way you're going.",
      },
      {
        time: 686,
        text: 'Keep that quality high. Every fake should be your best one.',
      },
      {
        time: 692,
        text: "Two minutes in! Let's add another layer of deception.",
      },
      {
        time: 698,
        text: "This time, sometimes dummy it, and sometimes take a sharp first touch into space like our first drill. Keep your partner guessing what you'll do.",
      },
      {
        time: 709,
        text: 'So, you might do a dummy, or you might do an escape touch. Be creative!',
      },
      {
        time: 716,
        text: 'This is how you become a smart player, by making different decisions.',
      },
      {
        time: 723,
        text: "Let's see a dummy now. Good. On the next one, an escape touch!",
      },
      {
        time: 730,
        text: "Excellent! You're keeping that imaginary defender on their toes.",
      },
      {
        time: 736,
        text: 'Keep switching roles. Both of you need to practice making these decisions.',
      },
      {
        time: 743,
        text: "Halfway! The timing of the pivot and acceleration is crucial. Don't be late to the ball after the dummy.",
      },
      {
        time: 752,
        text: 'As soon as that ball passes your foot, you should be turning and sprinting.',
      },
      {
        time: 759,
        text: "Let's focus on that quick turn. Let's see three sharp pivots in a row.",
      },
      {
        time: 766,
        text: "That's it! The faster you turn, the more space you create.",
      },
      {
        time: 772,
        text: "Let's go back to mixing it up. A dummy or an escape touch. Your choice.",
      },
      {
        time: 779,
        text: 'Keep that quality high. Every touch, every fake, every run matters.',
      },
      {
        time: 786,
        text: "You're both doing an amazing job. This is a very advanced skill.",
      },
      {
        time: 792,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 797,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 801,
        text: "Last minute! Let's see some real flair. A sharp head fake, a dip of the shoulder. Be an actor!",
      },
      {
        time: 809,
        text: "Really sell that fake. Make me believe you're going to touch it.",
      },
      {
        time: 815,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 821,
        text: 'Faster passes, quicker fakes, more explosive sprints! Come on!',
      },
      {
        time: 827,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 833,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 838,
        text: 'Get ready for the countdown!',
      },
      {
        time: 841,
        text: 'Five, four, three, two, one... and TIME! Masterful deception.',
      },
      {
        time: 848,
        text: "That was some amazing trickery! Next up, let's get back in the air for some advanced work with 'Directional Aerial Control'!",
      },
      {
        time: 857,
        text: "Server, you're going to toss a high, loopy ball. Receiver, your job is to control it out of the air with one touch and move it into a new space.",
      },
      {
        time: 867,
        text: "Here's the trick: Server, as soon as you toss the ball, you're going to run to the left or the right. Receiver, your first touch has to guide the ball perfectly into your partner's path.",
      },
      {
        time: 880,
        text: "This isn't just stopping the ball; it's controlling it with a purpose! Let's try it. Server, toss and run. Receiver, use your chest to angle the ball to them. Go!",
      },
      {
        time: 892,
        text: "Excellent! Now let's try it with your thigh. Angle your thigh to push the ball in the direction your partner is running.",
      },
      {
        time: 901,
        text: "Now, let's try a clever header. Not a powerful one, but a soft, guiding flick with your forehead to redirect the ball.",
      },
      {
        time: 910,
        text: "This requires amazing awareness and a perfect touch. Let's do five good reps each, then switch roles.",
      },
      {
        time: 918,
        text: 'One minute in! Your control is looking great. Server, make your run clear and early.',
      },
      {
        time: 925,
        text: 'Receiver, you have to read that run and deliver a perfect, soft touch right into their path.',
      },
      {
        time: 933,
        text: "Let's see another one. A good toss, a good run, and a perfect directional touch. Awesome!",
      },
      {
        time: 941,
        text: 'Keep switching roles so you both get to practice serving and receiving.',
      },
      {
        time: 948,
        text: "This is how you create quick counter-attacks in a game. You're doing great.",
      },
      {
        time: 955,
        text: "Let's see that chest control again. Angle your body to guide the ball.",
      },
      {
        time: 961,
        text: 'Now the thigh control. A soft cushion into space.',
      },
      {
        time: 967,
        text: 'And the flick header. Be clever with it.',
      },
      {
        time: 972,
        text: "Two minutes in! Let's make it a little harder. Server, you can now fake one way and run the other!",
      },
      {
        time: 980,
        text: 'Receiver, you have to read the fake and still deliver the ball to the right space. This is a big challenge!',
      },
      {
        time: 989,
        text: "Let's see it. A good toss, a tricky run... and a perfect touch to find your partner. Excellent!",
      },
      {
        time: 997,
        text: 'This is training your soccer brain to think and react at game speed.',
      },
      {
        time: 1003,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 1008,
        text: "Let's switch roles again. New server, new receiver. Let's see that deception.",
      },
      {
        time: 1015,
        text: "Halfway! Let's try to control it with some unusual surfaces. Can you pop it up with your shoulder into your partner's path?",
      },
      {
        time: 1024,
        text: "It's a tricky, creative move, just like the pros do. Give it a try!",
      },
      {
        time: 1030,
        text: 'Now, can you flick it into space with the outside of your boot as it drops? Be a magician!',
      },
      {
        time: 1038,
        text: "Let's go back to the chest control. Make it perfect.",
      },
      {
        time: 1043,
        text: 'And the thigh control. Make it just as good.',
      },
      {
        time: 1048,
        text: "You're building a complete toolbox of aerial skills. Keep it up.",
      },
      {
        time: 1054,
        text: "Let's see another shoulder pass. Can you get it right to your partner?",
      },
      {
        time: 1060,
        text: "Awesome! You're a creative player.",
      },
      {
        time: 1065,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1070,
        text: "Let's see one more perfect rep. A good toss, a good run, and a perfect touch.",
      },
      {
        time: 1077,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 1081,
        text: "Last minute! Let's see a seamless connection. The server runs, the receiver redirects the ball perfectly into their stride. Make it look easy.",
      },
      {
        time: 1091,
        text: 'This is game speed now. Quick tosses, quick runs, quick control.',
      },
      {
        time: 1097,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1103,
        text: 'Faster, sharper, more creative! Come on!',
      },
      {
        time: 1108,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1114,
        text: 'One more perfect aerial redirection from each of you!',
      },
      {
        time: 1119,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1122,
        text: 'Five, four, three, two, one... and TIME! World-class touch.',
      },
      {
        time: 1129,
        text: "That was some incredible aerial skill! Now for a super fun, super fast brain game called 'Controlled Chaos'!",
      },
      {
        time: 1137,
        text: 'This is a continuous drill in a tight space. You two will pass and move, but with a special rule.',
      },
      {
        time: 1143,
        text: "The player WITHOUT the ball is the 'director'. You must constantly be pointing to an open space on the field.",
      },
      {
        time: 1151,
        text: "The player WITH the ball is the 'worker'. You must take your first touch into the exact space your partner is pointing to. Then, you pass it back and become the director.",
      },
      {
        time: 1163,
        text: "So you're constantly reading your partner's direction, taking a touch, passing, and then immediately pointing for them. This forces you to keep your head up! Let's go!",
      },
      {
        time: 1175,
        text: 'Director, point to a space! Worker, receive the pass and take your first touch there. Good! Now pass it back.',
      },
      {
        time: 1183,
        text: "Now you're the director! Point! Good. Worker, read it and go! Excellent.",
      },
      {
        time: 1190,
        text: 'The pace should be high. The pointing should be clear and decisive. The touch must be accurate.',
      },
      {
        time: 1198,
        text: 'This is a mental workout as much as a physical one. You have to think and move at the same time.',
      },
      {
        time: 1206,
        text: 'One minute in! Your reactions are looking sharp. Keep that communication going with your pointing.',
      },
      {
        time: 1213,
        text: "Let's see a quick one. Point, pass, touch, pass, point! Keep the flow.",
      },
      {
        time: 1220,
        text: 'Remember, the touch has to be on your first touch. No extra touches.',
      },
      {
        time: 1226,
        text: 'This is how you learn to play quick, combination soccer.',
      },
      {
        time: 1232,
        text: "Let's see another good one. Director, make your partner move! Point to a tricky space.",
      },
      {
        time: 1240,
        text: 'Good work, worker! Great touch to get there.',
      },
      {
        time: 1245,
        text: "Two minutes in! Let's make it even harder. The pass back to the director must be one-touch.",
      },
      {
        time: 1253,
        text: "So it's: receive the pass, one touch into the pointed space, then a one-touch pass back. Two touches total!",
      },
      {
        time: 1262,
        text: "This is a huge challenge. Let's see it. Point, pass, touch, one-touch pass back. Go!",
      },
      {
        time: 1270,
        text: "Excellent! That was so fast. You're playing like pros.",
      },
      {
        time: 1276,
        text: 'Keep that quality high. The touches need to be perfect.',
      },
      {
        time: 1282,
        text: "Halfway! The communication is silent but constant. You're reading each other's minds!",
      },
      {
        time: 1290,
        text: "Let's see a good sequence. Keep it going for five passes in a row.",
      },
      {
        time: 1296,
        text: 'One... good. Two... sharp. Three... keep it up.',
      },
      {
        time: 1302,
        text: 'Four... almost there. Five! Awesome teamwork.',
      },
      {
        time: 1308,
        text: "Let's try that again. Another five-pass streak.",
      },
      {
        time: 1314,
        text: 'This is how you build a real connection with your teammate.',
      },
      {
        time: 1320,
        text: "Keep that focus. Don't let the quality drop.",
      },
      {
        time: 1325,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 1330,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1334,
        text: 'Last minute! Can you go one-touch only? As the ball travels, see the direction your partner is pointing and play a one-touch pass into that space. This is the highest level!',
      },
      {
        time: 1347,
        text: 'This requires amazing vision and a perfect touch. Give it a try!',
      },
      {
        time: 1353,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1359,
        text: 'Faster pointing, quicker passes! Come on!',
      },
      {
        time: 1364,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1370,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1375,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1378,
        text: 'Five, four, three, two, one... and TIME! Brilliant awareness.',
      },
      {
        time: 1385,
        text: "That was some incredible aerial skill! Now for a super fun, super fast brain game called 'Controlled Chaos'!",
      },
      {
        time: 1393,
        text: 'This is a continuous drill in a tight space. You two will pass and move, but with a special rule.',
      },
      {
        time: 1399,
        text: "The player WITHOUT the ball is the 'director'. You must constantly be pointing to an open space on the field.",
      },
      {
        time: 1407,
        text: "The player WITH the ball is the 'worker'. You must take your first touch into the exact space your partner is pointing to. Then, you pass it back and become the director.",
      },
      {
        time: 1419,
        text: "So you're constantly reading your partner's direction, taking a touch, passing, and then immediately pointing for them. This forces you to keep your head up! Let's go!",
      },
      {
        time: 1431,
        text: 'Director, point to a space! Worker, receive the pass and take your first touch there. Good! Now pass it back.',
      },
      {
        time: 1439,
        text: "Now you're the director! Point! Good. Worker, read it and go! Excellent.",
      },
      {
        time: 1446,
        text: 'The pace should be high. The pointing should be clear and decisive. The touch must be accurate.',
      },
      {
        time: 1454,
        text: 'This is a mental workout as much as a physical one. You have to think and move at the same time.',
      },
      {
        time: 1462,
        text: 'One minute in! Your reactions are looking sharp. Keep that communication going with your pointing.',
      },
      {
        time: 1469,
        text: "Let's see a quick one. Point, pass, touch, pass, point! Keep the flow.",
      },
      {
        time: 1476,
        text: 'Remember, the touch has to be on your first touch. No extra touches.',
      },
      {
        time: 1482,
        text: 'This is how you learn to play quick, combination soccer.',
      },
      {
        time: 1488,
        text: "Let's see another good one. Director, make your partner move! Point to a tricky space.",
      },
      {
        time: 1496,
        text: 'Good work, worker! Great touch to get there.',
      },
      {
        time: 1501,
        text: "Two minutes in! Let's make it even harder. The pass back to the director must be one-touch.",
      },
      {
        time: 1509,
        text: "So it's: receive the pass, one touch into the pointed space, then a one-touch pass back. Two touches total!",
      },
      {
        time: 1518,
        text: "This is a huge challenge. Let's see it. Point, pass, touch, one-touch pass back. Go!",
      },
      {
        time: 1526,
        text: "Excellent! That was so fast. You're playing like pros.",
      },
      {
        time: 1532,
        text: 'Keep that quality high. The touches need to be perfect.',
      },
      {
        time: 1538,
        text: "Halfway! The communication is silent but constant. You're reading each other's minds!",
      },
      {
        time: 1546,
        text: "Let's see a good sequence. Keep it going for five passes in a row.",
      },
      {
        time: 1552,
        text: 'One... good. Two... sharp. Three... keep it up.',
      },
      {
        time: 1558,
        text: 'Four... almost there. Five! Awesome teamwork.',
      },
      {
        time: 1564,
        text: "Let's try that again. Another five-pass streak.",
      },
      {
        time: 1570,
        text: 'This is how you build a real connection with your teammate.',
      },
      {
        time: 1576,
        text: "Keep that focus. Don't let the quality drop.",
      },
      {
        time: 1581,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 1586,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1590,
        text: 'Last minute! Can you go one-touch only? As the ball travels, see the direction your partner is pointing and play a one-touch pass into that space. This is the highest level!',
      },
      {
        time: 1603,
        text: 'This requires amazing vision and a perfect touch. Give it a try!',
      },
      {
        time: 1609,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1615,
        text: 'Faster pointing, quicker passes! Come on!',
      },
      {
        time: 1620,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1626,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1631,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1634,
        text: 'Five, four, three, two, one... and TIME! Brilliant awareness.',
      },
      {
        time: 1641,
        text: "That was some amazing teamwork! Now for our final exercise: 'The Master's Test'!",
      },
      {
        time: 1649,
        text: 'This is total freedom. For the next five minutes, you will pass and move in a small space. The only rule is you must use a different type of control on every touch.',
      },
      {
        time: 1660,
        text: 'One touch might be a simple stop. The next must be an escape touch. The next, a dummy. The next, a directional aerial touch. The next, a shielding touch.',
      },
      {
        time: 1672,
        text: "You must cycle through all the skills we've worked on. Don't be repetitive. Show your entire toolbox.",
      },
      {
        time: 1680,
        text: "This is where you show off your mastery. Can you perform these superstar skills when you're tired and link them creatively? Go!",
      },
      {
        time: 1690,
        text: "Let's start with an escape touch. Good. Now a simple stop.",
      },
      {
        time: 1696,
        text: 'Now a dummy! Let it run. Excellent.',
      },
      {
        time: 1701,
        text: 'Now a shielding touch. Protect it!',
      },
      {
        time: 1706,
        text: "You're thinking like a master. Keep cycling through the skills.",
      },
      {
        time: 1712,
        text: 'One minute in. The transitions must be seamless. No dead touches between moves.',
      },
      {
        time: 1719,
        text: "Let's see a directional aerial touch now. Toss it up and guide it.",
      },
      {
        time: 1725,
        text: 'Good. Now back to the ground. An escape touch.',
      },
      {
        time: 1730,
        text: 'Keep that flow beautiful. You two are perfectly in sync.',
      },
      {
        time: 1736,
        text: "Let's see another dummy. Sell the fake!",
      },
      {
        time: 1741,
        text: "Awesome. You're playing with real intelligence.",
      },
      {
        time: 1746,
        text: 'Two minutes in. Perform at the highest speed you can maintain while keeping control. This is where superstars are made.',
      },
      {
        time: 1755,
        text: 'Challenge each other with your passes to force different types of control.',
      },
      {
        time: 1762,
        text: 'A hard pass needs a soft touch. A soft pass needs a quick touch.',
      },
      {
        time: 1768,
        text: "Let's see a shielding touch again. Be strong.",
      },
      {
        time: 1773,
        text: 'Now an escape touch. Be quick.',
      },
      {
        time: 1778,
        text: "Halfway! Don't just repeat the same combo. Challenge yourself to create new sequences on the fly.",
      },
      {
        time: 1786,
        text: 'Every repetition is programming your brain and your feet to work together.',
      },
      {
        time: 1793,
        text: "Let's see another directional aerial touch. A toss and a guide.",
      },
      {
        time: 1799,
        text: 'Good. Now a dummy. Let it run.',
      },
      {
        time: 1804,
        text: "You're making it look easy. Keep that focus.",
      },
      {
        time: 1809,
        text: "Let's see another escape touch. Explode into space.",
      },
      {
        time: 1814,
        text: 'Prepare for the final minute. This is the last push.',
      },
      {
        time: 1819,
        text: 'Last minute! Empty the tank! Show me every skill you have with precision and flair.',
      },
      {
        time: 1826,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 1831,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1837,
        text: "Thirty seconds! Let's see a final, brilliant sequence!",
      },
      {
        time: 1842,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 1847,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 1852,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 1857,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... AND RELAX! DRILL COMPLETE!',
      },
      {
        time: 1867,
        text: 'That was the definition of mastery. Truly exceptional work from both of you. High-five your partner.',
      },
      {
        time: 1875,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take some deep breaths.",
      },
      {
        time: 1882,
        text: "You've trained your bodies and your brains at an elite level today. Be proud of that. Well done!",
      },
    ],
  },
  {
    id: 2176,
    name: 'Beginner Partner Wall Skills',
    categories: ['Passing', 'Ball Control', 'Wall Drill', 'First Touch'],
    sports: ['soccer'],
    difficulty: 'Beginner',
    skill:
      'Using a wall for basic passing, receiving, and first-touch practice.',
    instructions:
      'This 30-minute session turns a simple wall into your best training partner! You two will work together to master the basics of passing and receiving, using the wall to get hundreds of touches and improve your skills fast.',
    equipment: '1 soccer ball, 1 wall',
    timeTotal: 1594,
    timedInstructions: [
      {
        time: 0,
        text: "Hey superstars! Ready to train with our new teammate, the wall? First up: 'Simple Wall Pass'. Player 1, you'll start at the wall. Player 2, stand a few big steps behind them. Let's go!",
      },
      {
        time: 11,
        text: 'Player 1, pass the ball firmly against the wall with your right foot. As it comes back, control it with one soft touch.',
      },
      {
        time: 20,
        text: "Great control! Now, turn and pass the ball to Player 2. Player 2, you're up! Dribble into Player 1's spot.",
      },
      {
        time: 29,
        text: "Now it's your turn, Player 2! Pass to the wall with your right foot, control the rebound with a soft touch.",
      },
      {
        time: 38,
        text: 'Perfect! Now turn and pass to Player 1. You two are a team! Keep that flow going. The next player always goes to the wall.',
      },
      {
        time: 48,
        text: 'Remember to lock your ankle when you pass. It makes your pass straight and strong.',
      },
      {
        time: 54,
        text: 'And that first touch from the wall should be super soft. Imagine your foot is a pillow.',
      },
      {
        time: 61,
        text: "Let's try the left foot now! Player at the wall, pass and control with your LEFT foot only.",
      },
      {
        time: 69,
        text: "Good! Now pass to your partner. Next player, you're using your left foot too! It's a fun challenge.",
      },
      {
        time: 78,
        text: 'It might feel a little funny, but this is how you become a player who can use both feet!',
      },
      {
        time: 85,
        text: "One minute in! You're both doing great. Keep your body over the ball and stay on your toes. Be bouncy!",
      },
      {
        time: 93,
        text: "Let's go back to using your right foot. A firm pass to the wall, a soft touch to control.",
      },
      {
        time: 100,
        text: 'Then a sharp pass to your partner. Excellent teamwork.',
      },
      {
        time: 106,
        text: 'Keep that focus. A good pass to the wall makes the rebound easy to control.',
      },
      {
        time: 113,
        text: "And a good pass to your partner keeps the drill moving fast. You're in this together!",
      },
      {
        time: 120,
        text: "Two minutes in! Let's focus on that first touch. Make the ball stick to your foot like Messi's glue foot!",
      },
      {
        time: 128,
        text: "Let's see five perfect stops in a row from each of you. Don't let that ball roll away.",
      },
      {
        time: 135,
        text: "You're doing great. The better your first touch, the more time you have to find your partner.",
      },
      {
        time: 143,
        text: "Let's go back to your weak foot for a few turns. Pass and control with your other foot.",
      },
      {
        time: 150,
        text: "Halfway! You're both looking like passing masters. Now for a new challenge: accuracy!",
      },
      {
        time: 158,
        text: "Player at the wall, imagine there's a small target, like a secret button. Your job is to hit that button with your pass.",
      },
      {
        time: 167,
        text: "After you hit it and control the ball, pass to your partner. Now it's their turn to be a sniper!",
      },
      {
        time: 175,
        text: 'Remember to point your standing foot at the target. It helps you aim.',
      },
      {
        time: 181,
        text: 'Bullseye! Keep that focus. Head up to see the target, then down to pass.',
      },
      {
        time: 188,
        text: "Let's try to hit the target with your left foot now. This is the ultimate test of accuracy.",
      },
      {
        time: 195,
        text: "Don't worry about power, just think about hitting that secret button.",
      },
      {
        time: 201,
        text: "You're both doing amazing. This is a very important skill.",
      },
      {
        time: 206,
        text: "Let's do one more round of target practice. Right foot, try to hit it.",
      },
      {
        time: 212,
        text: 'Good. Now the next player. Right foot, bullseye!',
      },
      {
        time: 217,
        text: 'Now left foot. A perfect shot.',
      },
      {
        time: 221,
        text: 'And the next player, left foot. Finish it off!',
      },
      {
        time: 225,
        text: "Awesome accuracy! Let's go back to our normal passing. No target.",
      },
      {
        time: 231,
        text: 'Keep that flow going. Pass, control, find your partner, pass.',
      },
      {
        time: 237,
        text: 'Prepare for the final minute of this drill.',
      },
      {
        time: 240,
        text: "Last minute! Let's see how fast you can go. Quicker passes, quicker turns!",
      },
      {
        time: 247,
        text: 'The ball should be moving constantly. No lazy moments!',
      },
      {
        time: 253,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 259,
        text: 'Faster, faster! But keep it clean. A good pass and a good stop.',
      },
      {
        time: 265,
        text: "Keep that energy high! You're almost there.",
      },
      {
        time: 270,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 275,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 280,
        text: 'Get ready for the countdown!',
      },
      {
        time: 283,
        text: 'Five, four, three, two, one... and time! Great start.',
      },
      {
        time: 290,
        text: "That was a fantastic first exercise! No break, let's keep that energy going! Next up, we're picking up the pace with the 'One-Touch Wall Pass'!",
      },
      {
        time: 299,
        text: "This is a super fun team challenge! Player 1, you'll start a few steps from the wall. Player 2, you'll stand a few steps behind Player 1.",
      },
      {
        time: 308,
        text: 'Player 1, pass the ball to the wall. As it comes back, run past the ball and let it go to Player 2.',
      },
      {
        time: 316,
        text: 'Player 2, as the ball comes to you, you have to hit it back to the wall with one touch! Then you run past it and let it go to Player 1.',
      },
      {
        time: 326,
        text: "Now it's Player 1's turn to hit it with one touch! You're a team, keeping a rally going. Let's go!",
      },
      {
        time: 334,
        text: "So it's: Player 1 passes, Player 2 one-touch, Player 1 one-touch, and so on! Don't let the ball stop.",
      },
      {
        time: 343,
        text: "Use the inside of your foot and lock your ankle. It's like playing ping-pong with the wall!",
      },
      {
        time: 350,
        text: "Let's see if you two can get a rally of ten passes in a row. Count them out loud!",
      },
      {
        time: 357,
        text: 'One... two... three... keep it going!',
      },
      {
        time: 362,
        text: 'Four... five... six... great teamwork!',
      },
      {
        time: 367,
        text: 'Seven... eight... nine... almost there!',
      },
      {
        time: 372,
        text: "Ten! Awesome job! Let's try for a new record.",
      },
      {
        time: 378,
        text: 'Remember to stay on your toes. You have to be ready for the ball to come to you.',
      },
      {
        time: 384,
        text: "This is how you learn to play fast and think fast. You're doing great.",
      },
      {
        time: 390,
        text: "Two minutes in! Let's try to use your other foot now. Your weak foot! This is a big challenge.",
      },
      {
        time: 398,
        text: 'Player 1, pass to the wall. Player 2, one-touch with your weak foot!',
      },
      {
        time: 405,
        text: 'Now Player 1, one-touch with your weak foot! Keep the rally going.',
      },
      {
        time: 412,
        text: "It's okay if it's not perfect. Every touch with that foot makes it stronger.",
      },
      {
        time: 419,
        text: "Let's see if you can get a rally of five with your weak feet. You can do it!",
      },
      {
        time: 426,
        text: 'Excellent effort! Keep working on it.',
      },
      {
        time: 431,
        text: "Halfway! Back to your strong feet. Let's see your best one-touch passing.",
      },
      {
        time: 438,
        text: "Let's try to move a little closer to the wall. This will make the ball come back even faster!",
      },
      {
        time: 446,
        text: 'Quick feet, quick reactions! You have to be ready.',
      },
      {
        time: 452,
        text: 'Good! Now take a step back. A little more time, but the passes need to be firmer.',
      },
      {
        time: 460,
        text: "You're both adapting so well. This is what great players do.",
      },
      {
        time: 466,
        text: "Let's go for a new record. How many can you get in a row? Go!",
      },
      {
        time: 473,
        text: "Come on, keep that rally alive! Don't let the ball stop.",
      },
      {
        time: 479,
        text: "You're a well-oiled machine! Keep that focus.",
      },
      {
        time: 484,
        text: "Let's see another good streak. Push yourselves.",
      },
      {
        time: 490,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 494,
        text: "Last minute! Let's see your fastest rally yet. Go, go, go!",
      },
      {
        time: 500,
        text: 'Thirty seconds left! Empty the tank. Keep that rally going.',
      },
      {
        time: 506,
        text: "Come on, a few more passes. Don't stop now.",
      },
      {
        time: 511,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 517,
        text: 'Push it, push it! Almost there!',
      },
      {
        time: 522,
        text: 'Five, four, three, two, one... and TIME! Awesome reaction speed.',
      },
      {
        time: 529,
        text: "That was some super-fast passing! Now, let's add some movement with a new drill called 'Receive and Turn'!",
      },
      {
        time: 538,
        text: "Here's how it works. Player 1, you'll stand facing the wall. Player 2, you'll stand a few big steps behind Player 1.",
      },
      {
        time: 546,
        text: 'Player 1, pass the ball to the wall. As it comes back, your first touch should be a turn that takes you away from the wall, to the left or the right.',
      },
      {
        time: 556,
        text: 'After you turn, you have to find Player 2, who has moved to a new spot, and pass the ball to them. Player 2, your job is to be a clever target!',
      },
      {
        time: 567,
        text: "Once Player 2 gets the pass, they take Player 1's spot at the wall and do the same thing. Let's go!",
      },
      {
        time: 576,
        text: "So it's: Pass to wall, receive and turn, find your partner, and pass. Then it's the next player's turn.",
      },
      {
        time: 585,
        text: "Player at the wall, remember to peek over your shoulder before the ball comes back. You have to know where you're going to turn.",
      },
      {
        time: 594,
        text: "Player waiting for the pass, keep moving! Don't make it easy for your partner. Find a new open space every time.",
      },
      {
        time: 603,
        text: "One minute in! Your turns are looking good. Let's make the turn sharper. Use the outside of your foot for a quicker escape.",
      },
      {
        time: 612,
        text: "This is how you learn to play with your head up. You're not just kicking; you're thinking!",
      },
      {
        time: 620,
        text: "Let's see another good one. A sharp pass to the wall, a quick turn, and a perfect pass to your moving partner.",
      },
      {
        time: 629,
        text: 'You two are working together perfectly. This is what teamwork looks like.',
      },
      {
        time: 636,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 641,
        text: "Two minutes in! Let's focus on the pass to your partner. It needs to be accurate.",
      },
      {
        time: 648,
        text: 'Your partner is a moving target, so you have to pass it right to their feet. Lead them with the pass.',
      },
      {
        time: 656,
        text: "Let's see five perfect passes to your moving partner in a row. You can do it.",
      },
      {
        time: 663,
        text: "Excellent! You're not just players; you're playmakers.",
      },
      {
        time: 669,
        text: 'Keep that energy high. This is a great drill for your fitness too.',
      },
      {
        time: 675,
        text: "Halfway! Let's see some explosive turns. A soft touch to receive, then accelerate into that space before you pass.",
      },
      {
        time: 684,
        text: 'The first step after your turn should be a powerful one. Create that space!',
      },
      {
        time: 691,
        text: "Let's see that explosion. Turn and GO! Don't hesitate.",
      },
      {
        time: 697,
        text: "Awesome! That's how you leave a defender behind.",
      },
      {
        time: 703,
        text: 'Keep that focus. Every turn should be your best one.',
      },
      {
        time: 709,
        text: "Let's see another one. Make it sharp and fast.",
      },
      {
        time: 715,
        text: "You're both doing an amazing job. This is a very smart drill.",
      },
      {
        time: 721,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 727,
        text: "Let's see one more perfect rep. Pass, turn, find, pass. Beautiful.",
      },
      {
        time: 734,
        text: 'Prepare for the final minute.',
      },
      {
        time: 738,
        text: "Last minute! Let's see it at game speed. Quick turns, sharp passes, smart movement.",
      },
      {
        time: 746,
        text: 'Imagine this is a real game. You have to be fast and clever.',
      },
      {
        time: 752,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 758,
        text: 'Faster, sharper, smarter! Come on!',
      },
      {
        time: 763,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 769,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 774,
        text: 'Get ready for the countdown!',
      },
      {
        time: 777,
        text: 'Five, four, three, two, one... and TIME! Great game awareness.',
      },
      {
        time: 784,
        text: "That was some smart soccer! Now for a classic team move, 'The 1-2 Wall Pass'!",
      },
      {
        time: 792,
        text: "This is a three-player move, but we're going to use the wall as our third player. Player A, start with the ball. Player B, stand near the wall, ready to be the setup player.",
      },
      {
        time: 803,
        text: "Player A, you're going to pass the ball to Player B. As soon as you pass, make a run past them into the open space.",
      },
      {
        time: 812,
        text: "Player B, you're the magic setup player! With one touch, you're going to pass the ball at an angle against the wall, right into the path of Player A.",
      },
      {
        time: 823,
        text: "Player A, you have to run onto the rebound from the wall. It's a perfect give-and-go! After you get it, pass it back to Player B and you switch roles. Go!",
      },
      {
        time: 835,
        text: "So it's: Pass, run, one-touch off the wall, and receive! It's a beautiful team play.",
      },
      {
        time: 844,
        text: 'The pass off the wall from the setup player has to be perfect. The right angle and the right speed.',
      },
      {
        time: 852,
        text: "And the runner's run has to be timed perfectly. Don't go too early or too late.",
      },
      {
        time: 860,
        text: "Let's see five good combos. Keep switching roles after each one.",
      },
      {
        time: 867,
        text: 'One minute in! Your timing is looking great. Keep that quality high.',
      },
      {
        time: 874,
        text: 'Remember, the pass off the wall is a one-touch pass. No stopping the ball.',
      },
      {
        time: 881,
        text: 'This makes the move super fast and impossible to defend.',
      },
      {
        time: 887,
        text: "Let's see that quick one-touch off the wall. Be sharp!",
      },
      {
        time: 893,
        text: "Excellent! You're thinking and playing fast.",
      },
      {
        time: 899,
        text: "Two minutes in! Let's focus on the run. It has to be a real sprint!",
      },
      {
        time: 906,
        text: 'The faster you run, the more space you create. Explode into that space.',
      },
      {
        time: 913,
        text: "Let's see that sprint now. Pass and GO! Don't hesitate.",
      },
      {
        time: 920,
        text: "Awesome! That's how you create a scoring chance.",
      },
      {
        time: 926,
        text: 'Keep that focus. Every pass, every run matters.',
      },
      {
        time: 932,
        text: "Halfway! Let's make sure we're communicating. The runner can shout 'Wall!' to let the passer know they're ready.",
      },
      {
        time: 941,
        text: "Good communication makes the move even better. Let's hear it!",
      },
      {
        time: 947,
        text: "Let's see another perfect combo. A good pass, a sharp run, a clever wall pass, and a great receive.",
      },
      {
        time: 956,
        text: 'You two are looking like a professional partnership. Keep it up.',
      },
      {
        time: 962,
        text: "Let's see one with your weak foot on the first pass. Challenge yourself.",
      },
      {
        time: 969,
        text: 'Good effort! Keep working on it.',
      },
      {
        time: 974,
        text: 'Now back to your strong foot. Make it perfect.',
      },
      {
        time: 979,
        text: "You're doing great. This is a very smart, tactical drill.",
      },
      {
        time: 985,
        text: 'Prepare for the final minute.',
      },
      {
        time: 989,
        text: "Last minute! Let's see it at game speed. This should be a fluid, unstoppable move.",
      },
      {
        time: 997,
        text: "Imagine you're breaking through the defense. That's the speed we need.",
      },
      {
        time: 1004,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1010,
        text: 'Faster runs, sharper passes! Come on!',
      },
      {
        time: 1015,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1021,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1026,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1029,
        text: 'Five, four, three, two, one... and TIME! Fantastic movement.',
      },
      {
        time: 1036,
        text: "That was some amazing teamwork! Next up we have a super important challenge: the 'Weak Foot Wall Work'!",
      },
      {
        time: 1045,
        text: "This whole drill is with your 'other' foot, your non-dominant one. It feels weird, but this makes you a much better player. Player 1, start at the wall. Player 2, behind them.",
      },
      {
        time: 1056,
        text: 'Player 1, pass to the wall with your weak foot, and control the rebound with your weak foot. Then, turn and pass to Player 2. Go!',
      },
      {
        time: 1066,
        text: "It's okay if it's not perfect! The more you practice, the stronger that foot will get. Player 2, your turn at the wall!",
      },
      {
        time: 1075,
        text: "Focus on the technique. Lock your ankle, use the inside of your foot. Don't just kick with your toe.",
      },
      {
        time: 1083,
        text: "Let's see five good turns each. Pass, control, turn, pass to your partner. Keep it flowing.",
      },
      {
        time: 1091,
        text: "One minute in! You're both doing great. Practice makes perfect.",
      },
      {
        time: 1097,
        text: "Let's try a one-touch pass off the wall with your weak foot now! Just a quick tap.",
      },
      {
        time: 1104,
        text: "So it's: Partner passes to you, you one-touch it off the wall, control the rebound with your weak foot, and pass back. A 1-2 combo!",
      },
      {
        time: 1115,
        text: "Let's see that combo again. Pass from partner, one-touch off the wall, control, pass back. Awesome!",
      },
      {
        time: 1124,
        text: 'Keep switching roles. Both players get to practice the combo.',
      },
      {
        time: 1130,
        text: 'This is how you build confidence in that other foot. Keep working hard.',
      },
      {
        time: 1136,
        text: "Let's see another good one. Make it your best one yet.",
      },
      {
        time: 1141,
        text: "Excellent. You're looking more comfortable already.",
      },
      {
        time: 1146,
        text: "Two minutes in! Let's go back to the simple pass and control off the wall with the weak foot.",
      },
      {
        time: 1154,
        text: 'But this time, your partner behind you is going to move. After you control it, you have to find them!',
      },
      {
        time: 1162,
        text: "So it's: Weak foot pass to wall, weak foot control, find your moving partner, and pass. Go!",
      },
      {
        time: 1171,
        text: 'This is a big challenge for your brain and your feet. Keep that head up!',
      },
      {
        time: 1178,
        text: "You're doing great. Keep that focus. Every touch makes you better.",
      },
      {
        time: 1184,
        text: "Let's see another good one. Find that open player!",
      },
      {
        time: 1189,
        text: 'Halfway! Be patient with yourselves. Every successful pass is a huge victory for your weak foot.',
      },
      {
        time: 1197,
        text: "Let's go back to the first drill. Just a simple pass and control off the wall with your weak foot. Make it perfect.",
      },
      {
        time: 1206,
        text: "Let's see ten good reps in a row from each of you. Switch after ten. Go!",
      },
      {
        time: 1213,
        text: 'Remember to keep your body balanced. It helps you make a good pass.',
      },
      {
        time: 1219,
        text: "You're building a brand new skill. Be proud of your effort.",
      },
      {
        time: 1225,
        text: "Let's see another good one. Make it clean.",
      },
      {
        time: 1230,
        text: "Awesome. You're looking like a two-footed player.",
      },
      {
        time: 1236,
        text: "Keep that quality high. We're building great habits.",
      },
      {
        time: 1241,
        text: "Let's go back to the 1-2 combo. Pass from partner, one-touch off the wall, control.",
      },
      {
        time: 1249,
        text: 'This is a great way to practice thinking fast with your other foot.',
      },
      {
        time: 1255,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1259,
        text: "Last minute! Let's just focus on making good, clean contact with the ball. A solid pass and a solid touch with your weak foot.",
      },
      {
        time: 1268,
        text: 'Freestyle with your weak foot. One-touch, two-touch, your choice. Just keep it going.',
      },
      {
        time: 1275,
        text: "Thirty seconds left! Empty the tank. Give me everything you've got with that weak foot.",
      },
      {
        time: 1282,
        text: "Come on, you're almost there. Don't stop now.",
      },
      {
        time: 1287,
        text: 'Final push. Make these last touches your best ones.',
      },
      {
        time: 1292,
        text: 'Get ready for the countdown.',
      },
      {
        time: 1295,
        text: 'Five, four, three, two, one... and TIME! You just made yourselves better players.',
      },
      {
        time: 1302,
        text: "That was some great work on your weak foot! For our final drill, get ready for the super fun 'Wall Control Challenge'!",
      },
      {
        time: 1311,
        text: "This is a fun game that tests your brain and your feet! Player 1, you'll be the 'Passer' at the wall. Player 2, you'll be the 'Caller' a few steps behind.",
      },
      {
        time: 1321,
        text: "Passer, you'll pass to the wall. Caller, while the ball is moving, you'll shout an instruction! The Passer has to react and control the rebound based on the call.",
      },
      {
        time: 1332,
        text: "After the Passer controls it, they pass it to the Caller. Then you switch roles! The Caller becomes the new Passer. Let's go!",
      },
      {
        time: 1342,
        text: "Alright, first Passer, hit the wall! Caller, get ready to shout 'RIGHT FOOT!' or 'LEFT FOOT!'",
      },
      {
        time: 1351,
        text: 'Good control! Now pass to your partner and switch spots! Quick, quick, quick!',
      },
      {
        time: 1358,
        text: 'New Passer, hit the wall! New Caller, give the command!',
      },
      {
        time: 1364,
        text: 'Excellent reaction! Pass and switch! Keep that flow going.',
      },
      {
        time: 1370,
        text: 'This is great teamwork. You both have to be focused.',
      },
      {
        time: 1376,
        text: "One minute in! Your reactions are looking sharp. Let's add a new command: 'STOP IT DEAD!'",
      },
      {
        time: 1385,
        text: "So the calls are: 'RIGHT FOOT!', 'LEFT FOOT!', or 'STOP IT DEAD!'. Let's see it. Pass to the wall!",
      },
      {
        time: 1394,
        text: 'Good call! And a perfect stop! Now pass and switch.',
      },
      {
        time: 1400,
        text: "Caller, be tricky! Don't let your partner know what's coming.",
      },
      {
        time: 1406,
        text: 'Passer, you have to be ready for anything. Stay on your toes.',
      },
      {
        time: 1412,
        text: "Let's see another one. Good call, and great control. Pass and switch!",
      },
      {
        time: 1419,
        text: "You're both doing an amazing job. This is a very smart drill.",
      },
      {
        time: 1425,
        text: "Two minutes in! Let's add the final command: 'ONE-TOUCH!'",
      },
      {
        time: 1432,
        text: "When the Caller shouts 'ONE-TOUCH!', the Passer has to hit the rebound back to the Caller with their first touch. It's a big challenge!",
      },
      {
        time: 1442,
        text: "Alright, let's play! All four commands are in. Pass to the wall!",
      },
      {
        time: 1448,
        text: 'Good call! And a sharp one-touch pass! Now switch!',
      },
      {
        time: 1454,
        text: 'This is a great brain game for soccer. Thinking, seeing, and doing all at once.',
      },
      {
        time: 1461,
        text: "Let's see another one. What's the call going to be? Great reaction!",
      },
      {
        time: 1467,
        text: 'Keep that focus. Every pass, every call is important.',
      },
      {
        time: 1473,
        text: "Halfway! You're both looking like pros. Let's go for a streak. Can you get five perfect reps in a row without a mistake?",
      },
      {
        time: 1483,
        text: "That's one! Switch! That's two! Switch!",
      },
      {
        time: 1489,
        text: 'Three! Keep it going! Four! One more!',
      },
      {
        time: 1495,
        text: "Five! Awesome teamwork! Let's try for another streak.",
      },
      {
        time: 1501,
        text: 'This is how you build a real connection on the field.',
      },
      {
        time: 1507,
        text: "Keep that quality high. Don't get sloppy, even when you're tired.",
      },
      {
        time: 1513,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 1518,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1522,
        text: "Last minute! Let's go rapid-fire. Caller, give a new instruction on every single pass! Passer, you have to be ready!",
      },
      {
        time: 1531,
        text: "Pass! 'RIGHT FOOT!' Good, switch! Pass! 'ONE-TOUCH!' Good, switch!",
      },
      {
        time: 1539,
        text: "Thirty seconds left! Let's see that quick thinking and quick feet!",
      },
      {
        time: 1545,
        text: "Pass! 'LEFT FOOT!' Good, switch! Pass! 'STOP IT DEAD!' Good, switch!",
      },
      {
        time: 1552,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1558,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 1563,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1566,
        text: 'Five, four, three, two, one... and TIME! You did it!',
      },
      {
        time: 1573,
        text: 'Incredible work, team! You used that wall perfectly. Give your partner a big high-five!',
      },
      {
        time: 1581,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take some deep breaths.",
      },
      {
        time: 1589,
        text: "You worked on so many skills today and you both were amazing. Keep practicing, and you'll be unstoppable. Great job!",
      },
    ],
  },
  {
    id: 2177,
    name: 'Intermediate Partner Wall Skills',
    categories: [
      'Passing',
      'Ball Control',
      'Wall Drill',
      'First Touch',
      'Movement',
      'Combination Play',
    ],
    sports: ['soccer'],
    difficulty: 'Intermediate',
    skill:
      'Advanced wall passing, first touch under pressure, and dynamic combination play.',
    instructions:
      "This 30-minute intermediate session elevates wall training by incorporating your partner into fast-paced combinations. You'll work on quick decision-making, one-touch passing, and game-like movements to build a seamless connection.",
    equipment: '1 soccer ball, 1 wall',
    timeTotal: 1920,
    timedInstructions: [
      {
        time: 0,
        text: "Alright team, let's turn up the tempo! First up, a super fun drill called 'The Wall Combo Relay'. Player 1, you'll start about seven big steps from the wall. Player 2, stand a few steps behind them. Let's work!",
      },
      {
        time: 11,
        text: "Here's how it works: Player 1 will pass to the wall, control the rebound with a soft touch, and then pass back to Player 2.",
      },
      {
        time: 19,
        text: "As soon as Player 2 gets the pass, they'll quickly dribble up to take Player 1's spot, and Player 1 will move back. It's a continuous relay! Ready? Let's start the first rep. Player 1, pass to the wall!",
      },
      {
        time: 31,
        text: 'Good control! Now pass to Player 2 and switch! Quick, quick, quick!',
      },
      {
        time: 38,
        text: 'Player 2, your turn! Pass to the wall, soft control, and pass back to Player 1. Keep the flow going!',
      },
      {
        time: 47,
        text: 'The pace should be high. Pass to wall, touch, pass to partner, SWITCH! No standing still.',
      },
      {
        time: 55,
        text: "Let's see that again. A good pass to the wall, a clean control touch, and a sharp pass to your partner. Then switch fast!",
      },
      {
        time: 64,
        text: "One minute in! Your teamwork is looking great. Let's focus on the quality of the pass to your partner. It should be firm and accurate, making their life easy.",
      },
      {
        time: 75,
        text: "Let's try the whole sequence with your left foot now. Pass to the wall with your left, control with your left, and pass to your partner with your left. It's a challenge!",
      },
      {
        time: 86,
        text: 'Awesome! Now the next player does it with their left foot. This is how you build two superstar feet.',
      },
      {
        time: 94,
        text: "Back to your strong foot. Let's see that perfect combo. Wall, control, partner, switch!",
      },
      {
        time: 102,
        text: 'Remember to keep your head up. After you control the ball from the wall, you have to find your partner.',
      },
      {
        time: 110,
        text: "You're both doing great. Keep that energy high.",
      },
      {
        time: 115,
        text: "Let's see another one. Make it your fastest switch yet.",
      },
      {
        time: 120,
        text: "Excellent. You're looking sharp.",
      },
      {
        time: 124,
        text: "Two minutes in! Let's make that first touch from the wall even better. It should be a directional touch.",
      },
      {
        time: 132,
        text: 'So, when the ball comes back, take your first touch to the side, into space. This will give you a better angle to pass to your partner.',
      },
      {
        time: 142,
        text: "Let's see it. Pass to the wall, directional touch, then pass to your partner. Awesome!",
      },
      {
        time: 150,
        text: "This is a more advanced skill. It's how you create space in a real game.",
      },
      {
        time: 157,
        text: 'Halfway! Now, your first touch from the wall must be with your back foot, the foot farther away from the wall. This opens up your body to see your partner.',
      },
      {
        time: 168,
        text: "This is a pro-level move. Let's see you try it. Pass to the wall, open your body, and control with that back foot.",
      },
      {
        time: 178,
        text: 'Excellent! Now you can see your partner perfectly for the pass. Keep practicing that.',
      },
      {
        time: 185,
        text: "Let's see five good reps of that back-foot control from each of you.",
      },
      {
        time: 191,
        text: "You're both learning so fast. This is fantastic to see.",
      },
      {
        time: 197,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 202,
        text: "Let's go back to a simple control touch, but make it super quick. Touch and pass in a flash.",
      },
      {
        time: 210,
        text: "Awesome. You're thinking and playing fast.",
      },
      {
        time: 215,
        text: "Keep that quality high. We're building great habits.",
      },
      {
        time: 220,
        text: "Let's see one more with your weak foot. A good pass, a good control, and a good pass to your partner.",
      },
      {
        time: 228,
        text: 'Excellent effort! Keep working on it.',
      },
      {
        time: 233,
        text: 'Now back to your strong foot. Make it perfect.',
      },
      {
        time: 238,
        text: 'Prepare for the final minute.',
      },
      {
        time: 242,
        text: "Last minute! Let's see game speed. The transition between players should be seamless. No hesitation.",
      },
      {
        time: 250,
        text: "Imagine you're in a real game. You have to be fast and smart.",
      },
      {
        time: 256,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 262,
        text: 'Faster passes, quicker switches! Come on!',
      },
      {
        time: 267,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 273,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 278,
        text: 'Get ready for the countdown!',
      },
      {
        time: 281,
        text: 'Five, four, three, two, one... and time! Sharp start.',
      },
      {
        time: 288,
        text: "That was a fantastic first exercise! Next up, a test of rhythm and control I call the 'Rapid Fire Volley Rally'!",
      },
      {
        time: 296,
        text: "This is a team challenge! Player 1, you'll stand a few steps from the wall. Player 2, you'll stand right next to them. Get ready!",
      },
      {
        time: 304,
        text: 'Player 1, you start by tossing the ball against the wall. As it comes back, Player 2, you have to volley it back to the wall with one touch!',
      },
      {
        time: 313,
        text: "Now, Player 1, it's your turn! You have to volley the next rebound back to the wall with one touch. You're a team, keeping a rally going. Go!",
      },
      {
        time: 323,
        text: "So it's: Player 1 hits it, Player 2 hits it, Player 1 hits it... Don't let the ball stop!",
      },
      {
        time: 331,
        text: "Let's start with your right foot only. A one-touch volley with the inside of your right foot. Keep it clean.",
      },
      {
        time: 339,
        text: "Let's see if you two can get a rally of ten in a row. Count them out loud!",
      },
      {
        time: 345,
        text: 'One... two... three... keep it going!',
      },
      {
        time: 350,
        text: 'Four... five... six... great teamwork!',
      },
      {
        time: 355,
        text: 'Seven... eight... nine... almost there!',
      },
      {
        time: 360,
        text: "Ten! Awesome job! Let's try for a new record.",
      },
      {
        time: 366,
        text: 'Remember to stay on your toes. You have to be ready for the ball to come to you.',
      },
      {
        time: 372,
        text: "This is how you learn to play fast and think fast. You're doing great.",
      },
      {
        time: 378,
        text: "Now, let's switch to your left foot only. This is a big challenge!",
      },
      {
        time: 385,
        text: "It's okay if it's not perfect. Every touch with that foot makes it stronger.",
      },
      {
        time: 392,
        text: "Let's see if you can get a rally of five with your weak feet. You can do it!",
      },
      {
        time: 399,
        text: 'Excellent effort! Keep working on it.',
      },
      {
        time: 404,
        text: "Two minutes in! Let's go back to your strong feet. Let's see that perfect volley.",
      },
      {
        time: 411,
        text: "Now, let's try a half-volley. Let the ball bounce once before you hit it. Take turns.",
      },
      {
        time: 419,
        text: 'So Player 1 lets it bounce, hits it. Player 2 lets it bounce, hits it. Find that rhythm.',
      },
      {
        time: 427,
        text: 'This gives you a little more time, but the touch has to be just as clean.',
      },
      {
        time: 433,
        text: "Let's see a good half-volley rally. Keep it going.",
      },
      {
        time: 438,
        text: "Halfway! Now for the ultimate challenge. The server's choice!",
      },
      {
        time: 444,
        text: 'Whoever is hitting the ball can choose: a volley OR a half-volley. Your partner has to read it and react!',
      },
      {
        time: 453,
        text: 'So you might hit a volley, and your partner might hit a half-volley. Be unpredictable!',
      },
      {
        time: 461,
        text: 'This is how you train your soccer brain to think and react quickly.',
      },
      {
        time: 467,
        text: "Let's see it. A volley... good. Now a half-volley... excellent adjustment!",
      },
      {
        time: 474,
        text: 'Keep that rally going. You two are working together like a real team.',
      },
      {
        time: 480,
        text: "Let's see another good streak. How many can you get?",
      },
      {
        time: 486,
        text: 'Keep that quality high. Every strike matters.',
      },
      {
        time: 491,
        text: "You're both doing an amazing job. This is a very advanced drill.",
      },
      {
        time: 497,
        text: "Let's see one more perfect rally before the final minute.",
      },
      {
        time: 503,
        text: 'Awesome. Prepare to empty the tank.',
      },
      {
        time: 507,
        text: "Last minute! Let's see your fastest rally yet. Pure one-touch volleys. Go, go, go!",
      },
      {
        time: 515,
        text: "Don't let it drop! Keep that ball moving.",
      },
      {
        time: 520,
        text: 'Thirty seconds left! Empty the tank. Keep that rally going.',
      },
      {
        time: 526,
        text: "Come on, a few more passes. Don't stop now.",
      },
      {
        time: 531,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 537,
        text: 'Push it, push it! Almost there!',
      },
      {
        time: 542,
        text: 'Five, four, three, two, one... and TIME! Great tempo control.',
      },
      {
        time: 549,
        text: "That was some super-fast teamwork! Now for a clever combination play called 'The Wall Layoff'!",
      },
      {
        time: 557,
        text: "This is a three-player move, but we're using the wall as our third player! Player A, you start with the ball. Player B, you stand near the wall, ready to be the setup player.",
      },
      {
        time: 568,
        text: "Player A, pass the ball to Player B. Player B, with one soft touch, you're going to play the ball at an angle against the wall. This is the 'layoff'.",
      },
      {
        time: 579,
        text: 'Player A, you have to read this and run onto the rebound from the wall! After you control it, pass it back to Player B, who has moved into your starting spot. Now you switch roles! Go!',
      },
      {
        time: 593,
        text: "So the sequence is: Pass, one-touch layoff off the wall, run and receive, then pass to your partner and switch! It's a perfect give-and-go.",
      },
      {
        time: 604,
        text: 'The layoff pass from the setup player has to be perfect. The right angle and the right speed.',
      },
      {
        time: 612,
        text: "And the runner's run has to be timed perfectly. Don't go too early or too late.",
      },
      {
        time: 619,
        text: "Let's see five good combos. Keep switching roles after each one.",
      },
      {
        time: 626,
        text: 'One minute in! Your timing is looking great. Keep that quality high.',
      },
      {
        time: 633,
        text: 'Remember, the layoff pass is a one-touch pass. No stopping the ball.',
      },
      {
        time: 640,
        text: 'This makes the move super fast and impossible to defend.',
      },
      {
        time: 646,
        text: "Let's see that quick one-touch off the wall. Be sharp!",
      },
      {
        time: 652,
        text: "Excellent! You're thinking and playing fast.",
      },
      {
        time: 658,
        text: "Two minutes in! Let's focus on the run. It has to be a real sprint!",
      },
      {
        time: 665,
        text: 'The faster you run, the more space you create. Explode into that space.',
      },
      {
        time: 672,
        text: "Let's see that sprint now. Pass and GO! Don't hesitate.",
      },
      {
        time: 679,
        text: "Awesome! That's how you create a scoring chance.",
      },
      {
        time: 685,
        text: 'Keep that focus. Every pass, every run matters.',
      },
      {
        time: 691,
        text: "Halfway! Let's make sure we're communicating. The runner can shout 'Wall!' to let the passer know they're ready.",
      },
      {
        time: 700,
        text: "Good communication makes the move even better. Let's hear it!",
      },
      {
        time: 706,
        text: "Let's see another perfect combo. A good pass, a sharp run, a clever wall pass, and a great receive.",
      },
      {
        time: 715,
        text: 'You two are looking like a professional partnership. Keep it up.',
      },
      {
        time: 721,
        text: "Let's see one with your weak foot on the first pass. Challenge yourself.",
      },
      {
        time: 728,
        text: 'Good effort! Keep working on it.',
      },
      {
        time: 733,
        text: 'Now back to your strong foot. Make it perfect.',
      },
      {
        time: 738,
        text: "You're doing great. This is a very smart, tactical drill.",
      },
      {
        time: 744,
        text: 'Prepare for the final minute.',
      },
      {
        time: 748,
        text: "Last minute! Let's see it at game speed. This should be a fluid, unstoppable move.",
      },
      {
        time: 756,
        text: "Imagine you're breaking through the defense. That's the speed we need.",
      },
      {
        time: 763,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 769,
        text: 'Faster runs, sharper passes! Come on!',
      },
      {
        time: 774,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 780,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 785,
        text: 'Get ready for the countdown!',
      },
      {
        time: 788,
        text: 'Five, four, three, two, one... and TIME! Beautiful soccer.',
      },
      {
        time: 795,
        text: "That was some incredibly smart teamwork! Time to get airborne with 'Aerial Control & Wall Volley'!",
      },
      {
        time: 803,
        text: "Player A, you'll be the server first. Player B, you're the volleyer. Server, toss a nice, soft, waist-high ball to your partner.",
      },
      {
        time: 811,
        text: "Volleyer, your job is to control the ball out of the air with your thigh or chest. With your very next touch, you must VOLLEY the ball against the wall. Don't let it bounce!",
      },
      {
        time: 822,
        text: "After you volley it off the wall, control the rebound and pass it back to your partner. Then you switch roles! It's a continuous relay. Go!",
      },
      {
        time: 833,
        text: "So it's: Control, volley off wall, control, pass, and SWITCH! This tests your aerial control and your volley technique all at once.",
      },
      {
        time: 843,
        text: "Let's see it. A good toss, a soft thigh control, and a clean volley against the wall. Perfect!",
      },
      {
        time: 851,
        text: 'Now the other player is up! Remember to use the inside of your foot for a controlled volley. Aim for a big spot on the wall.',
      },
      {
        time: 860,
        text: "One minute in! Your control is looking great. Let's try to use your chest now. A big, soft chest to cushion the ball.",
      },
      {
        time: 869,
        text: 'Toss, chest control, and a sharp volley! Awesome!',
      },
      {
        time: 875,
        text: 'Keep switching after every single rep. The faster you switch, the more touches you both get.',
      },
      {
        time: 883,
        text: "This is how you learn to be ready for any kind of pass in a game. You're doing great.",
      },
      {
        time: 890,
        text: "Let's see another one. Make it your best one yet. A perfect control and a perfect volley.",
      },
      {
        time: 897,
        text: "Excellent! You're looking like a pro.",
      },
      {
        time: 902,
        text: 'Two minutes in! The first touch is everything. It has to set you up perfectly for the volley.',
      },
      {
        time: 910,
        text: "Let's try a challenge. After you control the ball with your thigh or chest, can you let it bounce once and hit a half-volley?",
      },
      {
        time: 919,
        text: "So it's: Control, bounce, half-volley! This gives you a little more time to set up.",
      },
      {
        time: 927,
        text: "Let's see it. A good toss, a soft thigh control, let it bounce, and a clean half-volley. Awesome!",
      },
      {
        time: 936,
        text: 'Keep switching roles. Everyone gets to practice the half-volley.',
      },
      {
        time: 942,
        text: "You're both doing an amazing job. This is a very advanced skill.",
      },
      {
        time: 948,
        text: "Halfway! Let's try to volley it back with your laces for more power. Keep your eye on the ball and your ankle locked.",
      },
      {
        time: 957,
        text: 'This is a power shot! Control it, and then PING it off the wall with your laces.',
      },
      {
        time: 964,
        text: "Great power! Now let's go back to the inside-of-the-foot volley for control.",
      },
      {
        time: 971,
        text: "Let's switch roles again. New volleyer, let's see that lace volley.",
      },
      {
        time: 977,
        text: "Remember, point that toe down. That's the secret to a powerful strike.",
      },
      {
        time: 983,
        text: "Excellent strike! You're both looking like goal scorers.",
      },
      {
        time: 989,
        text: 'Keep that focus. Every volley is a chance to get better.',
      },
      {
        time: 994,
        text: "Let's see another good one. Make it your best one yet.",
      },
      {
        time: 999,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1003,
        text: "Last minute! Let's see some clean technique. A soft touch to control, and a crisp, clean volley.",
      },
      {
        time: 1010,
        text: "Your choice now: volley or half-volley. Show me what you've learned.",
      },
      {
        time: 1016,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1022,
        text: 'Faster tosses, quicker control, sharper volleys! Come on!',
      },
      {
        time: 1028,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1034,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1039,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1042,
        text: 'Five, four, three, two, one... and TIME! Great technical skill.',
      },
      {
        time: 1049,
        text: "That was some incredible flair! Now it's time to test your skills under pressure with 'Pressure Wall Passing'!",
      },
      {
        time: 1057,
        text: "Player 1, you're the attacker first. Stand facing the wall. Player 2, you're the defender. Stand right behind Player 1 and give them a little challenge.",
      },
      {
        time: 1067,
        text: 'Defender, pass the ball to your attacker. Attacker, your job is to play quick one-touch passes against the wall while the defender is on your back.',
      },
      {
        time: 1077,
        text: "You have to use your body to shield the ball and your feet have to be quick to adjust to every rebound. Defender, you're not trying to steal the ball, just be a pesky shadow!",
      },
      {
        time: 1089,
        text: "Attacker, after five passes, control the ball, turn, and pass to your partner. Then you become the defender! Let's go!",
      },
      {
        time: 1099,
        text: "Attacker, be strong! Use your arms to feel where the defender is. Don't let them push you off the ball.",
      },
      {
        time: 1107,
        text: 'Good! Now control, turn, and pass! Switch roles! Quick, quick, quick!',
      },
      {
        time: 1114,
        text: 'New attacker, get ready! Defender, apply that pressure.',
      },
      {
        time: 1120,
        text: 'One minute in! Your control under pressure is looking great. Keep that quality high.',
      },
      {
        time: 1127,
        text: 'Remember to stay low and balanced. It makes you much stronger on the ball.',
      },
      {
        time: 1134,
        text: "Let's see another good switch. Five passes, control, turn, pass, and switch!",
      },
      {
        time: 1142,
        text: "You're both doing great. This is how you learn to protect the ball in a real game.",
      },
      {
        time: 1149,
        text: 'Keep that focus. Every pass should be sharp, even with a defender on you.',
      },
      {
        time: 1155,
        text: "Two minutes in! Let's make it harder. Attacker, you now have to use your weak foot for two of the five passes.",
      },
      {
        time: 1204,
        text: 'This is a big challenge! It forces you to think and adjust under pressure.',
      },
      {
        time: 1211,
        text: "Let's see it. Three with your strong foot, two with your weak foot. You can do it.",
      },
      {
        time: 1218,
        text: 'Excellent! Now control, turn, and switch. New attacker, your turn for the weak foot challenge.',
      },
      {
        time: 1226,
        text: "This is how you build confidence in both feet, so you're ready for any situation.",
      },
      {
        time: 1233,
        text: "Keep that quality high. Don't get sloppy.",
      },
      {
        time: 1238,
        text: "Halfway! Let's add another new rule. Attacker, on your fifth pass, instead of controlling and turning, you must try to turn with your first touch.",
      },
      {
        time: 1250,
        text: "So it's four one-touch passes, and on the fifth rebound, a sharp turn away from the defender into space. Then pass to your partner and switch.",
      },
      {
        time: 1261,
        text: "This is a superstar move! Let's see it. Four quick passes... now a sharp turn! Awesome!",
      },
      {
        time: 1269,
        text: 'That turn has to be explosive. Get away from that pressure.',
      },
      {
        time: 1275,
        text: "Let's see it again. Keep that defender on their toes.",
      },
      {
        time: 1281,
        text: "You're both doing an amazing job. This is a very advanced drill.",
      },
      {
        time: 1287,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1293,
        text: "Let's see one more perfect rep. Four passes and an explosive turn.",
      },
      {
        time: 1300,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1304,
        text: "Last minute! Let's see some real fight. Protect the ball, keep your composure, and execute your passes under pressure.",
      },
      {
        time: 1313,
        text: 'Your choice now. Do the five passes and turn, or the four passes and explosive first-touch turn. Be creative!',
      },
      {
        time: 1322,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1328,
        text: 'Faster passes, quicker turns! Come on!',
      },
      {
        time: 1333,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1339,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 1344,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1347,
        text: 'Five, four, three, two, one... and TIME! Great strength and focus.',
      },
      {
        time: 1354,
        text: "That was some incredible work under pressure! For our final drill, it's time for the 'Wall Combination Freestyle'!",
      },
      {
        time: 1362,
        text: 'This is where you put everything together. For five minutes, you two will work together to create passing combinations using the wall. There are no set rules, just be creative!',
      },
      {
        time: 1374,
        text: "Let's start with a 'Wall Layoff'. Player A, pass to Player B at the wall. Player B, one-touch it off the wall for Player A to run onto. Go!",
      },
      {
        time: 1385,
        text: 'Good! Now, after Player A gets the ball, pass it back to Player B and switch roles. Keep that flow going.',
      },
      {
        time: 1393,
        text: "Let's see a 'Two-Touch Wall & Switch' now. Player at the wall, pass, control, and find your partner. Then switch!",
      },
      {
        time: 1403,
        text: "Awesome! Now back to a 'Wall Layoff'. Keep mixing it up.",
      },
      {
        time: 1409,
        text: 'The goal is to keep the ball moving and to always be thinking one step ahead of your partner and the wall.',
      },
      {
        time: 1418,
        text: 'One minute in. The movement off the ball is just as important as the pass. Are you moving into a space to help your partner?',
      },
      {
        time: 1427,
        text: "Let's try to string together a sequence of at least five passes without a mistake. Talk to each other! 'Wall!', 'Turn!', 'Layoff!'",
      },
      {
        time: 1438,
        text: "You're starting to think like a real team. This is fantastic to see.",
      },
      {
        time: 1444,
        text: "Let's see another creative combo. Maybe a one-touch volley off the wall to your partner?",
      },
      {
        time: 1452,
        text: 'Excellent idea! Keep that creativity flowing.',
      },
      {
        time: 1457,
        text: "Two minutes in! Let's see a combination that uses a wall layoff followed by a give-and-go.",
      },
      {
        time: 1466,
        text: 'This is how you break down defenses with smart, quick passing.',
      },
      {
        time: 1472,
        text: 'Remember to keep your passes sharp and your movement explosive.',
      },
      {
        time: 1478,
        text: "Let's see that combo again. Make it even faster this time.",
      },
      {
        time: 1484,
        text: "Awesome! You two are playing like you can read each other's minds.",
      },
      {
        time: 1491,
        text: "Halfway! This should be fluid and fast. It should look like you've been playing together for years.",
      },
      {
        time: 1499,
        text: "Let's challenge your weak foot. For the next minute, all passes to your partner must be with your other foot.",
      },
      {
        time: 1508,
        text: "This is a big challenge, but it's how you become unstoppable.",
      },
      {
        time: 1514,
        text: 'Keep that quality high, even with your weak foot.',
      },
      {
        time: 1520,
        text: "Let's see a good, clean pass. You can do it.",
      },
      {
        time: 1525,
        text: 'Excellent! Keep working that foot.',
      },
      {
        time: 1530,
        text: 'Thirty more seconds on the weak foot challenge. Keep it up.',
      },
      {
        time: 1536,
        text: 'Awesome! Back to using any foot you want. Feels easier, right?',
      },
      {
        time: 1543,
        text: "Let's see another creative combination. Surprise me!",
      },
      {
        time: 1549,
        text: "You're both doing an amazing job. This is a very advanced drill.",
      },
      {
        time: 1555,
        text: 'Prepare for the final minute.',
      },
      {
        time: 1559,
        text: 'Last minute! Empty the tank! Show me your best, most creative combinations. Make it beautiful!',
      },
      {
        time: 1567,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 1572,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1578,
        text: "Thirty seconds! Let's see a perfect, flowing sequence to finish!",
      },
      {
        time: 1584,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 1589,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 1594,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 1599,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND THAT'S A WRAP! DRILL COMPLETE!",
      },
      {
        time: 1609,
        text: 'That was a top-level session. Your chemistry is outstanding. Give your partner a huge high-five!',
      },
      {
        time: 1617,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take some deep breaths.",
      },
      {
        time: 1624,
        text: "You've pushed your skills and your minds today. That's how you improve. Amazing job. Well done!",
      },
    ],
  },
  {
    id: 2178,
    name: 'Advanced Partner Wall Skills',
    categories: [
      'Passing',
      'Ball Control',
      'Wall Drill',
      'First Touch',
      'Movement',
      'Combination Play',
      'Tactical',
    ],
    sports: ['soccer'],
    difficulty: 'Advanced',
    skill:
      'Mastering high-tempo combinations, deceptive plays, and reactive decision-making using a wall.',
    instructions:
      'This 30-minute advanced session transforms the wall into a dynamic third teammate for elite-level training. You and your partner will execute intricate combinations, deceptive movements, and high-pressure drills to achieve seamless, game-speed chemistry.',
    equipment: '1 soccer ball, 1 wall',
    timeTotal: 1628,
    timedInstructions: [
      {
        time: 0,
        text: "Alright, let's get to it. No time to waste. First drill: 'Rapid Wall Exchange'. Player 1, you're on the wall. Player 2, you're the support. Let's see the pace.",
      },
      {
        time: 5,
        text: "Player 1 passes to Player 2. Player 2, one touch back to Player 1. Player 1, one touch off the wall, then one touch back to Player 2. It's a continuous triangle.",
      },
      {
        time: 10,
        text: 'The sequence is: A to B, B to A, A off wall, A to B. Every pass is one-touch. The ball must not stop moving.',
      },
      {
        time: 15,
        text: 'This requires constant movement, perfect pass weight, and anticipation. After 45 seconds of high-tempo work, switch roles. Go!',
      },
      {
        time: 90,
        text: 'Two minutes in! The quality cannot drop. The passes must be crisp, the movement sharp. Demand perfection from each other.',
      },
      {
        time: 150,
        text: 'Halfway! Now, the player at the wall must use their weak foot for the one-touch pass off the wall. This is a massive test of technique.',
      },
      {
        time: 240,
        text: "Last minute! Back to strong foot. Let's see the highest tempo yet. The ball should be a blur.",
      },
      {
        time: 290,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and hold. Elite speed of play.',
      },
      {
        time: 300,
        text: "Quick water. Next, we add deception. 'The Wall Dummy & Take'.",
      },
      {
        time: 320,
        text: 'Player 1 passes to Player 2, who is positioned near the wall. Player 2, you are going to dummy the pass. Let it go through your legs and hit the wall.',
      },
      {
        time: 325,
        text: 'You must sell the fake. Shape your body to play a one-touch pass, then pull out at the last second.',
      },
      {
        time: 330,
        text: 'As soon as the ball passes you, pivot and receive the rebound from the wall, then play back to Player 1.',
      },
      {
        time: 335,
        text: "This move is all about timing and deception, completely unbalancing a defender. Let's do five each, then switch roles.",
      },
      {
        time: 420,
        text: 'Two minutes down. Now, add another layer. Player 2 can either dummy it OR play a one-touch layoff off the wall for Player 1 to run onto. Player 1 must read and react.',
      },
      {
        time: 480,
        text: 'Halfway! Your body language is the key. Make your intentions impossible to read until the last moment.',
      },
      {
        time: 540,
        text: "Last minute! Let's see some real flair. A sharp head fake, a drop of the shoulder. Be unpredictable.",
      },
      {
        time: 610,
        text: 'Ten seconds! Last one each! Make it your most deceptive move yet! And FREEZE! Brilliant.',
      },
      {
        time: 620,
        text: "Grab a drink. Time for an aerial battle: 'Continuous Wall Volley Rally'.",
      },
      {
        time: 640,
        text: 'This is a pure test of technique. Both players stand about five steps from the wall.',
      },
      {
        time: 645,
        text: 'Player 1 starts by volleying the ball against the wall. Player 2 must play the rebound back against the wall with a one-touch volley.',
      },
      {
        time: 650,
        text: 'The goal is to keep a continuous volley rally going against the wall. The ball cannot touch the ground.',
      },
      {
        time: 655,
        text: "You'll need quick feet, perfect balance, and clean contact. Use the inside of your foot for control.",
      },
      {
        time: 660,
        text: 'See how many consecutive volleys you can get. If the ball drops, start the count over. Go!',
      },
      {
        time: 720,
        text: 'Two minutes in. Focus on your body shape. Get your knee over the ball for a controlled, downward volley.',
      },
      {
        time: 780,
        text: 'Halfway point! Now, you must alternate feet on each volley. Right foot, left foot, right foot. This is the ultimate technical challenge.',
      },
      {
        time: 840,
        text: "Last minute! Back to any foot. Let's go for a personal best record. Push each other.",
      },
      {
        time: 900,
        text: 'Ten seconds left! Keep it alive! And TIME! World-class technique.',
      },
      {
        time: 920,
        text: "Quick drink! Now for a high-level tactical move: 'The Wall Overlap'.",
      },
      {
        time: 940,
        text: 'Player 1 starts with the ball. Player 2 stands between Player 1 and the wall.',
      },
      {
        time: 945,
        text: 'Player 1 plays a pass to Player 2. As soon as the pass is made, Player 1 makes a sprinting overlap run around Player 2.',
      },
      {
        time: 950,
        text: 'Player 2, with one touch, you must play a perfectly weighted pass off the wall that leads the overlapping runner into space.',
      },
      {
        time: 955,
        text: 'The timing, angle, and pace of the wall pass are everything. It must be perfect for your partner to run onto in stride.',
      },
      {
        time: 960,
        text: 'After a few successful combos, switch roles. This is how you break down a compact defense.',
      },
      {
        time: 1020,
        text: "Two minutes in. The runner's movement must be a full-speed sprint to sell the move and create the space.",
      },
      {
        time: 1080,
        text: "Halfway! Let's add a variation. The passer can now choose: either play the wall overlap OR play a quick one-two directly with their partner. The runner must read and react.",
      },
      {
        time: 1140,
        text: "Last minute! Let's see this at full speed. A seamless, intelligent, and unstoppable attacking combination.",
      },
      {
        time: 1200,
        text: "Ten seconds! Last one each! Make it perfect! And REST! That's tactical excellence.",
      },
      {
        time: 1220,
        text: "Sip of water! Two drills left. 'The Chaos Chamber'.",
      },
      {
        time: 1240,
        text: 'Player 1 faces the wall. Player 2 stands behind with the ball. Player 2 will play a variety of passes towards the wall, around Player 1.',
      },
      {
        time: 1245,
        text: "Player 1, you must react to the rebound. The pass could be hard, soft, to your left, to your right. You don't know what's coming.",
      },
      {
        time: 1250,
        text: 'Your job is to get a clean first touch on the unpredictable rebound and play it back to Player 2. You must be agile and sharp.',
      },
      {
        time: 1255,
        text: 'This simulates the chaos of a goalmouth scramble or a deflected shot. You have to find order in the chaos.',
      },
      {
        time: 1260,
        text: 'Go for 60 seconds of intense reaction work, then switch roles.',
      },
      {
        time: 1380,
        text: 'Two minutes down. Your feet should be constantly moving, ready to adjust in any direction. Stay low and balanced.',
      },
      {
        time: 1440,
        text: 'Halfway! Now, your first touch must be directional. Control the chaotic rebound and take your touch away from the wall into space.',
      },
      {
        time: 1520,
        text: "Last minute! Let's see how sharp your reactions are. Player 2, make the passes even more unpredictable.",
      },
      {
        time: 1540,
        text: 'Ten seconds! Stay focused! And TIME! Incredible reflexes.',
      },
      {
        time: 1560,
        text: "Final break! Amazing work. Last drill: 'The Wall Master's Challenge'.",
      },
      {
        time: 1580,
        text: 'This is total improvisation. For five minutes, you and your partner will use the wall to create a continuous, flowing game.',
      },
      {
        time: 1585,
        text: 'The only rule is that you must incorporate at least three of the skills we worked on in every sequence: a one-touch pass, a deceptive move, a combination, or an aerial control.',
      },
      {
        time: 1590,
        text: "This is not about just passing. It's about problem-solving in real-time. Communicate, be creative, and push the boundaries of what you can do.",
      },
      {
        time: 1595,
        text: "Show me everything you've got. The wall is your teammate. Use it to create magic. Go!",
      },
      {
        time: 1660,
        text: 'One minute in. Are you combining skills? A dummy into a wall layoff? An aerial control into a one-touch volley?',
      },
      {
        time: 1720,
        text: "Two minutes in. The game should be fluid, with both players reading each other's movements and ideas.",
      },
      {
        time: 1800,
        text: 'Halfway! Challenge each other. Force your partner into a difficult situation and see if they can solve it with their skill.',
      },
      {
        time: 1860,
        text: 'Last minute! Empty the tank! This is a showcase of your technical and mental ability. Make it a masterpiece.',
      },
      {
        time: 1880,
        text: "Thirty seconds! Let's see a final, brilliant sequence that uses the wall, your partner, and your brain!",
      },
      {
        time: 1900,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... AND RELAX! DRILL COMPLETE!',
      },
      {
        time: 1910,
        text: 'That was the pinnacle of wall training. Truly exceptional work. High-five your partner.',
      },
      {
        time: 1920,
        text: "Cool down and hydrate. You've trained at an elite level today. Be proud of that focus and quality. Well done!",
      },
    ],
  },
  {
    id: 2179,
    name: 'Beginner Ultimate Partner Drill',
    categories: [
      'Passing',
      'Ball Control',
      'Wall Drill',
      'Dribbling',
      'Tricks',
    ],
    sports: ['soccer'],
    difficulty: 'Beginner',
    skill:
      'A fun, all-around introduction to passing, dribbling, wall work, and creative tricks.',
    instructions:
      'This 45-minute session is the ultimate partner workout for beginners! It combines the most important basics of passing and wall work with the fun flair of players like Messi and Ronaldo. Get ready to build your skills and have a blast!',
    equipment: '1 soccer ball, 1 wall',
    timeTotal: 2700,
    timedInstructions: [
      {
        time: 0,
        text: "Hey superstars! Welcome to today's training! Let's get started with our first and most important skill: the 'Tick-Tock Pass'. You two, stand about five big steps apart. Ready? Go!",
      },
      {
        time: 10,
        text: "Remember, since we're using a foam ball, it's super light! That means your passes need to be extra sharp and crisp to get to your partner. Let's see it!",
      },
      {
        time: 20,
        text: 'Player A, use the inside of your foot, that big flat part, to pass the ball nice and easy to your partner.',
      },
      {
        time: 28,
        text: "Great pass! Now, Player B, stop the ball with the bottom of your shoe. Just step on it gently, like you're squishing a bug.",
      },
      {
        time: 36,
        text: "Perfect! Now, Player B, pass it back with the inside of your other foot. So it's stop with one foot, pass with the other. Keep it going, you two!",
      },
      {
        time: 46,
        text: "Let's get a rhythm going. Tick-tock, back and forth. Stop, pass. Stop, pass.",
      },
      {
        time: 53,
        text: "Focus on that first touch when you stop the ball. Be a ball magnet! Don't let it bounce away from you.",
      },
      {
        time: 61,
        text: 'Good! When you pass, remember to point your toes up a little and lock your ankle. It makes the pass go super straight.',
      },
      {
        time: 69,
        text: "Remember to look at your partner before you pass! Give them a little nod so they know it's coming. That's teamwork!",
      },
      {
        time: 77,
        text: "That's it! Keep your body over the ball when you pass. Don't lean back.",
      },
      {
        time: 83,
        text: "Let's try a slightly firmer pass now. Make that foam ball zip across the floor to your partner.",
      },
      {
        time: 90,
        text: "Awesome! Now a super soft one. Like you're passing an egg you don't want to break.",
      },
      {
        time: 97,
        text: 'One minute in! You two are doing great. Keep that tick-tock rhythm going.',
      },
      {
        time: 103,
        text: "Let's see you two make five perfect passes in a row. Count them out loud! One...",
      },
      {
        time: 109,
        text: 'Two... remember to lock that ankle!',
      },
      {
        time: 113,
        text: 'Three... great eye contact!',
      },
      {
        time: 117,
        text: 'Four... what a soft touch to stop it!',
      },
      {
        time: 121,
        text: "Five! Yes! Let's do it again, team!",
      },
      {
        time: 126,
        text: 'Stay bouncy on your feet. Wiggle your toes! Be ready for the ball at all times.',
      },
      {
        time: 132,
        text: 'If the ball gets away, no problem! That happens. Just go get it and start the tick-tock again.',
      },
      {
        time: 139,
        text: "Let's check our feet. Is your other foot, the one not kicking, right next to the ball? That's your secret to staying balanced.",
      },
      {
        time: 148,
        text: 'Two minutes in! Your passes are looking sharper already. I can see the improvement.',
      },
      {
        time: 154,
        text: 'Okay, challenge time! For the next 30 seconds, only use your right foot. Pass and stop with just your right.',
      },
      {
        time: 162,
        text: "It feels a little weird, right? That's okay! We're waking up that foot and training our brains.",
      },
      {
        time: 169,
        text: 'Keep it up, just a few more seconds on the right foot.',
      },
      {
        time: 174,
        text: 'Awesome. Now switch! For the next 30 seconds, only use your left foot. Stop and pass with just the left.',
      },
      {
        time: 182,
        text: 'Come on, left foot, you can do it! This is how you become a player who can use both feet.',
      },
      {
        time: 189,
        text: 'Keep that focus. Stop, pass. Stop, pass.',
      },
      {
        time: 194,
        text: 'Awesome job, you two. Back to normal, use any foot you want. Tick-tock, tick-tock.',
      },
      {
        time: 201,
        text: 'Halfway through this drill! Keep that focus. Every single pass matters.',
      },
      {
        time: 207,
        text: 'Imagine a defender is between you two. Your pass has to be quick and accurate to get past them.',
      },
      {
        time: 215,
        text: "Let's see a super straight pass. No wobbles! A straight pass is a happy pass.",
      },
      {
        time: 221,
        text: "Think about your arms. Keep them out a little for balance, like you're an airplane.",
      },
      {
        time: 228,
        text: "Let's see another five perfect passes in a row. Go for it!",
      },
      {
        time: 233,
        text: "Keep that quality high. You're both working so hard.",
      },
      {
        time: 238,
        text: 'The best players in the world are the best passers. This is the most important skill.',
      },
      {
        time: 245,
        text: 'Keep that ball on the ground. A pass on the ground is much easier for your partner to control.',
      },
      {
        time: 251,
        text: "Last minute of Tick-Tock Passing! Let's make it the best minute yet. Maximum focus!",
      },
      {
        time: 258,
        text: "Let's see how many good passes you can get in 30 seconds. Count them up! Go!",
      },
      {
        time: 264,
        text: 'Faster, faster! But keep it clean. A good pass is better than a fast, sloppy pass.',
      },
      {
        time: 271,
        text: "Keep that energy high! You're almost there.",
      },
      {
        time: 276,
        text: 'Come on, push yourselves! Final few seconds of the count.',
      },
      {
        time: 281,
        text: 'Thirty seconds left! Can you speed it up even more? Faster, faster! Tick-tock-tick-tock!',
      },
      {
        time: 289,
        text: "Don't get tired now. This is where champions are made.",
      },
      {
        time: 294,
        text: 'Final push! Give me your best passes of the day.',
      },
      {
        time: 299,
        text: 'Keep going my friends!',
      },
      {
        time: 302,
        text: "That was a fantastic first exercise! Let's keep that energy going! Next up, we're going to learn how to dribble like the best in the world with the 'Messi Dribble'! You both need a ball for this one.",
      },
      {
        time: 312,
        text: "Alright, superstars, find your own little space. Let's start by just tapping the ball with the inside and outside of your feet. Tiny little touches.",
      },
      {
        time: 322,
        text: "Imagine the ball is glued to your feet, just like Messi! Keep it super, super close. That's the secret.",
      },
      {
        time: 330,
        text: 'Tap, tap, tap. Little touches. Try to keep your head up and take a quick peek around the room.',
      },
      {
        time: 338,
        text: "Now, let's try to dribble forward a few steps, keeping it close. Then turn around and come back. Lots of little taps, not big kicks.",
      },
      {
        time: 347,
        text: "Messi is the king of keeping the ball close. That's our only goal right now! Tiny touches.",
      },
      {
        time: 355,
        text: "Don't kick it and run! It's tap, tap, tap. You are the boss of that ball!",
      },
      {
        time: 362,
        text: "One minute in! Your feet are getting faster! Let's try to dribble in a little circle now.",
      },
      {
        time: 370,
        text: 'Keep the ball on the outside of your circle, using the outside of your foot to guide it.',
      },
      {
        time: 377,
        text: 'Good! Now go the other way! Make a circle in the other direction.',
      },
      {
        time: 384,
        text: "Let's use the bottom of our foot. Roll the ball forward with your sole. Then roll it back.",
      },
      {
        time: 392,
        text: "Now roll it side to side, right foot then left foot. You're dancing with the ball!",
      },
      {
        time: 400,
        text: 'Okay, back to dribbling. Take five little Messi touches forward, then stop the ball dead with the bottom of your shoe.',
      },
      {
        time: 409,
        text: "Let's do it again. Five touches, then STOP. Be the boss of that ball!",
      },
      {
        time: 416,
        text: "Try to look up for a second while you're dribbling. Just a quick peek! See your amazing partner working hard too!",
      },
      {
        time: 424,
        text: "You're both doing great. Keep that ball close. That's the Messi secret.",
      },
      {
        time: 430,
        text: "Let's see another circle. Make it a perfect, clean circle.",
      },
      {
        time: 436,
        text: 'Awesome. Your control is looking fantastic.',
      },
      {
        time: 442,
        text: "Let's try to dribble around your space now. Don't bump into your partner!",
      },
      {
        time: 448,
        text: 'This is great practice for keeping your head up and seeing other players.',
      },
      {
        time: 454,
        text: "Keep those tiny touches going. You're both looking like pros.",
      },
      {
        time: 460,
        text: "Let's go back to dribbling in a straight line. Forward and back. Keep it controlled.",
      },
      {
        time: 467,
        text: 'Remember, lots of little taps are better than one big kick.',
      },
      {
        time: 473,
        text: "Let's see another circle, this time with your weak foot only. It's a challenge!",
      },
      {
        time: 480,
        text: 'Good effort! Now back to both feet. Keep that rhythm.',
      },
      {
        time: 486,
        text: "Let's see another five touches and a stop. Make it sharp!",
      },
      {
        time: 492,
        text: "You're building amazing habits. Keep it up.",
      },
      {
        time: 497,
        text: "Halfway! Let's keep that energy high. More tiny touches!",
      },
      {
        time: 503,
        text: "Let's see who can do the most little taps in 10 seconds. Ready? Go!",
      },
      {
        time: 513,
        text: 'Awesome! Now back to your normal dribbling. Keep it close.',
      },
      {
        time: 519,
        text: "Let's see another perfect circle, make it smooth.",
      },
      {
        time: 525,
        text: 'And now the other way. Excellent.',
      },
      {
        time: 530,
        text: "You're both working so hard. This is how you become great dribblers.",
      },
      {
        time: 537,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 543,
        text: "Let's see another five touches and a stop. Make it your best one yet.",
      },
      {
        time: 550,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 554,
        text: "Last minute of Messi dribbles! Let's see your best stuff.",
      },
      {
        time: 560,
        text: "Dribble with a little more speed now! Go a little faster, but don't lose control! Still tiny touches!",
      },
      {
        time: 568,
        text: 'Thirty seconds left! Just dribble wherever you want. Free dance! But keep it close!',
      },
      {
        time: 576,
        text: 'Show me your best moves! A little spin, a quick turn!',
      },
      {
        time: 582,
        text: 'Final push! Keep that ball glued to your feet!',
      },
      {
        time: 587,
        text: 'Get ready for the countdown!',
      },
      {
        time: 591,
        text: 'Five, four, three, two, one... and FREEZE! Awesome control, both of you!',
      },
      {
        time: 598,
        text: "Fantastic job with the Messi Dribble! Now, let's use our new teammate, the wall, with a super smart drill called 'The 1-2 Wall Combo'!",
      },
      {
        time: 608,
        text: "Here's how it works. You two will stand side-by-side, a few feet apart, facing the wall. Player A, you'll start with the ball.",
      },
      {
        time: 617,
        text: "Player A, pass the ball to Player B. Player B, with your very first touch, play a little angled pass against the wall so it bounces back to Player A. It's a magic triangle! Let's try it.",
      },
      {
        time: 630,
        text: "Awesome! Player A, after you control it, it's your turn to be the setup player. Pass to Player B, who will play it off the wall for you. Keep switching roles. Go!",
      },
      {
        time: 642,
        text: "So it's: Pass to partner, one-touch off the wall, and control. Then the other way! Keep it flowing.",
      },
      {
        time: 651,
        text: 'The one-touch pass off the wall has to be soft and clever. Just a little tap to guide it back to your partner.',
      },
      {
        time: 659,
        text: "One minute in! Your teamwork is looking great. Let's see if you can get a rally of five combos in a row.",
      },
      {
        time: 667,
        text: 'Keep your feet ready. You have to be on your toes to make that one-touch pass.',
      },
      {
        time: 674,
        text: "This is how you learn to play quick, one-touch soccer. You're both doing great.",
      },
      {
        time: 681,
        text: "Let's try the whole combo with your left foot now. This is a big challenge!",
      },
      {
        time: 688,
        text: 'Pass with your left, and a one-touch wall pass with your left. You can do it!',
      },
      {
        time: 695,
        text: "Awesome effort! Back to your strong feet. Let's make it perfect.",
      },
      {
        time: 701,
        text: "Remember to talk to each other! A little shout of 'Here!' can help.",
      },
      {
        time: 708,
        text: "Two minutes in! Your passes are looking sharp. Let's focus on the angle of the wall pass.",
      },
      {
        time: 716,
        text: 'You want to pass it so it comes back right in front of your partner, not behind them.',
      },
      {
        time: 724,
        text: "Let's see a perfect angle on that wall pass. Guide it to them.",
      },
      {
        time: 731,
        text: "Excellent! You're not just passing; you're thinking like a playmaker.",
      },
      {
        time: 738,
        text: 'Keep that quality high. Every pass matters.',
      },
      {
        time: 743,
        text: "Let's see another good rally. How many can you get in a row?",
      },
      {
        time: 749,
        text: "You two are starting to read each other's minds! This is fantastic teamwork.",
      },
      {
        time: 756,
        text: "Halfway! Let's make it a little faster. Quicker passes between you.",
      },
      {
        time: 763,
        text: "The faster you pass, the faster you have to think. Let's push our brains!",
      },
      {
        time: 770,
        text: "Good. Keep that speed up, but don't lose your control. Quality is still number one.",
      },
      {
        time: 778,
        text: "Let's see another good one at this new speed. Pass, wall pass, control. Sharp!",
      },
      {
        time: 785,
        text: "You're both doing an amazing job. This is a very smart drill.",
      },
      {
        time: 791,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 797,
        text: "Let's see one more perfect combo. Make it your best one yet.",
      },
      {
        time: 803,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 807,
        text: "Last minute! Let's see your best stuff. Quick passes, smart angles, perfect control.",
      },
      {
        time: 815,
        text: "Imagine you're in a real game, breaking down the defense with your clever passing.",
      },
      {
        time: 822,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 828,
        text: 'Faster, sharper, smarter! Come on!',
      },
      {
        time: 833,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 839,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 844,
        text: 'Get ready for the countdown!',
      },
      {
        time: 847,
        text: 'Five, four, three, two, one... and TIME! Great teamwork.',
      },
      {
        time: 854,
        text: "That was some super smart passing! Now for a cool move to get past defenders, inspired by the great Cristiano Ronaldo! It's called the 'Ronaldo Sole Roll Escape'!",
      },
      {
        time: 865,
        text: "Here's how it works. Player A, you'll dribble slowly towards Player B. Player B, you're going to be a friendly defender, just standing still.",
      },
      {
        time: 875,
        text: 'Player A, when you get close to your partner, put the bottom of your right foot, your sole, on the ball and roll it across your body to the left.',
      },
      {
        time: 885,
        text: "As you roll it, take a big step with your right foot and then take the ball with your left foot to 'escape' past the defender. Then, pass it back and switch roles! Let's try it.",
      },
      {
        time: 898,
        text: "So it's: Dribble, roll, and escape! Good. Now switch! Player B, your turn to try the move.",
      },
      {
        time: 907,
        text: "The roll has to be quick and smooth. Imagine you're drawing a line on the floor with the ball.",
      },
      {
        time: 915,
        text: "Let's try it with the other foot. Dribble in, put your left foot on the ball, and roll it to your right. Escape with your right foot.",
      },
      {
        time: 925,
        text: "Awesome! You're both two-footed tricksters. Keep switching after every turn.",
      },
      {
        time: 932,
        text: "One minute in! Your rolls are looking great. Let's focus on the 'escape' part.",
      },
      {
        time: 939,
        text: 'After you roll the ball, your first touch with your other foot should be a bigger touch that pushes the ball into space.',
      },
      {
        time: 948,
        text: "Let's see that escape touch. Roll, and then a big touch to get away. Perfect!",
      },
      {
        time: 955,
        text: 'This is how you beat a defender and create space for a pass or a shot.',
      },
      {
        time: 962,
        text: "Let's see another one. Make that escape touch explosive!",
      },
      {
        time: 968,
        text: "You're doing great. Keep that quality high.",
      },
      {
        time: 973,
        text: "Two minutes in! Let's try to do the move a little bit faster now.",
      },
      {
        time: 979,
        text: 'Dribble in with a little more speed, then a quick roll and escape.',
      },
      {
        time: 986,
        text: "That's it! The speed makes the move even more tricky for the defender.",
      },
      {
        time: 992,
        text: 'Remember to keep your body low. It helps you change direction faster.',
      },
      {
        time: 998,
        text: "Let's see five good ones in a row from each of you. Keep switching.",
      },
      {
        time: 1004,
        text: "You're both looking like superstars. Keep it up.",
      },
      {
        time: 1009,
        text: "Halfway! Let's add a little fake to make it even better.",
      },
      {
        time: 1015,
        text: 'Before you do the roll, dip your shoulder one way, then roll the ball the other way.',
      },
      {
        time: 1023,
        text: "A little body fake will totally freeze the defender. Let's see it. Fake, roll, escape!",
      },
      {
        time: 1031,
        text: 'Awesome! That was a super sneaky move.',
      },
      {
        time: 1036,
        text: 'Keep practicing that. The more you trick the defender, the better.',
      },
      {
        time: 1042,
        text: "Let's see another one with the fake. Make it a big, convincing fake.",
      },
      {
        time: 1049,
        text: "Excellent! You're a true trickster.",
      },
      {
        time: 1054,
        text: 'Keep that focus. Every repetition makes you better.',
      },
      {
        time: 1060,
        text: "Let's see one more perfect rep. Dribble, fake, roll, and explode away.",
      },
      {
        time: 1067,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1071,
        text: "Last minute! Let's see your best Ronaldo Sole Roll. Game speed now!",
      },
      {
        time: 1078,
        text: "Imagine you're in a big game and you need to beat one last defender. This is the move!",
      },
      {
        time: 1086,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1092,
        text: 'Faster, sharper, more explosive! Come on!',
      },
      {
        time: 1097,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1103,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1108,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1111,
        text: 'Five, four, three, two, one... and TIME! Very tricky.',
      },
      {
        time: 1118,
        text: "That was some amazing skill! Now it's time to get strong on the ball with 'The Shielding Challenge'!",
      },
      {
        time: 1126,
        text: "This drill is all about protecting the ball like it's a treasure. Player A, you're the 'Shield' first. Player B, you're the gentle 'Defender'.",
      },
      {
        time: 1135,
        text: "Defender, pass the ball to the Shield. Shield, your job is to get your body between the defender and the ball. Don't let them get it!",
      },
      {
        time: 1144,
        text: "Defender, you're just going to apply gentle pressure. Try to poke the ball away, but don't be too rough. Shield, you have to protect it for 15 seconds! Ready? Go!",
      },
      {
        time: 1156,
        text: 'Shield, get your body low and wide. Use your arms to feel where the defender is. Be strong like a rock!',
      },
      {
        time: 1164,
        text: 'Keep the ball moving with little touches, but always keep your body between the ball and the defender.',
      },
      {
        time: 1172,
        text: 'Five, four, three, two, one... SWITCH! Great job! Now Player B is the Shield. Player A is the Defender. Go!',
      },
      {
        time: 1182,
        text: 'New Shield, get low and strong! Protect that treasure. New Defender, put on some gentle pressure.',
      },
      {
        time: 1190,
        text: "One minute in! You're both looking so strong on the ball. Keep it up.",
      },
      {
        time: 1196,
        text: "Remember to keep your knees bent. It gives you a strong base so you can't be pushed over.",
      },
      {
        time: 1204,
        text: 'Five, four, three, two, one... SWITCH! Back to our first Shield.',
      },
      {
        time: 1210,
        text: 'This time, Shield, try to turn away from the pressure. If the defender comes from the right, turn to your left.',
      },
      {
        time: 1219,
        text: 'This is how you escape pressure in a real game. Protect, then turn away.',
      },
      {
        time: 1226,
        text: "Five, four, three, two, one... SWITCH! New Shield, let's see that turn.",
      },
      {
        time: 1232,
        text: "Two minutes in! Your shielding is getting so much better. You're both looking tough to beat.",
      },
      {
        time: 1239,
        text: 'Defender, try to be a little quicker with your feet. Make the Shield work hard.',
      },
      {
        time: 1246,
        text: 'Shield, keep that ball moving. A still ball is an easy ball to steal.',
      },
      {
        time: 1253,
        text: "Five, four, three, two, one... SWITCH! Let's go again.",
      },
      {
        time: 1259,
        text: "This is a tiring drill, but it's making you both so much stronger.",
      },
      {
        time: 1265,
        text: "Halfway! Let's make it a little harder. Shield, you have to protect the ball for 20 seconds now!",
      },
      {
        time: 1273,
        text: 'You can do it! Stay low, stay strong, and keep that ball safe. Go!',
      },
      {
        time: 1279,
        text: 'Ten seconds down, ten to go! Keep fighting!',
      },
      {
        time: 1284,
        text: 'Five, four, three, two, one... SWITCH! Awesome work.',
      },
      {
        time: 1290,
        text: 'New Shield, your turn for the 20-second challenge. Get ready!',
      },
      {
        time: 1296,
        text: "Remember to use your arms to feel where the defender is. Don't let them surprise you.",
      },
      {
        time: 1303,
        text: 'Ten seconds down! Keep that body between them and the ball.',
      },
      {
        time: 1308,
        text: 'Five, four, three, two, one... SWITCH! You did it!',
      },
      {
        time: 1314,
        text: "You're both doing an amazing job. This is a very important skill.",
      },
      {
        time: 1320,
        text: "Let's go back to 15 seconds. Let's see how easy it feels now.",
      },
      {
        time: 1326,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 1331,
        text: "Last minute! Let's see your best shielding yet. Be as strong as a mountain!",
      },
      {
        time: 1338,
        text: 'Shield, after you protect it, try to do a quick turn and dribble away one step before passing.',
      },
      {
        time: 1346,
        text: "This is the final step: protect, and then escape! Let's see it.",
      },
      {
        time: 1352,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1358,
        text: 'Stronger, smarter, tougher! Come on!',
      },
      {
        time: 1363,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1369,
        text: 'One more perfect shield from each of you!',
      },
      {
        time: 1374,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1377,
        text: 'Five, four, three, two, one... and TIME! Great work.',
      },
      {
        time: 1384,
        text: "That was some amazing strength! Now for a super cool move from a legend: 'Zidane's Beginner Spin'!",
      },
      {
        time: 1393,
        text: "This is a simplified version of his famous turn. Player A, you'll pass to Player B. Player B, your job is to stop the ball with the sole of your right foot.",
      },
      {
        time: 1403,
        text: "As soon as you stop it, Player B, pull the ball back behind your standing leg while turning your body. It's a magic trick!",
      },
      {
        time: 1412,
        text: "After you complete the spin, pass the ball back to Player A, and then it's Player A's turn to do the move. Let's go!",
      },
      {
        time: 1422,
        text: 'So the pattern is: Pass, stop, drag, spin, and pass back. Then you switch!',
      },
      {
        time: 1430,
        text: "Let's see it again. A good pass, a clean stop, and a smooth spin. Awesome.",
      },
      {
        time: 1437,
        text: "Now let's try it with the left foot. Stop with the left, drag it back, and spin the other way.",
      },
      {
        time: 1446,
        text: "You're both two-footed magicians! Keep switching after every turn.",
      },
      {
        time: 1453,
        text: 'One minute in! Your spins are looking great. Remember to use your arms to help you balance as you turn.',
      },
      {
        time: 1461,
        text: "Let's see five good turns with your right foot. Keep switching roles.",
      },
      {
        time: 1467,
        text: 'Now five with your left. The more you practice, the sharper your spin will get.',
      },
      {
        time: 1474,
        text: 'This is a great move for protecting the ball while creating space.',
      },
      {
        time: 1481,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 1487,
        text: "Excellent! You're looking like a pro.",
      },
      {
        time: 1492,
        text: "Two minutes in! Let's try to make the move a little faster now.",
      },
      {
        time: 1498,
        text: "A quicker stop, and a quicker spin. Let's see that speed.",
      },
      {
        time: 1504,
        text: "That's it! The speed makes the move even more tricky for the defender.",
      },
      {
        time: 1510,
        text: 'Remember to keep your body low. It helps you spin faster.',
      },
      {
        time: 1516,
        text: "Let's see five good ones in a row from each of you at this new speed.",
      },
      {
        time: 1523,
        text: "You're both looking like superstars. Keep it up.",
      },
      {
        time: 1528,
        text: "Halfway! Let's focus on the 'take' after the spin. The pass back to your partner should be sharp.",
      },
      {
        time: 1536,
        text: "So it's: Stop, spin, and then a crisp, clean pass. No lazy passes.",
      },
      {
        time: 1543,
        text: "Let's see that sharp pass now. Make it perfect.",
      },
      {
        time: 1549,
        text: "Excellent! You're not just doing a trick; you're making a play.",
      },
      {
        time: 1555,
        text: 'Keep that quality high. Every part of the move matters.',
      },
      {
        time: 1561,
        text: "Let's see another one. A smooth spin and a sharp pass.",
      },
      {
        time: 1567,
        text: "You're both doing an amazing job. This is a very smart move.",
      },
      {
        time: 1573,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1579,
        text: "Let's see one more perfect rep. Stop, spin, and a perfect pass.",
      },
      {
        time: 1586,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1590,
        text: "Last minute! Let's see a smooth, elegant turn. You're dancing with the ball!",
      },
      {
        time: 1597,
        text: "Imagine you're Zidane in a big game. Show me that superstar quality.",
      },
      {
        time: 1604,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1610,
        text: 'Faster, smoother, sharper! Come on!',
      },
      {
        time: 1615,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1621,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1626,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1629,
        text: 'Five, four, three, two, one... and TIME! Pure class.',
      },
      {
        time: 1636,
        text: "That was some amazing skill! Now for a super fast, super fun team challenge called 'The One-Touch Wall Rally'!",
      },
      {
        time: 1645,
        text: 'For this drill, you two are a team! Stand side-by-side, a few feet apart, and a few big steps from the wall.',
      },
      {
        time: 1653,
        text: "Player A, you'll start by passing the ball against the wall. As it comes back, Player B, you have to hit it back to the wall with one touch!",
      },
      {
        time: 1663,
        text: "Now, Player A, it's your turn! You have to hit the next rebound with one touch. You're working together to keep a rally going. Let's go!",
      },
      {
        time: 1674,
        text: "So it's: Player A hits it, Player B hits it, Player A hits it... Don't let the ball stop!",
      },
      {
        time: 1682,
        text: "Let's start with your right foot only. A one-touch pass with the inside of your right foot. Keep it clean.",
      },
      {
        time: 1690,
        text: "Let's see if you two can get a rally of ten in a row. Count them out loud!",
      },
      {
        time: 1697,
        text: 'One... two... three... keep it going!',
      },
      {
        time: 1702,
        text: 'Four... five... six... great teamwork!',
      },
      {
        time: 1707,
        text: 'Seven... eight... nine... almost there!',
      },
      {
        time: 1712,
        text: "Ten! Awesome job! Let's try for a new record.",
      },
      {
        time: 1718,
        text: 'Remember to stay on your toes. You have to be ready for the ball to come to you.',
      },
      {
        time: 1724,
        text: "This is how you learn to play fast and think fast. You're doing great.",
      },
      {
        time: 1730,
        text: "Two minutes in! Now, let's switch to your left foot only. This is a big challenge!",
      },
      {
        time: 1738,
        text: "It's okay if it's not perfect. Every touch with that foot makes it stronger.",
      },
      {
        time: 1745,
        text: "Let's see if you can get a rally of five with your weak feet. You can do it!",
      },
      {
        time: 1752,
        text: 'Excellent effort! Keep working on it.',
      },
      {
        time: 1757,
        text: "Halfway! Back to your strong feet. Let's see your best one-touch passing.",
      },
      {
        time: 1764,
        text: "Let's try to move a little closer to the wall. This will make the ball come back even faster!",
      },
      {
        time: 1772,
        text: 'Quick feet, quick reactions! You have to be ready.',
      },
      {
        time: 1778,
        text: 'Good! Now take a step back. A little more time, but the passes need to be firmer.',
      },
      {
        time: 1786,
        text: "You're both adapting so well. This is what great players do.",
      },
      {
        time: 1792,
        text: "Let's go for a new record. How many can you get in a row? Go!",
      },
      {
        time: 1799,
        text: "Come on, keep that rally alive! Don't let the ball stop.",
      },
      {
        time: 1805,
        text: "You're a well-oiled machine! Keep that focus.",
      },
      {
        time: 1810,
        text: "Let's see another good streak. Push yourselves.",
      },
      {
        time: 1816,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1820,
        text: "Last minute! Let's see your fastest rally yet. Go, go, go!",
      },
      {
        time: 1826,
        text: 'Thirty seconds left! Empty the tank. Keep that rally going.',
      },
      {
        time: 1832,
        text: "Come on, a few more passes. Don't stop now.",
      },
      {
        time: 1837,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1843,
        text: 'Push it, push it! Almost there!',
      },
      {
        time: 1848,
        text: 'Five, four, three, two, one... and TIME! Awesome reaction speed.',
      },
      {
        time: 1855,
        text: "That was some super-fast passing! For our final drill, it's time for a super fun 1v1 game: 'King of the Ring'!",
      },
      {
        time: 1864,
        text: "Imagine there's a big circle on the ground around you. That's our ring! Player A, you're the attacker first. Player B, you're the defender.",
      },
      {
        time: 1874,
        text: "Attacker, your goal is to use your 'Messi's Glue Foot Dribble' to keep the ball inside the circle for 20 seconds. Defender, your job is to apply light pressure and try to poke the ball out of the ring.",
      },
      {
        time: 1888,
        text: "Defender, don't be too rough, just be a pesky shadow! After 20 seconds, you'll switch roles. Ready, Attacker? Go!",
      },
      {
        time: 1898,
        text: 'Attacker, keep that ball close! Tiny touches. Use your body to shield the ball.',
      },
      {
        time: 1905,
        text: 'Defender, keep your feet moving! Try to get a little poke at the ball.',
      },
      {
        time: 1912,
        text: 'Ten seconds down, ten to go! Keep it in the ring, attacker!',
      },
      {
        time: 1918,
        text: 'Five, four, three, two, one... SWITCH! Great job! Now Player B is the attacker. Go!',
      },
      {
        time: 1928,
        text: 'New attacker, show me that glue foot dribble! Keep it safe.',
      },
      {
        time: 1934,
        text: 'New defender, can you win the ball back? Be quick!',
      },
      {
        time: 1940,
        text: 'Ten seconds down! Keep working hard, both of you.',
      },
      {
        time: 1946,
        text: 'Five, four, three, two, one... SWITCH! Awesome work.',
      },
      {
        time: 1952,
        text: "Two minutes in! Let's make it a little harder. Attacker, you now have to try one of our skill moves to 'beat' the defender.",
      },
      {
        time: 1962,
        text: "Try a 'Ronaldo Sole Roll' or a 'Zidane Spin' to create some space inside the ring. Ready? Go!",
      },
      {
        time: 1971,
        text: 'Attacker, look for your moment! A quick move can give you lots of space.',
      },
      {
        time: 1978,
        text: "Defender, watch their feet! Don't get tricked by the move.",
      },
      {
        time: 1984,
        text: "Ten seconds down! Did you try a move? Let's see it!",
      },
      {
        time: 1990,
        text: 'Five, four, three, two, one... SWITCH! Great skills.',
      },
      {
        time: 1996,
        text: 'New attacker, your turn to be the trickster! Show me a move. Go!',
      },
      {
        time: 2003,
        text: "This is how you learn to use your skills in a real game situation. You're both doing great.",
      },
      {
        time: 2010,
        text: 'Keep that focus. Every second counts.',
      },
      {
        time: 2016,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 2021,
        text: "Halfway! You're both looking like 1v1 masters. Let's go back to just keeping the ball.",
      },
      {
        time: 2029,
        text: "But this time, the ring is smaller! Imagine it's a tiny circle. You have to use even smaller touches. Go!",
      },
      {
        time: 2039,
        text: 'This is the ultimate test of your Messi dribble. Keep it super, super close.',
      },
      {
        time: 2046,
        text: 'Defender, the small space makes it easier for you. Try to win it back.',
      },
      {
        time: 2053,
        text: "Ten seconds down! Keep it in! Don't let it touch the line.",
      },
      {
        time: 2059,
        text: 'Five, four, three, two, one... SWITCH! Awesome control.',
      },
      {
        time: 2065,
        text: 'New attacker, your turn in the tiny ring. Show me that amazing control. Go!',
      },
      {
        time: 2073,
        text: "This is tough, but you're both working so hard. This is how you get better.",
      },
      {
        time: 2080,
        text: 'Keep that focus. Every touch is important.',
      },
      {
        time: 2086,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 2091,
        text: 'Last minute! Back to the big ring. Your choice, attacker! Just keep the ball, or try a fancy move. Go!',
      },
      {
        time: 2100,
        text: "This is your time to shine. Show me everything you've learned.",
      },
      {
        time: 2106,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 2112,
        text: 'Faster feet, quicker turns! Come on!',
      },
      {
        time: 2117,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 2123,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 2128,
        text: 'Get ready for the countdown!',
      },
      {
        time: 2131,
        text: 'Five, four, three, two, one... and TIME! Great 1v1 battle.',
      },
      {
        time: 2139,
        text: "Fantastic work today, superstars! You both completed the whole drill! Give your partner a huge high-five and tell them 'Great job!'",
      },
      {
        time: 2150,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take a big, deep breath in... and out.",
      },
      {
        time: 2160,
        text: "You both worked on passing, dribbling, turning, teamwork, and 1v1 skills today. You're becoming true all-around players. Amazing job. Drill complete!",
      },
    ],
  },
  {
    id: 2180,
    name: 'Intermediate Ultimate Partner Drill',
    categories: [
      'Passing',
      'Ball Control',
      'Wall Drill',
      'Dribbling',
      'Tricks',
      'Combination Play',
    ],
    sports: ['soccer'],
    difficulty: 'Intermediate',
    skill:
      'A complete range of intermediate skills including passing, dribbling, wall combinations, and creative tricks.',
    instructions:
      'This 45-minute session is the ultimate partner workout, combining fundamental passing and wall work with the creative flair of players like Messi, Ronaldo, Zidane, and Neymar. Get ready to sharpen your skills and have some fun!',
    equipment: '1 soccer ball, 1 wall',
    timeTotal: 2700,
    timedInstructions: [
      {
        time: 0,
        text: "Alright team, welcome to the ultimate workout! Let's start with a 'Dynamic Warm-up Pass'. Stand ten steps apart and let's get moving. Go!",
      },
      {
        time: 9,
        text: 'Start with simple two-touch passing. Control the foam ball with a soft touch, then pass it back. But after you pass, you must jog to a new spot. No standing still!',
      },
      {
        time: 20,
        text: "Your partner has to pass to the new space you've moved to. This is all about movement and awareness from the very start.",
      },
      {
        time: 29,
        text: "Let's see it. Pass, then move! Find a new window for your partner to pass through.",
      },
      {
        time: 36,
        text: 'Remember, the foam ball is light, so your passes need to be crisp and your first touch has to be extra soft.',
      },
      {
        time: 44,
        text: 'Keep that rhythm going. Control, pass, and move to a new spot. Excellent.',
      },
      {
        time: 51,
        text: "One minute in! You're both looking sharp. Keep that energy high.",
      },
      {
        time: 57,
        text: "Let's see five good passes each while you keep moving. You can do it.",
      },
      {
        time: 63,
        text: 'The player without the ball should be on their toes, ready to move to receive the pass.',
      },
      {
        time: 70,
        text: 'Awesome! Keep that continuous flow. This is what real soccer looks like.',
      },
      {
        time: 77,
        text: "Let's focus on your movement. After you pass, make a quick little sprint to a new spot.",
      },
      {
        time: 84,
        text: "That's it! That change of speed is what creates space in a game.",
      },
      {
        time: 90,
        text: "Two minutes in! Let's go to one-touch passing. The movement off the ball has to be even quicker now. Read your partner and the space.",
      },
      {
        time: 100,
        text: 'This is a big challenge! As the ball is coming, move to it and pass it back with your first touch.',
      },
      {
        time: 108,
        text: "It's all about timing and teamwork. You have to trust your partner.",
      },
      {
        time: 114,
        text: "Let's see if you can get a rally of five one-touch passes in a row. Go for it!",
      },
      {
        time: 121,
        text: 'Keep those feet active. You have to be ready to move and pass at any moment.',
      },
      {
        time: 128,
        text: "You're both doing great. This is a very advanced skill.",
      },
      {
        time: 134,
        text: "Halfway! Let's go back to two-touch to get our rhythm back. Control, pass, and move.",
      },
      {
        time: 142,
        text: "Let's focus on the quality of the pass. Make it easy for your partner to play one-touch if they can.",
      },
      {
        time: 150,
        text: "A good pass is a gift to your teammate. Let's see some great passes.",
      },
      {
        time: 157,
        text: "Now, let's try one-touch again. Let's see if it feels any easier.",
      },
      {
        time: 163,
        text: 'Keep that rally going! How many can you get?',
      },
      {
        time: 168,
        text: "Remember to communicate! A little shout of 'Here!' helps your partner know where you are.",
      },
      {
        time: 176,
        text: "Excellent teamwork. You're looking like a real partnership.",
      },
      {
        time: 182,
        text: "Keep that focus. We're in the final stretch of this warm-up.",
      },
      {
        time: 188,
        text: "Let's see one more perfect rally. Make it your best one yet.",
      },
      {
        time: 194,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 198,
        text: "Last minute! Let's see some creative movement. Curved runs, checking away and coming back. Make your partner's pass easy.",
      },
      {
        time: 208,
        text: 'Your choice now: one-touch or two-touch. Just keep the ball moving and keep yourselves moving.',
      },
      {
        time: 216,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 222,
        text: 'Faster movement, sharper passes! Come on!',
      },
      {
        time: 227,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 233,
        text: 'One more perfect pass from each of you!',
      },
      {
        time: 238,
        text: 'Get ready for the countdown!',
      },
      {
        time: 241,
        text: "Great warm-up! Let's get straight to the wall for a test of rhythm and control called 'One-Touch & Control'!",
      },
      {
        time: 250,
        text: "Player 1, you're up first at the wall. Player 2, you're the relay partner standing behind them. Here's the challenge: Player 1, you have to do five fast one-touch passes against the wall.",
      },
      {
        time: 263,
        text: "On the fifth rebound, you'll control it with one soft touch, turn, and pass it to Player 2. Then you switch spots! Player 2, you'll do the same. Ready? Go!",
      },
      {
        time: 276,
        text: "Let's see it, Player 1! One, two, three, four, five... now control, turn, and pass! Quick switch!",
      },
      {
        time: 286,
        text: 'Player 2, your turn! Five fast passes... control, turn, and pass! Keep that relay going!',
      },
      {
        time: 296,
        text: 'This drill is all about changing your speed. Super fast, then super calm and controlled. Just like the pros.',
      },
      {
        time: 305,
        text: "One minute in! Your transitions are looking good. Let's focus on that control touch. It has to be perfect.",
      },
      {
        time: 314,
        text: 'After those fast passes, your brain is going a million miles an hour. Calm it down for that one soft touch.',
      },
      {
        time: 323,
        text: "Let's see it again. Fast, fast, fast... and a soft pillow touch to control. Awesome.",
      },
      {
        time: 331,
        text: 'Keep that quality high. Every pass, every touch.',
      },
      {
        time: 337,
        text: 'Two minutes down. Now for a big challenge. The five one-touch passes must be with your WEAK FOOT! Your other foot.',
      },
      {
        time: 348,
        text: "This is tough, but it's how you become unstoppable. Let's see it. Five with the weak foot... control, turn, and pass.",
      },
      {
        time: 358,
        text: "Excellent effort! Now the other player tries. This is hard work, but it's worth it.",
      },
      {
        time: 366,
        text: 'Keep that focus. A good pass to the wall makes your job easier.',
      },
      {
        time: 373,
        text: "Halfway! Back to your strong foot. Let's make the control touch a directional touch.",
      },
      {
        time: 381,
        text: 'So after your five one-touch passes, your control touch should take the ball to the side, into space, before you turn and pass.',
      },
      {
        time: 391,
        text: "This is a great way to escape a defender who is closing you down. Let's see that escape touch.",
      },
      {
        time: 400,
        text: 'Five fast passes... now a sharp touch to the side! Excellent.',
      },
      {
        time: 407,
        text: "Keep switching roles. You're both doing an amazing job with this advanced skill.",
      },
      {
        time: 415,
        text: "Let's see another one. Make that directional touch even sharper.",
      },
      {
        time: 422,
        text: "You're not just controlling the ball; you're creating space. That's superstar thinking.",
      },
      {
        time: 430,
        text: "Keep that energy high. We're in the final stretch of this drill.",
      },
      {
        time: 436,
        text: "Let's see one more perfect rep. Five fast passes, a sharp directional touch, a turn, and a perfect pass.",
      },
      {
        time: 446,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 450,
        text: "Last minute! Let's see how clean and efficient you can be. Your choice: simple control or directional control. But make it fast!",
      },
      {
        time: 460,
        text: "The transition between you two should be instant. As soon as you pass, you're moving.",
      },
      {
        time: 468,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 474,
        text: 'Faster passes, quicker switches! Come on!',
      },
      {
        time: 479,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 485,
        text: 'One more perfect rep from each of you!',
      },
      {
        time: 490,
        text: 'Get ready for the countdown!',
      },
      {
        time: 493,
        text: 'Five, four, three, two, one... and TIME! Great rhythm.',
      },
      {
        time: 500,
        text: "That was some high-speed work! Now it's time for some flair with the 'Messi Dribble Challenge'! You'll both need a ball for this one.",
      },
      {
        time: 509,
        text: "Find your own space. For the next 60 seconds, your only goal is to keep the ball as close as possible. Tiny touches, using the inside and outside of both feet. Imagine it's glued to you! Go!",
      },
      {
        time: 522,
        text: 'Keep your head up and be aware of your partner. Try not to bump into them! This is great practice for dribbling in a crowd.',
      },
      {
        time: 531,
        text: "Let's see those tiny touches. Tap, tap, tap. The ball should never be more than a step away from you.",
      },
      {
        time: 539,
        text: "Thirty seconds down! Keep that ball on a string. You're both looking like Messi out there!",
      },
      {
        time: 546,
        text: "Let's see a little burst of speed, then bring it right back under control. Good.",
      },
      {
        time: 553,
        text: "Final fifteen seconds of this first minute. Push the pace a little, but don't lose that control.",
      },
      {
        time: 560,
        text: 'Five, four, three, two, one... and time! Great start.',
      },
      {
        time: 566,
        text: "Now, for the next minute, your partner is a 'passive defender'. Player A, you're the dribbler. Player B, just jog slowly around Player A. Don't try to take the ball.",
      },
      {
        time: 578,
        text: 'Player A, your job is to keep the ball away from your partner using your close control. Use your body to shield the ball. Go!',
      },
      {
        time: 587,
        text: 'Keep your head up to see where the defender is. A quick peek is all you need.',
      },
      {
        time: 594,
        text: 'Use little fakes with your body to trick them. Dip your shoulder one way, go the other.',
      },
      {
        time: 601,
        text: "Thirty seconds down. Keep that ball safe! Don't let them get a touch.",
      },
      {
        time: 607,
        text: 'Final ten seconds for Player A. Keep it close!',
      },
      {
        time: 612,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 617,
        text: "Great work! Player B, you're the dribbler now. Player A, you're the passive defender. Go!",
      },
      {
        time: 625,
        text: 'Player B, show me that glue foot dribble! Protect that ball.',
      },
      {
        time: 631,
        text: 'Player A, keep that pressure light, but be a good defender. Stay on your toes.',
      },
      {
        time: 638,
        text: 'Thirty seconds down for Player B. Keep that ball moving and protected.',
      },
      {
        time: 644,
        text: "Final ten seconds. Don't let them get it!",
      },
      {
        time: 649,
        text: 'Five, four, three, two, one... TIME!',
      },
      {
        time: 654,
        text: "Awesome! Halfway through this drill. Now, you're both dribbling in your own space again.",
      },
      {
        time: 661,
        text: 'This time, I want you to practice changing direction sharply. Dribble forward, then a quick cut to the left. Then a quick cut to the right.',
      },
      {
        time: 671,
        text: "Use the outside of your foot for those sharp cuts. It's the fastest way to turn.",
      },
      {
        time: 678,
        text: "Let's see it. Dribble, cut! Dribble, cut! Be explosive.",
      },
      {
        time: 685,
        text: 'Keep your head up so you can see the space you want to cut into.',
      },
      {
        time: 692,
        text: "You're both looking so sharp. This is how you create space in a game.",
      },
      {
        time: 699,
        text: "Let's see a few more quick cuts. Make them unpredictable.",
      },
      {
        time: 705,
        text: "Last minute! For the next 30 seconds, Player A, you're the dribbler. Player B, you're the defender again, but this time you can TRY to take the ball! 1v1!",
      },
      {
        time: 717,
        text: "Player A, use your Messi dribble and your sharp cuts to protect the ball. Don't let them win it! Go!",
      },
      {
        time: 725,
        text: 'Good shielding! Keep your body between the defender and the ball.',
      },
      {
        time: 731,
        text: 'Ten seconds left! Keep it safe!',
      },
      {
        time: 736,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 741,
        text: "Final 30 seconds! Player B, you're the attacker. Player A, you're defending. Go!",
      },
      {
        time: 749,
        text: 'Come on, attacker, show me your skills! Can you beat the defender?',
      },
      {
        time: 755,
        text: "Defender, stay on your toes! Don't make it easy.",
      },
      {
        time: 761,
        text: 'Ten seconds left! Final push!',
      },
      {
        time: 766,
        text: 'Five, four, three, two, one... and TIME! Silky skills.',
      },
      {
        time: 772,
        text: "That was an amazing dribbling challenge! Now for some power and deception with 'Ronaldo Chops & Pass'!",
      },
      {
        time: 780,
        text: "Player A, you're the attacker. Dribble with some speed towards Player B, who will be our friendly defender.",
      },
      {
        time: 788,
        text: "When you get close, perform a 'Ronaldo Chop'. While you're running, use the inside of your right foot to sharply chop the ball behind your standing left leg.",
      },
      {
        time: 799,
        text: "This move changes your direction instantly! As soon as you land the chop, play a firm pass to your partner. Then it's their turn to try. Let's go!",
      },
      {
        time: 810,
        text: "So it's: Dribble, chop, pass! And then you switch roles. Keep it moving.",
      },
      {
        time: 817,
        text: "Let's try it with the other foot. Dribble in, and chop with your left foot behind your right. Be a two-footed player!",
      },
      {
        time: 826,
        text: 'The chop has to be sharp and quick to surprise the defender. Make it snappy!',
      },
      {
        time: 833,
        text: 'One minute in! Your chops are looking powerful. Remember to sell the fake.',
      },
      {
        time: 840,
        text: "Before you chop, pretend you're going to shoot or pass forward. A little body fake makes the move unstoppable.",
      },
      {
        time: 849,
        text: "Let's see that fake now. Dribble in, fake the shot, then CHOP! Excellent!",
      },
      {
        time: 856,
        text: 'Keep switching roles after every turn. Both of you need to master this move.',
      },
      {
        time: 863,
        text: 'The pass after the chop should be quick. Chop, and then a one-touch pass if you can.',
      },
      {
        time: 871,
        text: 'This is how you create chances in a real game. A quick move and a quick pass.',
      },
      {
        time: 878,
        text: "Let's see another one. Make it your best one yet. Sharp, fast, and powerful.",
      },
      {
        time: 885,
        text: "Two minutes in! Let's try the chop with both your right and left foot. It's important to be able to go both ways.",
      },
      {
        time: 894,
        text: "Let's see a right-footed chop. Good. Now a left-footed chop. Awesome.",
      },
      {
        time: 901,
        text: "The defender will never know which way you're going!",
      },
      {
        time: 907,
        text: 'Keep that quality high. Every chop, every pass.',
      },
      {
        time: 912,
        text: "Let's see another one. Dribble with a bit more speed this time.",
      },
      {
        time: 918,
        text: 'The faster you dribble, the more effective the chop is. Great work.',
      },
      {
        time: 924,
        text: "Halfway! Let's focus on the explosion after the move.",
      },
      {
        time: 930,
        text: 'After you chop the ball, your first step should be a powerful push-off. Explode into the new direction.',
      },
      {
        time: 939,
        text: "Let's see that power. Chop and accelerate! Good.",
      },
      {
        time: 945,
        text: 'This is how you create that half-second of space you need to make a pass.',
      },
      {
        time: 952,
        text: "Let's see three good chops in a row with that explosive first step.",
      },
      {
        time: 958,
        text: "You're both looking like superstars. Keep it up.",
      },
      {
        time: 963,
        text: 'Remember to keep your head up. After the chop, you need to see your partner to make the pass.',
      },
      {
        time: 971,
        text: "This is a brainy move too. You have to think about your next move before you've even finished the first one.",
      },
      {
        time: 980,
        text: "Let's see another one. Dribble, chop, and a perfect pass.",
      },
      {
        time: 986,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 990,
        text: "Last minute! Let's see some explosion. The chop should be fast and the pass should be immediate. Chop, pass. No extra touches.",
      },
      {
        time: 1000,
        text: "This is game speed now. Imagine you're in a big match.",
      },
      {
        time: 1006,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1012,
        text: 'Faster, sharper, more explosive! Come on!',
      },
      {
        time: 1017,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1023,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1028,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1031,
        text: 'Five, four, three, two, one... and TIME! Great power.',
      },
      {
        time: 1038,
        text: "That was some amazing skill! Now for some magic with the 'Zidane Turn & Wall Escape'!",
      },
      {
        time: 1046,
        text: "This is a high-level combination. Player A, you'll pass to Player B. Player B, you're going to receive the pass and immediately do a Zidane turn to spin away from your partner.",
      },
      {
        time: 1057,
        text: "Remember, that's the drag-back with the sole of your foot while you spin your body. As you complete the spin, you should be facing the wall.",
      },
      {
        time: 1066,
        text: "Then, play a quick one-touch pass off the wall to yourself to 'escape' into space. After you control it, you become the passer for Player A. Let's go!",
      },
      {
        time: 1078,
        text: "So it's: Receive, Zidane turn, wall pass, control, then switch! It's a continuous flow.",
      },
      {
        time: 1087,
        text: "Let's see it again. A good pass, a smooth spin, and a clever wall pass to escape. Awesome!",
      },
      {
        time: 1095,
        text: "Let's try it with the other foot. Receive, and do a Zidane turn with your left foot. This is a big challenge!",
      },
      {
        time: 1104,
        text: "You're both two-footed magicians! Keep switching after every turn.",
      },
      {
        time: 1111,
        text: 'One minute in! Your spins are looking great. The pass off the wall needs to be at a good angle so you can run onto it.',
      },
      {
        time: 1120,
        text: "Let's see five good turns each. Keep that quality high.",
      },
      {
        time: 1126,
        text: 'The flow is crucial. The turn should lead you perfectly into your pass off the wall. It should be one fluid motion.',
      },
      {
        time: 1135,
        text: "You're doing great. This is how you create space when a defender is tight on your back.",
      },
      {
        time: 1142,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 1148,
        text: "Excellent! You're looking like a pro.",
      },
      {
        time: 1153,
        text: "Two minutes in! Let's focus on the speed of the turn. It has to be quick to fool the defender.",
      },
      {
        time: 1161,
        text: "A faster spin makes the move much more effective. Let's see that speed.",
      },
      {
        time: 1167,
        text: "That's it! Now the wall pass has to be just as quick. One touch!",
      },
      {
        time: 1173,
        text: 'Remember to keep your body low during the spin. It helps you stay balanced.',
      },
      {
        time: 1180,
        text: "Let's see five good ones in a row from each of you at this new speed.",
      },
      {
        time: 1187,
        text: "You're both looking like superstars. Keep it up.",
      },
      {
        time: 1192,
        text: "Halfway! Let's make the first touch even better. As the pass comes from your partner, your first touch should be the start of the Zidane turn.",
      },
      {
        time: 1202,
        text: "So instead of stopping it, you're going to let the ball roll across your body and use its momentum to go right into the drag-back and spin. It's all one motion!",
      },
      {
        time: 1214,
        text: "This is a very high-level skill. Let's see you try it. A flowing first touch right into the spin.",
      },
      {
        time: 1222,
        text: "Awesome! That was so smooth. It's almost impossible to defend.",
      },
      {
        time: 1228,
        text: 'Keep practicing that. The more you do it, the more natural it will feel.',
      },
      {
        time: 1234,
        text: "Let's see another one with that flowing first touch. Make it look easy.",
      },
      {
        time: 1241,
        text: "Excellent! You're a true magician.",
      },
      {
        time: 1246,
        text: 'Keep that focus. Every repetition makes you better.',
      },
      {
        time: 1252,
        text: "Let's see one more perfect rep. A flowing turn and a clever escape.",
      },
      {
        time: 1259,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1263,
        text: "Last minute! Let's see the elegance of Zidane. A smooth turn and a clever escape. Game speed now!",
      },
      {
        time: 1271,
        text: "Imagine you're in a big game. This is the move that creates the winning goal.",
      },
      {
        time: 1278,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1284,
        text: 'Faster, smoother, sharper! Come on!',
      },
      {
        time: 1289,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1295,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1300,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1303,
        text: 'Five, four, three, two, one... and TIME! Pure class.',
      },
      {
        time: 1310,
        text: "That was some amazing skill! Now for some Brazilian flair with the 'Neymar Flick & Control'!",
      },
      {
        time: 1318,
        text: "This is a super fun and tricky move! Player 1, you're the server. Player 2, you're Neymar. Server, gently roll the ball to your partner.",
      },
      {
        time: 1327,
        text: "Player 2, as the ball arrives, your job is to use your foot to flick it up and over your own head. It's a rainbow flick!",
      },
      {
        time: 1336,
        text: "To do it, you have to roll the ball up your leg a little and use your heel to flick it. Let's practice just that motion without the roll first. Just try to flick it up.",
      },
      {
        time: 1346,
        text: "After you flick it over yourself, you must turn and control it before passing it back. This is all about touch and timing. Let's try it for real. Server, roll the ball.",
      },
      {
        time: 1358,
        text: "Good try! This is one of the hardest tricks in soccer, so don't worry if you don't get it right away. Let's switch roles.",
      },
      {
        time: 1367,
        text: "New Neymar, let's see your flick! Remember to lean forward a little bit. It helps get the ball up.",
      },
      {
        time: 1375,
        text: "One minute in! You're both doing great. Just attempting this move is making you a better player.",
      },
      {
        time: 1383,
        text: "Let's focus on the roll up your leg. It's a quick little scoop with your toes.",
      },
      {
        time: 1390,
        text: "Now the flick with your heel. It's a quick snap. Scoop and snap!",
      },
      {
        time: 1397,
        text: "Let's see it again. Don't give up! Every try gets you closer.",
      },
      {
        time: 1403,
        text: 'Awesome! That was a great flick! Now remember to turn and control it.',
      },
      {
        time: 1410,
        text: 'Keep switching roles every few tries. You both need to become masters of this move.',
      },
      {
        time: 1417,
        text: "Two minutes in! Let's try to get the ball a little higher now. A bigger flick.",
      },
      {
        time: 1424,
        text: 'The key is to get it just over your head, not too high.',
      },
      {
        time: 1430,
        text: "Let's see that perfect height. A nice little rainbow.",
      },
      {
        time: 1436,
        text: "Excellent! You're starting to get the feel for it.",
      },
      {
        time: 1442,
        text: 'Keep that focus. This move is all about practice, practice, practice.',
      },
      {
        time: 1449,
        text: "Halfway! Let's just focus on the flick itself for a minute. Don't worry about controlling it after.",
      },
      {
        time: 1457,
        text: "Just get that satisfying pop over your head. Let's see who can get the highest flick.",
      },
      {
        time: 1465,
        text: "Good one! Now the other player. Let's see your power.",
      },
      {
        time: 1471,
        text: "Now let's see who can get the most stylish flick. Add a little flair to it.",
      },
      {
        time: 1478,
        text: "You're both looking like freestyle superstars. Keep it up.",
      },
      {
        time: 1484,
        text: 'Okay, back to the full move. Flick, turn, and control.',
      },
      {
        time: 1490,
        text: "Let's see if you can do it a little faster now. A quick turn after the flick.",
      },
      {
        time: 1497,
        text: "The faster you turn, the more you'll surprise the defender.",
      },
      {
        time: 1503,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 1509,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1513,
        text: "Last minute! Let's see your best Neymar flick. Show me that Brazilian magic!",
      },
      {
        time: 1520,
        text: "Imagine you're in a big game, and you pull off this incredible move. Let's see it.",
      },
      {
        time: 1528,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1534,
        text: 'Faster, higher, more stylish! Come on!',
      },
      {
        time: 1539,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1545,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1550,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1553,
        text: 'Five, four, three, two, one... and TIME! Great creativity.',
      },
      {
        time: 1560,
        text: "That was some amazing skill! Now, let's get back to some fundamental teamwork with the 'Combination Give-and-Go'!",
      },
      {
        time: 1568,
        text: "This is all about non-stop movement and quick passes. Player A, you'll pass to Player B and then immediately sprint to a new open space.",
      },
      {
        time: 1577,
        text: "Player B, you'll play a one-touch pass right back to Player A. After Player A controls it, it's their turn to pass to you and you make the run! It's a continuous give-and-go. Go!",
      },
      {
        time: 1589,
        text: "So it's: Pass, sprint, receive. Then you become the passer! Keep that flow going.",
      },
      {
        time: 1596,
        text: "The passes must be perfectly weighted, leading your partner into their run. Don't pass it behind them.",
      },
      {
        time: 1604,
        text: 'And the run has to be a real sprint! Explode into that open space.',
      },
      {
        time: 1610,
        text: "One minute in! Your movement is looking great. Let's focus on your communication.",
      },
      {
        time: 1617,
        text: 'As you run, point to the space where you want the ball. Help your partner out!',
      },
      {
        time: 1624,
        text: "Let's see that pointing now. Good pass, good run, good point! Excellent.",
      },
      {
        time: 1631,
        text: 'This is how you and your partner get on the same page. Great teamwork.',
      },
      {
        time: 1637,
        text: 'Keep that quality high. Every pass, every run.',
      },
      {
        time: 1642,
        text: "Let's see another good one. Make it your best one yet.",
      },
      {
        time: 1647,
        text: "Awesome. You're looking like a real dynamic duo.",
      },
      {
        time: 1652,
        text: "Two minutes in! Let's make the run a little more creative. Try a curved run into space.",
      },
      {
        time: 1660,
        text: 'This makes it harder for the defender to follow you. The passer has to adjust their pass.',
      },
      {
        time: 1667,
        text: "Let's see that curved run. Good! And a perfect pass to match.",
      },
      {
        time: 1673,
        text: 'Now the other partner. Show me a creative run.',
      },
      {
        time: 1678,
        text: "You're both thinking like smart soccer players. Keep it up.",
      },
      {
        time: 1684,
        text: "Halfway! Let's focus on the one-touch pass. It has to be soft and accurate.",
      },
      {
        time: 1691,
        text: "Imagine you're just cushioning the ball right into your partner's path. Be a pillow.",
      },
      {
        time: 1698,
        text: "Let's see five perfect one-touch passes in a row. You can do it.",
      },
      {
        time: 1704,
        text: "Excellent! You're making it look easy.",
      },
      {
        time: 1709,
        text: 'Keep that energy high. This is a great workout.',
      },
      {
        time: 1714,
        text: "Let's see another good run. Where are you going to go?",
      },
      {
        time: 1719,
        text: 'Great choice! And a perfect pass to reward the run.',
      },
      {
        time: 1724,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1730,
        text: "Let's see one more perfect rep. Pass, run, receive. Beautiful.",
      },
      {
        time: 1736,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1740,
        text: "Last minute! Let's see this at game speed. This is how you break down a defense with quick, intelligent passing and movement.",
      },
      {
        time: 1750,
        text: "Imagine you're in a big game. This is the winning play!",
      },
      {
        time: 1756,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1762,
        text: 'Faster runs, sharper passes! Come on!',
      },
      {
        time: 1767,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1773,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1778,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1781,
        text: 'Five, four, three, two, one... and TIME! Awesome teamwork.',
      },
      {
        time: 1788,
        text: "That was some incredible movement! Now for a high-pressure drill called 'The Pressure Box'!",
      },
      {
        time: 1796,
        text: "This is a super fun 1v1 game. Imagine a small box around you two. Player A, you're the attacker with the ball. Player B, you're the defender.",
      },
      {
        time: 1806,
        text: "Attacker, your job is to protect the ball inside the box for 45 seconds. Defender, you're going to apply real pressure and try to win the ball!",
      },
      {
        time: 1816,
        text: "Here's the special rule: Attacker, you can use the wall as your teammate! If you're in trouble, play a quick pass off the wall to yourself to escape. Ready? Go!",
      },
      {
        time: 1828,
        text: 'Attacker, be strong! Use your body to shield the ball. Keep it moving.',
      },
      {
        time: 1834,
        text: "Defender, stay on your toes! Be a pesky shadow. Don't make it easy.",
      },
      {
        time: 1840,
        text: 'Attacker, use the wall! A quick one-two off the wall can get you out of trouble.',
      },
      {
        time: 1847,
        text: 'Fifteen seconds down! Keep that ball safe, attacker!',
      },
      {
        time: 1852,
        text: 'Defender, keep that pressure high. Good work.',
      },
      {
        time: 1857,
        text: 'Thirty seconds down! Attacker, show me a good turn to protect the ball.',
      },
      {
        time: 1863,
        text: "Final ten seconds for this round! Don't lose it now!",
      },
      {
        time: 1868,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 1873,
        text: 'Great job! Now Player B is the attacker. Player A is the defender. 45 seconds on the clock. Go!',
      },
      {
        time: 1882,
        text: 'New attacker, protect that ball! Use your body, use your skills.',
      },
      {
        time: 1888,
        text: 'New defender, can you win it back? Show me that hustle.',
      },
      {
        time: 1894,
        text: "Attacker, don't forget your teammate, the wall! A quick pass can save you.",
      },
      {
        time: 1901,
        text: 'Fifteen seconds down! Keep that ball moving.',
      },
      {
        time: 1906,
        text: 'Thirty seconds down! Show me a clever move to create some space.',
      },
      {
        time: 1912,
        text: 'Final ten seconds! Keep fighting for that ball.',
      },
      {
        time: 1917,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 1922,
        text: "Awesome work! Back to our first attacker. Halfway through the drill! Let's up the challenge.",
      },
      {
        time: 1930,
        text: 'Attacker, this time you MUST use the wall at least twice to keep possession. You have to think like a playmaker. 45 seconds. Go!',
      },
      {
        time: 1941,
        text: 'Look for your moments to use the wall. A quick one-two can beat the defender instantly.',
      },
      {
        time: 1948,
        text: "That's one! Good. Now keep it and look for your second wall pass.",
      },
      {
        time: 1954,
        text: 'Defender, try to read the wall pass and intercept it!',
      },
      {
        time: 1960,
        text: "That's two! You did it! Now just protect the ball.",
      },
      {
        time: 1965,
        text: "Final ten seconds! Don't let them get it.",
      },
      {
        time: 1970,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 1975,
        text: 'Fantastic! New attacker, your turn for the two-wall-pass challenge. 45 seconds. Go!',
      },
      {
        time: 1984,
        text: 'Be smart. Use your body to create an angle for the wall pass.',
      },
      {
        time: 1990,
        text: "That's one! Good. Now keep possession and find your next one.",
      },
      {
        time: 1996,
        text: 'Defender, can you block that angle to the wall? Good defending.',
      },
      {
        time: 2002,
        text: "That's two! Awesome job. Now just keep it safe.",
      },
      {
        time: 2007,
        text: "Final ten seconds! You've got this.",
      },
      {
        time: 2012,
        text: 'Five, four, three, two, one... TIME!',
      },
      {
        time: 2017,
        text: 'Last minute of this drill. Back to our first attacker. The rule is simple: keep the ball! 30 seconds. Go!',
      },
      {
        time: 2026,
        text: "This is where champions are made. Can you handle the pressure when you're tired?",
      },
      {
        time: 2032,
        text: 'Ten seconds left! Keep it, keep it, keep it!',
      },
      {
        time: 2037,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 2042,
        text: 'Final 30 seconds of the drill! Last attacker, show me your best stuff. Go!',
      },
      {
        time: 2050,
        text: "Come on, empty the tank! Protect that ball with everything you've got.",
      },
      {
        time: 2056,
        text: 'Ten seconds left! Final push!',
      },
      {
        time: 2061,
        text: 'Five, four, three, two, one... and TIME! Great strength.',
      },
      {
        time: 2068,
        text: "That was some incredible work under pressure! For our final drill, it's time to put everything together in the 'Freestyle Finale'! Everyone start with your own ball in front of you.",
      },
      {
        time: 2077,
        text: "This is your stage. For the last five minutes, you and your partner will combine everything you've learned. Use the wall, use your tricks, use your combinations. The only rule is to be creative and keep the ball moving!",
      },
      {
        time: 2089,
        text: "Let's start with a Combination Give-and-Go. Pass, move, and get that one-touch pass back. Go!",
      },
      {
        time: 2097,
        text: 'Good. Now, the player with the ball, dribble at your partner and show me a Ronaldo Chop! Then a sharp pass.',
      },
      {
        time: 2106,
        text: 'Excellent! Now the other player. Receive it and go right into a Zidane Turn & Wall Escape. Turn, use the wall, and get away!',
      },
      {
        time: 2116,
        text: "You're linking the moves together! This is what the best players do. Keep that flow going.",
      },
      {
        time: 2124,
        text: "Let's see some of that Messi Dribble now. Keep it close, keep it tight.",
      },
      {
        time: 2131,
        text: 'Now, serve it up for a Neymar Flick & Control. A little flair!',
      },
      {
        time: 2138,
        text: "Awesome! You're using your whole toolbox of skills.",
      },
      {
        time: 2144,
        text: "One minute in! Let's see a sequence. A Dynamic Pass to your partner, who controls it and goes into The Pressure Box drill. Shield it!",
      },
      {
        time: 2155,
        text: 'Good strength! Now use the wall to escape and find your partner with a pass.',
      },
      {
        time: 2202,
        text: "You're not just doing tricks; you're solving problems like in a real game. Keep it up.",
      },
      {
        time: 2210,
        text: "Let's see another creative combo. Your choice this time. Surprise me!",
      },
      {
        time: 2217,
        text: 'Excellent! The more you mix up your moves, the more unpredictable you are.',
      },
      {
        time: 2224,
        text: "Two minutes in! Let's challenge your weak foot. Can you do a Ronaldo Chop with your other foot?",
      },
      {
        time: 2232,
        text: 'Good effort! Now a Zidane Turn with your other foot.',
      },
      {
        time: 2238,
        text: "The more you use that foot, the more confident you'll become.",
      },
      {
        time: 2244,
        text: 'Back to your strong foot. Show me your favorite combination of the day.',
      },
      {
        time: 2251,
        text: 'Make it sharp, make it fast, make it clean.',
      },
      {
        time: 2256,
        text: "Halfway! Keep that quality high, even when you're getting tired.",
      },
      {
        time: 2262,
        text: "Let's see a One-Touch & Control sequence. Five fast passes off the wall, then find your partner.",
      },
      {
        time: 2271,
        text: 'Good speed! Now the other partner tries. Keep that tempo high.',
      },
      {
        time: 2278,
        text: "You're both working so hard. This is how you become champions.",
      },
      {
        time: 2284,
        text: "Let's see another creative link. Maybe a Zidane Turn into a pass for a Neymar Flick?",
      },
      {
        time: 2292,
        text: 'Awesome idea! Keep thinking like a creative player.',
      },
      {
        time: 2298,
        text: "Keep that focus. We're in the final stretch.",
      },
      {
        time: 2303,
        text: "Let's see one more perfect combination. Make it your best one yet.",
      },
      {
        time: 2310,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 2314,
        text: 'Last minute! Empty the tank! Show me your most creative, most amazing combination of the day.',
      },
      {
        time: 2322,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 2327,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 2333,
        text: "Thirty seconds! Let's see a masterpiece!",
      },
      {
        time: 2338,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 2343,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 2348,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 2353,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND THAT'S A WRAP! AMAZING SESSION!",
      },
      {
        time: 2363,
        text: "That was an incredible 45 minutes. You've worked on every part of your game. Give your partner a huge high-five. You both crushed it.",
      },
      {
        time: 2374,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take a big, deep breath in... and out.",
      },
      {
        time: 2384,
        text: "You worked on passing, dribbling, turning, teamwork, and some amazing tricks today. You're becoming true all-around players. Amazing job. Drill complete!",
      },
    ],
  },
  {
    id: 2181,
    name: 'Advanced Ultimate Partner Drill',
    categories: [
      'Passing',
      'Ball Control',
      'Wall Drill',
      'Dribbling',
      'Tricks',
      'Tactical',
    ],
    sports: ['soccer'],
    difficulty: 'Advanced',
    skill:
      'An elite, all-around session focusing on speed, deception, and tactical combinations.',
    instructions:
      "This 45-minute session is the ultimate test for advanced partners, blending high-speed technical drills with the creative genius of the world's best players. Prepare for a high-intensity workout that will push your skill, speed, and soccer IQ to the limit.",
    equipment: '1 soccer ball, 1 wall',
    timeTotal: 2700,
    timedInstructions: [
      {
        time: 0,
        text: "Alright team, let's raise the level! Welcome to the advanced drill. First up: 'Dynamic One-Touch'. Stand about ten big steps apart. High tempo from the first whistle. Go!",
      },
      {
        time: 11,
        text: 'The ball cannot stop moving in this drill. Every single pass is one-touch. As soon as you pass, you have to move. Explode into a new space!',
      },
      {
        time: 22,
        text: "Your movement tells your partner where to pass. Don't just run anywhere. Make a sharp, quick cut to your left or right. Your partner has to read it and play the ball to your new spot.",
      },
      {
        time: 34,
        text: 'The pass has to be perfect: firm, on the ground, and right in front of your partner so they can play it back with one touch. Go!',
      },
      {
        time: 44,
        text: "Think like a superstar. As you're moving to the ball, you should already know where your next pass is going.",
      },
      {
        time: 52,
        text: "Let's vary the movement. A short zig, a little zag, a curved run. Be unpredictable like Messi!",
      },
      {
        time: 61,
        text: 'This is all about having a fast brain and fast feet. Read the game, play the pass, and move!',
      },
      {
        time: 69,
        text: "Let's demand quality. If the pass isn't perfect, or the movement is lazy, we start our count over. Let's try to get ten perfect one-touch passes in a row.",
      },
      {
        time: 82,
        text: 'One... good pass. Two... great movement. Three... keep it going!',
      },
      {
        time: 89,
        text: "Four... sharp pass. Five... you're halfway there! Six... don't get sloppy now.",
      },
      {
        time: 97,
        text: 'Seven... excellent. Eight... almost there. Nine... one more!',
      },
      {
        time: 104,
        text: "Ten! You did it! Awesome job. Let's start a new count.",
      },
      {
        time: 110,
        text: "Keep that ball on the ground. A one-touch pass is much easier when it's not bouncing.",
      },
      {
        time: 117,
        text: "Let's see that sharp movement again. After you pass, explode into a new space.",
      },
      {
        time: 124,
        text: 'Two minutes in! The tempo should be high now. The ball should be a blur between you two. No lazy passes.',
      },
      {
        time: 133,
        text: "Keep that quality high. You're both working so hard.",
      },
      {
        time: 138,
        text: 'The best players in the world are the best passers. This is the most important skill.',
      },
      {
        time: 145,
        text: "Let's see another ten in a row. Go for it.",
      },
      {
        time: 150,
        text: 'Halfway! Now for a big challenge. Weak foot only for the next minute! All one-touch passes must be with your other foot.',
      },
      {
        time: 161,
        text: "This is where you become an amazing player. It's going to be tricky, but you can do it. Let's go!",
      },
      {
        time: 169,
        text: "Focus on locking your ankle and making a clean connection. Don't worry about power, just accuracy.",
      },
      {
        time: 177,
        text: 'Good try! Keep working on it. Every touch with that foot makes it stronger.',
      },
      {
        time: 183,
        text: "Let's see five good one-touch passes in a row with your weak foot. You've got this.",
      },
      {
        time: 190,
        text: "Excellent! You're building a new weapon. Keep it up.",
      },
      {
        time: 195,
        text: "Keep that movement sharp, even with your weak foot. Don't get lazy.",
      },
      {
        time: 201,
        text: "Let's see another good one. Make it clean.",
      },
      {
        time: 206,
        text: "You're both doing great. This is a tough challenge.",
      },
      {
        time: 211,
        text: 'Keep that focus. Watch the ball all the way onto your foot.',
      },
      {
        time: 217,
        text: 'Awesome. Prepare to switch back to your strong foot.',
      },
      {
        time: 222,
        text: "Last minute! Back to any foot. Let's see the highest tempo yet. Crisp, sharp, perfect.",
      },
      {
        time: 230,
        text: "This is game speed now. Imagine you're playing in a big final. Every pass matters.",
      },
      {
        time: 238,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 244,
        text: 'Faster movement, sharper passes! Come on!',
      },
      {
        time: 249,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 255,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 260,
        text: 'Get ready for the countdown!',
      },
      {
        time: 263,
        text: 'Five, four, three, two, one... and hold. Top level.',
      },
      {
        time: 270,
        text: "That was a fantastic first exercise! Now, let's head to the wall for a super fast drill called the 'Wall Combination Rally'!",
      },
      {
        time: 279,
        text: "Player 1, you'll be on the wall. Player 2, you'll be the support player a few steps behind. Here's the combo: Player 1 passes to Player 2...",
      },
      {
        time: 288,
        text: "Player 2, you play a one-touch pass back to Player 1. Player 1, you then play a one-touch pass off the wall, control the rebound, and start the combo again by passing to Player 2. It's a continuous triangle! Go!",
      },
      {
        time: 303,
        text: "Let's see that flow: Partner, wall, control, partner, wall, control! The ball should be a blur.",
      },
      {
        time: 311,
        text: "After 45 seconds of high-tempo work, you'll switch roles. Keep that pace high!",
      },
      {
        time: 318,
        text: 'This is all about speed of thought and precision. Every pass has to be perfect.',
      },
      {
        time: 325,
        text: 'Fifteen seconds left for this round! Push it!',
      },
      {
        time: 330,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 335,
        text: "New player at the wall! Let's see that same energy. 45 seconds. Go!",
      },
      {
        time: 342,
        text: 'Remember, the pass from the partner and the pass from the wall are both one-touch!',
      },
      {
        time: 349,
        text: 'The only control touch is the one after the ball comes off the wall. Keep it sharp.',
      },
      {
        time: 356,
        text: "You're both doing great. This is a very advanced combination.",
      },
      {
        time: 362,
        text: 'Fifteen seconds left! Keep that quality high.',
      },
      {
        time: 367,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 372,
        text: "Two minutes down! Back to the first player at the wall. Let's make it harder.",
      },
      {
        time: 379,
        text: 'The player at the wall must now receive the pass from their partner and take their first touch OFF the wall before playing back. Two touches max.',
      },
      {
        time: 390,
        text: "So it's: Pass from partner, one touch off the wall, control, pass back. Let's see that new combo. 45 seconds. Go!",
      },
      {
        time: 400,
        text: 'This is a great test of your first touch under pressure.',
      },
      {
        time: 406,
        text: 'Keep it clean. A soft touch off the wall is key.',
      },
      {
        time: 412,
        text: 'Fifteen seconds left! Finish strong.',
      },
      {
        time: 417,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 422,
        text: 'New player at the wall! Your turn for the two-touch combo. 45 seconds. Go!',
      },
      {
        time: 430,
        text: "Excellent! You're thinking like a pro, playing fast even with two touches.",
      },
      {
        time: 437,
        text: 'Keep that focus. Every pass, every touch.',
      },
      {
        time: 443,
        text: 'Fifteen seconds left! Push the pace.',
      },
      {
        time: 448,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 453,
        text: "Last minute! Back to the first player. And back to pure one-touch for everyone! Let's see the highest tempo yet. Clean, crisp, and perfect.",
      },
      {
        time: 464,
        text: 'This is game speed now. The ball should be flying. Go!',
      },
      {
        time: 470,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 476,
        text: 'Faster passes, quicker reactions! Come on!',
      },
      {
        time: 481,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 487,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 492,
        text: 'Get ready for the countdown!',
      },
      {
        time: 495,
        text: 'Five, four, three, two, one... and TIME! Elite speed.',
      },
      {
        time: 502,
        text: "That was some high-speed teamwork! Now it's time to enter 'Messi's Pressure Cooker'!",
      },
      {
        time: 510,
        text: "This is a 1v1 challenge! Imagine a small circle around you. Player A, you're the dribbler first. Your job is to keep the ball inside the circle. Player B, you're the defender, and you're going to apply active, high pressure. Try to win the ball!",
      },
      {
        time: 524,
        text: "Dribbler, you must use your body to shield the ball and your quick feet to maneuver. Use tiny touches and body fakes. You'll work for 60 seconds, then we switch. Ready? Go!",
      },
      {
        time: 536,
        text: 'Dribbler, protect that ball! Keep your body between the defender and the ball. Defender, be a pesky shadow!',
      },
      {
        time: 544,
        text: "Good! Dribbler, use those tiny Messi touches to keep it close. Don't let them get a foot in.",
      },
      {
        time: 551,
        text: 'Defender, keep your feet moving! Try to fake one way and poke the ball out the other.',
      },
      {
        time: 558,
        text: 'Thirty seconds down! Attacker, show me a quick turn to get away from the pressure.',
      },
      {
        time: 565,
        text: 'Keep fighting for it, both of you! This is a real battle.',
      },
      {
        time: 571,
        text: 'Final fifteen seconds for this round. Dribbler, can you keep it all the way?',
      },
      {
        time: 577,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 582,
        text: "Great work! Player B, you're the dribbler now. Player A, you're the defender. 60 seconds on the clock. Go!",
      },
      {
        time: 591,
        text: 'New dribbler, show me that glue foot dribble! Keep it safe.',
      },
      {
        time: 597,
        text: 'New defender, can you win the ball back? Be quick and clever.',
      },
      {
        time: 603,
        text: "Dribbler, use your body! Be strong. Don't let them push you off the ball.",
      },
      {
        time: 610,
        text: 'Thirty seconds down! Keep working hard, both of you.',
      },
      {
        time: 616,
        text: "Final fifteen seconds. Don't stop now!",
      },
      {
        time: 621,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 626,
        text: "Awesome work! Back to our first dribbler. Halfway through the drill! Let's up the challenge.",
      },
      {
        time: 634,
        text: "Dribbler, this time, you must complete at least one successful skill move to 'beat' the defender during your 60-second turn. A quick turn or a body fake!",
      },
      {
        time: 646,
        text: 'Defender, you know the moves are coming. Try to read it! Go!',
      },
      {
        time: 652,
        text: 'Dribbler, look for your moment! A quick move can create that little bit of space you need.',
      },
      {
        time: 660,
        text: "Defender, watch their hips! That's the secret to not getting faked out.",
      },
      {
        time: 667,
        text: "Thirty seconds down! Did you try a move? Let's see it!",
      },
      {
        time: 673,
        text: 'Final fifteen seconds! Show me that skill!',
      },
      {
        time: 678,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 683,
        text: 'Fantastic! New dribbler, your turn to be the trickster! Show me a move. Go!',
      },
      {
        time: 690,
        text: "This is how you learn to use your skills in a real game situation. You're both doing great.",
      },
      {
        time: 697,
        text: 'Keep that focus. Every second counts.',
      },
      {
        time: 703,
        text: "Thirty seconds down! Let's see that move!",
      },
      {
        time: 708,
        text: "Final fifteen seconds! Don't let them win it back.",
      },
      {
        time: 713,
        text: 'Five, four, three, two, one... TIME!',
      },
      {
        time: 718,
        text: 'Last minute of this drill. Back to our first dribbler. The rule is simple: keep the ball! 30 seconds. Go!',
      },
      {
        time: 727,
        text: "This is where champions are made. Can you handle the pressure when you're tired?",
      },
      {
        time: 733,
        text: 'Ten seconds left! Keep it, keep it, keep it!',
      },
      {
        time: 738,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 743,
        text: 'Final 30 seconds of the drill! Last attacker, show me your best stuff. Go!',
      },
      {
        time: 751,
        text: "Come on, empty the tank! Protect that ball with everything you've got.",
      },
      {
        time: 757,
        text: 'Ten seconds left! Final push!',
      },
      {
        time: 762,
        text: 'Five, four, three, two, one... and TIME! Great competitive work.',
      },
      {
        time: 769,
        text: "That was an amazing battle! Now for some power and deception with 'Ronaldo's Step-Over & Strike'!",
      },
      {
        time: 777,
        text: "Player A, you're the attacker. Dribble with some speed towards Player B, who will be our friendly defender.",
      },
      {
        time: 785,
        text: "When you get close, perform a fast step-over. Swing your right foot around the front of the ball, fake like you're going that way, and then take the ball with the outside of your left foot.",
      },
      {
        time: 797,
        text: "Immediately after you beat your partner, play a powerful, one-touch driven pass to them. Then it's their turn to be the attacker! Let's go!",
      },
      {
        time: 808,
        text: "So it's: Dribble, step-over, explode, pass! And then you switch roles. Keep it moving.",
      },
      {
        time: 816,
        text: "Let's try it with the other foot. Dribble in, and do a step-over with your left foot, taking it away with your right. Be a two-footed player!",
      },
      {
        time: 826,
        text: 'The step-over has to be fast and convincing to trick the defender. Sell the fake!',
      },
      {
        time: 833,
        text: 'One minute in! Your step-overs are looking good. Remember to explode after the move.',
      },
      {
        time: 840,
        text: "The pass after the move should be sharp and powerful, like a shot! That's the 'strike' part.",
      },
      {
        time: 848,
        text: "Let's see that power now. Dribble in, step-over, and a rocket pass! Excellent!",
      },
      {
        time: 856,
        text: 'Keep switching roles after every turn. Both of you need to master this move.',
      },
      {
        time: 863,
        text: 'The pass after the step-over should be quick. One touch if you can.',
      },
      {
        time: 870,
        text: 'This is how you create chances in a real game. A quick move and a quick pass.',
      },
      {
        time: 877,
        text: "Let's see another one. Make it your best one yet. Sharp, fast, and powerful.",
      },
      {
        time: 884,
        text: 'Two minutes in! The step-over must be convincing. Sell it with your hips and shoulders. The explosion after is key.',
      },
      {
        time: 893,
        text: "Let's see a right-footed step-over. Good. Now a left-footed step-over. Awesome.",
      },
      {
        time: 900,
        text: "The defender will never know which way you're going!",
      },
      {
        time: 906,
        text: 'Keep that quality high. Every step-over, every pass.',
      },
      {
        time: 911,
        text: "Let's see another one. Dribble with a bit more speed this time.",
      },
      {
        time: 917,
        text: 'The faster you dribble, the more effective the step-over is. Great work.',
      },
      {
        time: 923,
        text: "Halfway! Let's try a double step-over! This is a superstar move.",
      },
      {
        time: 929,
        text: "Swing your right foot over, then your left foot over, and then explode away. It's a one-two fake!",
      },
      {
        time: 938,
        text: "This will really freeze the defender. Let's see you try it.",
      },
      {
        time: 944,
        text: 'Awesome! Now a sharp pass after the move. Excellent.',
      },
      {
        time: 950,
        text: 'Keep practicing that. The more you do it, the smoother it will get.',
      },
      {
        time: 956,
        text: "Let's see another double step-over. Quick feet!",
      },
      {
        time: 962,
        text: "You're both looking like superstars. Keep it up.",
      },
      {
        time: 967,
        text: 'Remember to keep your head up. After the move, you need to see your partner to make the pass.',
      },
      {
        time: 975,
        text: "This is a brainy move too. You have to think about your next move before you've even finished the first one.",
      },
      {
        time: 984,
        text: "Let's see another one. Dribble, double step-over, and a perfect pass.",
      },
      {
        time: 991,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 995,
        text: "Last minute! Focus on the transition from the trick to the 'strike'. It must be seamless. Skill, speed, and power.",
      },
      {
        time: 1005,
        text: "This is game speed now. Imagine you're in a big match.",
      },
      {
        time: 1011,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1017,
        text: 'Faster, sharper, more explosive! Come on!',
      },
      {
        time: 1022,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1028,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1033,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1036,
        text: 'Five, four, three, two, one... and TIME! Great power.',
      },
      {
        time: 1043,
        text: "That was some amazing skill! Now for some pure magic with a move from a legend: 'Zidane's Marseille Turn & Vision'!",
      },
      {
        time: 1052,
        text: "This is a super advanced 360-degree spin. Player A, you'll pass to Player B. Player B, as the ball arrives, you're going to perform the spin to shield the ball.",
      },
      {
        time: 1063,
        text: "To do the spin, you'll put one foot on the ball, drag it back slightly, and spin your body all the way around it, taking the ball with your other foot.",
      },
      {
        time: 1073,
        text: "Here's the 'Vision' part: As you complete the turn, you must have your head up to find your partner, who has moved to a new position, and play a sharp pass to them. Then you switch roles! Go!",
      },
      {
        time: 1087,
        text: "So it's: Pass, spin, find your partner, pass, and SWITCH! It's a continuous, high-level combination.",
      },
      {
        time: 1096,
        text: "Let's see it again. A good pass, a smooth 360 spin, and a clever pass to your moving partner. Awesome!",
      },
      {
        time: 1105,
        text: 'The key is to keep the ball under your body during the spin. Protect it like a treasure.',
      },
      {
        time: 1112,
        text: "One minute in! Your spins are looking great. Let's see five good turns from each of you. Keep switching.",
      },
      {
        time: 1119,
        text: 'Remember to use your arms for balance during the spin. It helps you stay steady.',
      },
      {
        time: 1126,
        text: 'This is how you create space when a defender is right on your back.',
      },
      {
        time: 1132,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 1138,
        text: "Excellent! You're looking like a pro.",
      },
      {
        time: 1143,
        text: "Two minutes in! Let's focus on the pass after the turn. It has to be quick and accurate.",
      },
      {
        time: 1151,
        text: 'The moment you finish your spin, your head should be up, and that pass should be gone. No hesitation.',
      },
      {
        time: 1159,
        text: "Let's see that quick transition. Spin right into a pass. Perfect!",
      },
      {
        time: 1166,
        text: "This is a huge test of your technical skill and your awareness. You're both doing great.",
      },
      {
        time: 1174,
        text: 'Keep that quality high. Every spin, every pass.',
      },
      {
        time: 1179,
        text: "Let's see another one. Dribble, spin, and a perfect pass.",
      },
      {
        time: 1185,
        text: 'Halfway! Now, the pass after the turn must be with the outside of the foot, adding another layer of disguise and difficulty.',
      },
      {
        time: 1195,
        text: 'This is a superstar move. Spin, and then a clever outside-of-the-foot pass to your partner.',
      },
      {
        time: 1203,
        text: "Let's see you try it. It's tricky, but you can do it.",
      },
      {
        time: 1209,
        text: 'Awesome! That was so sneaky. The defender would never see that coming.',
      },
      {
        time: 1216,
        text: 'Keep practicing that. The more you do it, the more it will become a natural reaction.',
      },
      {
        time: 1223,
        text: "Let's see another one with that outside-of-the-foot pass. Make it clean.",
      },
      {
        time: 1230,
        text: "Excellent. You're both playing with so much flair.",
      },
      {
        time: 1236,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1242,
        text: "Let's see one more perfect rep. A smooth spin and a clever pass.",
      },
      {
        time: 1249,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1253,
        text: "Last minute! Let's see the grace and intelligence of Zidane. A flawless spin straight into a perfect pass. Game speed now!",
      },
      {
        time: 1263,
        text: "Imagine you're in a big game. This is the move that creates the magic.",
      },
      {
        time: 1270,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1276,
        text: 'Faster, smoother, sharper! Come on!',
      },
      {
        time: 1281,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1287,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1292,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1295,
        text: 'Five, four, three, two, one... and TIME! Unbelievable skill.',
      },
      {
        time: 1302,
        text: "That was some amazing magic! Now for some Brazilian flair with 'Neymar's Deceptive Combination'!",
      },
      {
        time: 1310,
        text: "This is all about being tricky and smart. Player A, pass the ball to Player B. Player B, you're going to perform a deceptive move using the wall.",
      },
      {
        time: 1320,
        text: "Let's start with a dummy! Player B, as the pass comes, let it go right through your legs, hit the wall behind you, and then you spin and collect the rebound!",
      },
      {
        time: 1331,
        text: "After you control it, pass it back to Player A, and you switch roles. Let's see that sneaky dummy. Go!",
      },
      {
        time: 1340,
        text: "Remember to sell the fake! Act like you're going to touch the ball, then... WHOOSH! It's gone.",
      },
      {
        time: 1348,
        text: "Excellent! Now the other player tries. Let's see that big fake.",
      },
      {
        time: 1354,
        text: 'One minute in! Your dummies are looking great. Now for a new deception: the no-look wall pass.',
      },
      {
        time: 1363,
        text: 'Player A passes to Player B. Player B, as you receive the pass, look at Player A, but play a one-touch pass off the wall into space.',
      },
      {
        time: 1374,
        text: 'After you play the wall pass, you run onto it yourself, control it, and pass back to your partner. Then switch roles!',
      },
      {
        time: 1384,
        text: "This is a super clever move. You're faking with your eyes. Let's see it.",
      },
      {
        time: 1391,
        text: 'Awesome! That was so tricky. The defender would never know where the ball is going.',
      },
      {
        time: 1398,
        text: 'Keep practicing that. The more you do it, the more natural it will feel.',
      },
      {
        time: 1405,
        text: "Two minutes in! Let's go back to the dummy through the legs. Make it even more convincing.",
      },
      {
        time: 1413,
        text: 'Remember to spin quickly after you let the ball go through. You have to be the first one to the rebound.',
      },
      {
        time: 1421,
        text: "Let's see that quick spin now. Dummy and turn! Excellent.",
      },
      {
        time: 1427,
        text: 'Now back to the no-look wall pass. Look your partner right in the eye and pass to the wall.',
      },
      {
        time: 1435,
        text: "You're both playing with so much flair and creativity. This is fantastic.",
      },
      {
        time: 1442,
        text: 'Halfway! Your choice now, player receiving the ball. You can either do the dummy OR the no-look wall pass. Surprise your partner!',
      },
      {
        time: 1453,
        text: 'This is how you learn to make decisions in a game. You have to read the situation.',
      },
      {
        time: 1460,
        text: "Let's see what you choose. A dummy! Good choice. Now switch.",
      },
      {
        time: 1466,
        text: 'What about this time? A no-look! Awesome deception.',
      },
      {
        time: 1472,
        text: 'Keep that quality high. Every fake, every pass, every run.',
      },
      {
        time: 1478,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 1484,
        text: "Excellent. You're looking like a pro.",
      },
      {
        time: 1489,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1495,
        text: "Let's see one more perfect rep. A clever move and a clean pass.",
      },
      {
        time: 1502,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1506,
        text: "Last minute! Let's see your best Neymar deceptions. Game speed now!",
      },
      {
        time: 1513,
        text: "Imagine you're in a big game. This is the move that creates the magic.",
      },
      {
        time: 1520,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1526,
        text: 'Faster, smoother, sharper! Come on!',
      },
      {
        time: 1531,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1537,
        text: 'One more perfect move from each of you!',
      },
      {
        time: 1542,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1545,
        text: 'Five, four, three, two, one... and TIME! Pure entertainment.',
      },
      {
        time: 1552,
        text: "That was some amazing trickery! Now for a super smart, tactical move: the 'Advanced Wall Overlap'!",
      },
      {
        time: 1560,
        text: "This is a high-level team play. Player A, you'll start with the ball. Player B, you'll be the setup player near the wall.",
      },
      {
        time: 1568,
        text: 'Player A, pass to Player B. As soon as you pass, make a sprinting overlap run around the outside of Player B.',
      },
      {
        time: 1577,
        text: 'Player B, with one touch, lay the ball off the wall at an angle, right into the path of your running partner.',
      },
      {
        time: 1585,
        text: "Player A, you have to run onto that rebound, control it, and then you'll pass back to Player B, who has moved into your spot. Then you switch roles! Let's go!",
      },
      {
        time: 1597,
        text: "So it's: Pass, overlap, layoff, receive, and switch! It's a continuous, flowing move.",
      },
      {
        time: 1605,
        text: "The layoff pass has to be perfect. The right angle, the right speed. It's a clever little pass.",
      },
      {
        time: 1613,
        text: 'And the overlap run has to be a real sprint to create the space. Sell it!',
      },
      {
        time: 1620,
        text: "One minute in! Your timing is looking great. Let's see five good combos, switching each time.",
      },
      {
        time: 1627,
        text: 'Remember, the layoff is a one-touch pass. No stopping the ball.',
      },
      {
        time: 1633,
        text: 'This makes the move super fast and hard for defenders to read.',
      },
      {
        time: 1639,
        text: "Let's see that quick one-touch off the wall. Be sharp!",
      },
      {
        time: 1645,
        text: "Excellent! You're thinking and playing fast.",
      },
      {
        time: 1650,
        text: "Two minutes in! Let's make this even trickier. The runner, Player A, can now choose to fake the overlap.",
      },
      {
        time: 1659,
        text: 'Fake the run outside, then cut inside. Player B, you have to read the run and play the layoff pass to the inside instead. This is a big brain challenge!',
      },
      {
        time: 1671,
        text: "Let's see it. A good pass, a tricky run... and a perfect pass to match. You have to be connected!",
      },
      {
        time: 1679,
        text: "This is how you learn to read your teammate's movements without them even talking.",
      },
      {
        time: 1686,
        text: "Let's go back to the normal overlap run. Make it perfect.",
      },
      {
        time: 1692,
        text: 'Now try the fake overlap again. Keep the defender guessing!',
      },
      {
        time: 1698,
        text: 'Halfway! The weight of the passes is critical. The layoff pass must be soft, and the first pass must be firm.',
      },
      {
        time: 1707,
        text: "Let's focus on the runner's movement. After you pass, it's a full-speed sprint on the overlap.",
      },
      {
        time: 1715,
        text: "Then, a quick change of direction if you're faking. It's all about that explosive movement.",
      },
      {
        time: 1723,
        text: "Let's see that sharp change of direction on the fake. Go!",
      },
      {
        time: 1729,
        text: "Awesome! That's how you create space for yourself.",
      },
      {
        time: 1735,
        text: "Keep that communication going. A little shout can help your partner know what you're doing.",
      },
      {
        time: 1743,
        text: "You're both doing great. This is a very advanced tactical idea.",
      },
      {
        time: 1749,
        text: "Let's see another perfect combo. Make it your best one yet.",
      },
      {
        time: 1755,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1759,
        text: "Last minute! Let's see it at full speed. This should look like a professional team attacking. Fluid, fast, and intelligent.",
      },
      {
        time: 1769,
        text: "Imagine you're in a big game. This is the move that wins the championship!",
      },
      {
        time: 1776,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1782,
        text: 'Faster runs, sharper passes! Come on!',
      },
      {
        time: 1787,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1793,
        text: 'One more perfect combo from each of you!',
      },
      {
        time: 1798,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1801,
        text: "Five, four, three, two, one... and TIME! That's beautiful soccer.",
      },
      {
        time: 1808,
        text: "That was some incredibly smart teamwork! Now for a high-pressure battle called the 'Reactive Pressure Box'!",
      },
      {
        time: 1816,
        text: "This is a super intense 1v1 game. Imagine a small box around you. Player A, you're the attacker with the ball. Player B, you're the defender.",
      },
      {
        time: 1826,
        text: "Attacker, your job is to protect the ball inside the box. Defender, you're going to apply real pressure and try to win the ball!",
      },
      {
        time: 1835,
        text: "Here's the special rule: Attacker, you can use the wall as your teammate! If you're in trouble, play a quick pass off the wall to yourself to escape. You'll work for 60 seconds, then switch. Ready? Go!",
      },
      {
        time: 1849,
        text: 'Attacker, be strong! Use your body to shield the ball. Keep it moving. Defender, be a pesky shadow!',
      },
      {
        time: 1857,
        text: "Good! Attacker, use those tiny Messi touches to keep it close. Don't let them get a foot in.",
      },
      {
        time: 1864,
        text: 'Defender, keep your feet moving! Try to fake one way and poke the ball out the other.',
      },
      {
        time: 1871,
        text: 'Attacker, use the wall! A quick one-two off the wall can get you out of trouble.',
      },
      {
        time: 1878,
        text: 'Thirty seconds down! Keep that ball safe, attacker!',
      },
      {
        time: 1883,
        text: 'Defender, keep that pressure high. Good work.',
      },
      {
        time: 1888,
        text: 'Final fifteen seconds for this round. Dribbler, can you keep it all the way?',
      },
      {
        time: 1894,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 1899,
        text: 'Great job! Now Player B is the attacker. Player A is the defender. 60 seconds on the clock. Go!',
      },
      {
        time: 1908,
        text: 'New attacker, protect that ball! Use your body, use your skills.',
      },
      {
        time: 1914,
        text: 'New defender, can you win it back? Be quick and clever.',
      },
      {
        time: 1920,
        text: "Attacker, don't forget your teammate, the wall! A quick pass can save you.",
      },
      {
        time: 1927,
        text: 'Thirty seconds down! Keep working hard, both of you.',
      },
      {
        time: 1933,
        text: "Final fifteen seconds. Don't stop now!",
      },
      {
        time: 1938,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 1943,
        text: "Awesome work! Back to our first attacker. Halfway through the drill! Let's up the challenge.",
      },
      {
        time: 1951,
        text: 'Attacker, this time you MUST use the wall at least twice to keep possession. You have to think like a playmaker. 60 seconds. Go!',
      },
      {
        time: 1962,
        text: 'Look for your moments to use the wall. A quick one-two can beat the defender instantly.',
      },
      {
        time: 1969,
        text: "That's one! Good. Now keep it and look for your second wall pass.",
      },
      {
        time: 1975,
        text: 'Defender, try to read the wall pass and intercept it!',
      },
      {
        time: 1981,
        text: "That's two! You did it! Now just protect the ball.",
      },
      {
        time: 1986,
        text: "Final ten seconds! Don't let them get it.",
      },
      {
        time: 1991,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 1996,
        text: 'Fantastic! New attacker, your turn for the two-wall-pass challenge. 60 seconds. Go!',
      },
      {
        time: 2005,
        text: 'Be smart. Use your body to create an angle for the wall pass.',
      },
      {
        time: 2011,
        text: "That's one! Good. Now keep possession and find your next one.",
      },
      {
        time: 2017,
        text: 'Defender, can you block that angle to the wall? Good defending.',
      },
      {
        time: 2023,
        text: "That's two! Awesome job. Now just keep it safe.",
      },
      {
        time: 2028,
        text: "Final ten seconds! You've got this.",
      },
      {
        time: 2033,
        text: 'Five, four, three, two, one... TIME!',
      },
      {
        time: 2038,
        text: 'Last minute of this drill. Back to our first attacker. The rule is simple: keep the ball! 30 seconds. Go!',
      },
      {
        time: 2047,
        text: "This is where champions are made. Can you handle the pressure when you're tired?",
      },
      {
        time: 2053,
        text: 'Ten seconds left! Keep it, keep it, keep it!',
      },
      {
        time: 2058,
        text: 'Five, four, three, two, one... SWITCH!',
      },
      {
        time: 2063,
        text: 'Final 30 seconds of the drill! Last attacker, show me your best stuff. Go!',
      },
      {
        time: 2071,
        text: "Come on, empty the tank! Protect that ball with everything you've got.",
      },
      {
        time: 2077,
        text: 'Ten seconds left! Final push!',
      },
      {
        time: 2082,
        text: 'Five, four, three, two, one... and TIME! Great soccer brain.',
      },
      {
        time: 2089,
        text: "That was some incredible composure! For our final drill, it's time for 'The Master's Improvisation'!",
      },
      {
        time: 2097,
        text: "This is your canvas. For the final five minutes, you will create a continuous game, combining every skill we've worked on today.",
      },
      {
        time: 2105,
        text: "Here's how it works: The player without the ball is the coach! You'll call out a move, and your partner has to do it. Then they pass to you, and you switch roles. Player A, you call first! Go!",
      },
      {
        time: 2116,
        text: 'Good call! And great execution. Now pass and switch! New coach, new player.',
      },
      {
        time: 2123,
        text: "Let's see a 'Ronaldo Step-Over & Strike'! Remember, that's the step-over and a sharp pass.",
      },
      {
        time: 2131,
        text: "Excellent! Switch! Now a 'Zidane Turn & Vision'! The full 360 spin and a pass to your moving partner.",
      },
      {
        time: 2140,
        text: "You're linking the skills together like pros. Keep that flow going.",
      },
      {
        time: 2147,
        text: "One minute in! Let's get the wall involved. Coach, you can now call 'WALL COMBO'!",
      },
      {
        time: 2154,
        text: "When you hear that, the player has to do a quick 'Advanced Wall Overlap'. Pass, run, and get that layoff from your partner.",
      },
      {
        time: 2163,
        text: "Let's see it! 'WALL COMBO!' Good. Now switch!",
      },
      {
        time: 2169,
        text: "Now the coach can choose any move. 'NEYMAR'S DECEPTIVE COMBO!'",
      },
      {
        time: 2176,
        text: "Remember, that's a dummy or a no-look pass using the wall. Be tricky!",
      },
      {
        time: 2183,
        text: "Awesome! You're thinking and playing fast. Switch!",
      },
      {
        time: 2189,
        text: 'Two minutes in! The calls should be faster. The reactions should be faster.',
      },
      {
        time: 2196,
        text: "Let's see a 'DE BRUYNE DOUBLE PASS'! Power, control, power!",
      },
      {
        time: 2203,
        text: "Good. Switch! Now a 'MESSI PRESSURE COOKER'! Protect it for 10 seconds!",
      },
      {
        time: 2211,
        text: 'Good shielding! Now pass and switch!',
      },
      {
        time: 2216,
        text: "You're combining everything. This is what the best players do.",
      },
      {
        time: 2222,
        text: 'Halfway! Keep that quality high. The moves should be sharp.',
      },
      {
        time: 2228,
        text: "Let's see a 'REACTIVE PRESSURE BOX' moment. Attacker, use the wall to escape! 10 seconds!",
      },
      {
        time: 2237,
        text: "Good. Pass and switch! Now a 'ZIDANE TURN'!",
      },
      {
        time: 2243,
        text: "You're making it look easy. Keep that focus.",
      },
      {
        time: 2248,
        text: "Let's see another 'WALL COMBO'. Make it perfect.",
      },
      {
        time: 2254,
        text: "Excellent teamwork. You're reading the game.",
      },
      {
        time: 2260,
        text: 'Prepare for the final minute. This is the last push.',
      },
      {
        time: 2265,
        text: 'Last minute! Freestyle! No more calls. Just you two, working together, combining moves, keeping the ball flowing.',
      },
      {
        time: 2274,
        text: 'Show me a masterpiece. A sequence of at least three different skills.',
      },
      {
        time: 2281,
        text: 'Thirty seconds left! Empty the tank.',
      },
      {
        time: 2286,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 2292,
        text: 'Final fifteen seconds! Make it beautiful.',
      },
      {
        time: 2297,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND THAT'S THE SESSION! ABSOLUTELY ELITE!",
      },
      {
        time: 2307,
        text: 'That was 45 minutes of professional-level training. The quality and intensity were outstanding. Give your partner a massive high-five. You both crushed it.',
      },
      {
        time: 2318,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take a big, deep breath in... and out.",
      },
      {
        time: 2328,
        text: "You worked on passing, dribbling, turning, teamwork, and some amazing tricks today. You're becoming true all-around players. Amazing job. Drill complete!",
      },
    ],
  },
];

export default soccerPartnerDrills;
